// Card-automation runtime, extracted from GameBoard.jsx. This is the
// dispatch layer between the parsed card-text grammar
// (engine/automation: triggers / ops / scopes / conditions) and the
// live board: applyCardAutomation fires a card's entries for a set of
// triggers, _activateAbility pays costs and runs activated entries,
// the targeting/cell-pick flows gather player input for targeted ops,
// and executeTargetOp / executeCellOp resolve a single op against the
// chosen target. Prompt confirm/cancel route player choices back into
// effect application.
//
// Game-rule semantics live in engine/automation (this module should
// never grow rule logic. Extend the grammar there instead, see
// engine/automation/REFERENCE.md). Host contract: the GameBoard
// surface, especially _openPrompt/_closePrompt, _pushStorylineEvents,
// playerResources, effectLedger, sync, and the board primitives
// (_applyDamageToTarget, _spawnSiteAtCell, _applyTap, _moveCardToCell).

import { UI } from '../../../utils/arena/uiSounds';
import { resolveAvatarRules } from '../../../utils/game/avatarRules';
import { DURATION, FORCED_MOVE_OPS, OP, REGION, TRIGGER, animateSyncPayload, applyAnimateAuraTransform, applyStatusToggle, auraStepAnchorForCell, auraStepCandidates, unvisitedAdjacentSiteCells, buildPromptForEntry, cellsInScope, eligibleDestinations, evaluateCondition as evaluateConditionRule, getEffectiveStatuses, isDisabled, isForceMovable, maskedImposterBaseCardId, meetsDragonThreshold, minionsAtCell, ownerElementAllowsCast, projectilePaths, pushDestination, regionConsequence, wardBreaksOnTargetOp } from '../../../utils/game/engine/automation';
import { builtinSignature, parsedSignature } from '../../../utils/game/engine/automation/abilitySignatures';
import * as authCardState from './authCardState';
import { triggerIntent, choosePileIntent, SERVER_SILENT_OPS, SERVER_ABILITY_CELL_OPS, SERVER_ABILITY_MODAL_OPS, SERVER_ABILITY_PILE_OPS, SERVER_ABILITY_DIRECT_OPS, SERVER_AUTONOMOUS_TRIGGERS, entryIsSilentPrefixChoice, serverOwnsSilentEntry, serverOwnsTurnTriggerEntry } from '../../../utils/game/net/automationIntents';
import { effectiveSubtypes } from '../../../utils/game/engine/automation/cardFilter';
import { dispatchSilentOp, SILENT_OP_SET, tryDispatchAllSilent } from '../../../utils/game/engine/automation/ops/silentOps';
import { resolveTargetOp, RESOLVE_TARGET_OPS } from '../../../utils/game/engine/automation/ops/targetOps';
import { matchesFilter } from '../../../utils/game/engine/automation/cardFilter';
import { resolveDrawCount } from '../../../utils/game/engine/automation/drawOp';
import { buildRestorePlan, collectDepartedRows, departedPileKind } from '../../../utils/game/engine/automation/ops/cards/kairosArchive';
import { collectThresholdWaivers, thresholdsMetAt } from '../../../utils/game/engine/automation/ops/thresholdWaiver';
import { eligibleCemeteryCards } from '../../../utils/game/engine/automation/ops/cemeteryDeckOps';
import { sacrificeVictimsHere } from '../../../utils/game/engine/automation/ops/sacrificeDraw';
import { hasBanishablePair } from '../../../utils/game/engine/automation/ops/cards/sphinxBanishPair';
import { topWindow, eligibleWindowCards } from '../../../utils/game/engine/automation/ops/cards/blackMass';
import { alliedUnitsAtCell, sourceCellsWithAllies } from '../../../utils/game/engine/automation/ops/cards/flankingManeuver';
import { outerColumnVoidCells, classifyDealtCard, dealtTopCards, planHowlDeal } from '../../../utils/game/engine/automation/ops/cards/howlFromBeyond';
import { knightMoveCells } from '../../../utils/game/engine/automation/ops/knightMove';
import { buildStrikeLifelinkEntry } from '../../../utils/game/engine/automation/ops/strikeLifelink';
import { isOnEnemySite } from '../../../utils/game/engine/automation/ops/siteOccupancy';
import { cellCenterPoint } from '../../../utils/game/engine/automation/ops/summonAtTarget';
import { applySpellcasterNoThresholdGrant } from '../../../utils/game/engine/automation/ops/spellcasterGrant';
import { siteRelativeTargetCells } from '../../../utils/game/engine/automation/ops/siteRelativeTargets';
import { pullableAbovegroundUnitCells } from '../../../utils/game/engine/automation/ops/pullToCell';
import { siteCellKeys, siteCardAtCell } from '../../../utils/game/engine/automation/ops/cards/sirPerceval';
import { alliedSiteCellKeys, buryRegionForElement } from '../../../utils/game/engine/automation/ops/conjureUnderSite';
import { pickRandomSiteOrVoidCell } from '../../../utils/game/engine/automation/ops/treasureTeleport';
import { JUDAS_MARK } from '../../../utils/game/engine/automation/ops/cards/kissOfJudas';
import { triggerTargetDeathrite } from '../../../utils/game/engine/automation/ops/cards/feignDeath';
import { applyRevealDrawDamageBearer } from '../../../utils/game/engine/automation/ops/cards/pnakoticManuscript';
import { applyAllPlayersDrawPerScope } from '../../../utils/game/engine/automation/ops/cards/seelieCourt';
import { applyRollBanishSourceDraw } from '../../../utils/game/engine/automation/ops/cards/salmonOfKnowledge';
import { applyDrawFromCollection } from '../../../utils/game/engine/automation/ops/cards/eriksCuriosa';
import { castForceRevealDraw, resolveForceRevealDraw } from '../../../utils/game/engine/automation/ops/cards/searingTruth';
import { requestOpponentSwap, swapPileEnds } from '../../../utils/game/engine/automation/ops/opponentMaySwapPileEnds';
import { startPileMultiSelect } from './pileMultiSelect';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';
import { aura4AnchorWorldPoint, bfsStepReachable, consumeStealth, currentRegion, elementsAtCell, getCardCost, hasSummoningSickness, isAutomatonCard, isLandSiteAtCell, isMonumentCard, isWaterSiteAtCell, nearestAura4Anchor, ownerCanCastLocked, regionsAtCell, resolveOwnerId, sampleCellGeometry, stampSiteProvidesThisTurn } from '../../../utils/game/engine/core';
import { isCardSilenced } from '../../../utils/game/engine/core/keywordQuery';
import { computeGridPositions, getCellNumber } from '../../../utils/game/grid';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { toast } from 'sonner';
import { t, k } from '../../../localization/t';
import { SERVER_DRAW_PROMPT_PREFIX, confirmServerDrawPrompt } from './turnPhaseRunner';

// Silent player-heal resolver for combat triggers. amount is
// either args.amount (fixed) or the watch context's damage amount
// (dynamic: "heals you by damage dealt"). DD gating + cap + log
// are owned by `playerResources.gainLife`.
export function resolveHealPlayerInline(host, sourceCard, effect, context) {
  const controller = sourceCard.rotated ? 'p2' : 'p1';
  const fromWatch = effect.args?.from_watch_amount === true;
  const amount = fromWatch ? (context?.amount || 0) : (effect.args?.amount || 0);
  host.playerResources.gainLife(controller, amount, sourceCard.name || 'Source');
}

// Automation chokepoint. The session-wide mode decides for the whole table.
// both seats read the same MatchState flag, so a card either automates for
// everyone or for nobody. Never gates on a card's stats or subtypes: those
// are identity and stay invariant.
//
// Printed keywords used to be listed here as identity too. They are not
// (qa#121, Fabian 2026-08-22): a printed keyword is what places the Ward,
// Stealth and Lance tokens and lights every status badge, so at a manual
// table it is automation and the card instance carries none. The card's
// rules TEXT is the invariant. The inspector and the status ring read the
// catalog row, so the players can still read every keyword and place it by
// hand. The gate itself lives at the single hydration point,
// `cardPileLifecycle.hydrateCatalogFields`, not here.
//
// Anything that resolves a card's effect must pass through this chokepoint.
// Hardcoded avatar logic is the trap: it bypasses this function entirely
// and needs its own check (qa#129, Druid's Tawny).
// Static ops whose effect is owned end to end by the continuous aura
// derivation (engine/automation/staticAuraDerive.js). Mirrors that module's
// dispatch list; keep the two in step.
const AURA_OWNED_STATIC_OPS = new Set([
  OP.GRANT_STAT,
  OP.GRANT_STAT_PER_OTHER_IN_SCOPE,
  OP.GRANT_STAT_PER_EACH_SELF,
  OP.DISPEL_SELF_IF_SCOPE_EMPTY,
  OP.GRANT_KEYWORD,
  OP.REVOKE_KEYWORD,
  OP.TRANSFORM_SUBTYPES,
  OP.ADD_NAME_ALIAS,
  OP.MULTIPLY_THRESHOLDS_ON_SCOPE,
  OP.SUPPRESS_THRESHOLD_ON_SCOPE,
  OP.GRANT_MANA_PER_COVERED_SITE,
  OP.GRANT_CONTROL,
  OP.GRANT_TRIGGER_TO_SCOPE,
].filter(Boolean));

export function isAuraOwnedStaticEntry(entry) {
  if (!entry || entry.trigger !== 'static' || entry.optional) return false;
  const effects = entry.effect || [];
  return effects.length > 0 && effects.every((e) => AURA_OWNED_STATIC_OPS.has(e?.op));
}

export function shouldRunAutomation(host) {
  return host._automationsEnabled();
}

// `overrides.controller` ('p1' | 'p2') overrides the card's own
// rotated-derived controller. Used when firing automation for an
// equipped artifact: the effective controller is the HOST's, not
// the equipment's printed ownership.
export function applyCardAutomation(host, cardInstance, triggers = ['on_play', 'static'], overrides = {}) {
  // S3. Server-autonomous trigger families (on_stop / on_pickup / on_surface /
  // on_summon_here / on_kill / on_draw / on_damage_taken / on_death) resolve
  // wholly on the server, fired from its own commits: the router drops client
  // trigger intents for them, and any "may" surfaces as a server pendingChoice
  // modal.
  // Running the local walk here would double the prompt (or resolve effects a
  // second time through commands), so the whole automation pass is skipped.
  if (host._authZonesEnabled?.()
      && triggers.length > 0
      && triggers.every((t) => SERVER_AUTONOMOUS_TRIGGERS.has(t))) {
    return;
  }
  const fullCard = host._getFullCard(cardInstance.cardId);
  // Granted entries (from another card's GRANT_TRIGGER_TO_SCOPE) ride
  // alongside the catalog's intrinsic automation so trigger-matching
  // covers both. Homecoming's "Tap → Gain (1)" arrives this way:
  // installed on receivers' `_grantedTriggers[]` by the static-aura
  // recompute, fired when the activated path runs against the
  // receiver.
  const granted = cardInstance._grantedTriggers || [];
  const entries = [...(fullCard?.automation || []), ...granted];
  // An Animist Spirit is a Magic converted into a vanilla Minion (power =
  // its cost). It enters the realm as a unit, NOT cast as a magic. And the
  // magic's printed box text does not carry to the Spirit form. Its on_play
  // fires via the normal realm-entry path (applySitePlayEffects), so without
  // this guard `isMagicPlay` (which reads the catalog type, still 'Magic')
  // would re-route the freshly-summoned Spirit back through the cast pipeline
  // and send it to the cemetery. Suppress on_play for the Spirit entirely.
  if (cardInstance._animistSpirit && triggers.includes('on_play')) return;
  // A Magic being PLAYED still needs the cast pipeline (fire on_spell_cast
  // watchers + fizzle/resolve to cemetery) even with zero box automation.
  // A vanilla spell like Valor would otherwise be pulled from hand and
  // vanish. Never firing watchers (The Immortal Throne, Enchantress,
  // Sylvan Splendor) and never reaching the cemetery.
  const isMagicPlay = fullCard?.type === 'Magic' && triggers.includes('on_play')
    && overrides.spellCaster !== '__none__';
  if (entries.length === 0 && !isMagicPlay) return;
  // v1.104. Auto-mode gate. Skip the entire automation walk when the
  // user has disabled it for this card / type / globally.
  if (!host._shouldRunAutomation(cardInstance, fullCard)) return;
  // Silence (Sorcery): a silenced minion loses ALL box text. That
  // covers every trigger. On_play, on_death, activated, on_strike,
  // on_enter_location, on_spell_cast, on_realm_entry/exit, etc. The
  // single gate here keeps the rule in one place; the inherent basic
  // abilities (move/attack/defend/intercept) live outside this
  // dispatcher and remain available.
  if (isCardSilenced(cardInstance)) return;
  // Disabled (Sorcery): "a disabled card has no abilities." Same blanket
  // gate as Silence. Every triggered automation (on_turn_start, on_play,
  // on_death, etc.) is offline while the card carries the disabled status.
  if (isDisabled(cardInstance)) return;

  const player = overrides.controller || (cardInstance.rotated ? 'p2' : 'p1');

  // v1.104. Magic spell automation is bypassed for now. The full
  // spellcaster-pick pipeline (`_routeSpellPlay` / `_castSpellWithCaster`)
  // stays in place but isn't reached today. BEFORE the bypass we still
  // resolve standalone self-controller resource effects (Divine
  // Healing's "gain 7 life", future "lose N life" / "draw N" /
  // "gain N mana this turn" spells). These don't need a caster pick
  // or a target prompt. They route directly through the engine's
  // `playerResources` class, then the spell goes to cemetery as
  // before. New self-controller silent ops light up here by adding
  // one dispatch case.
  // A Magic whose every on_play effect is a silent self-controller
  // resource op (life / mana / draw) resolves directly. No spellcaster
  // pick, no target prompt (Tithe, King's Council). Any other op (target
  // picks, area, positional effects) routes through the full pipeline
  // (routeSpellPlay → castSpellWithCaster), which picks a spellcaster
  // Element-filtered. And anchors the spell's prompts at it. The
  // `__none__` sentinel is routeSpellPlay's zero-caster fallback: it
  // skips this branch and falls through to the entries loop so the
  // prompts resolve without an anchor.
  if (isMagicPlay) {
    // Cast-legality gate. An owner-locked spell needs its predetermined
    // permanent still in play; any other spell needs a legal
    // element-appropriate Spellcaster to exist. This gate also covers
    // the SILENT fast path below, which otherwise skips caster selection
    // and would let a Fire-only board cast an Earth spell. When no legal
    // caster exists, the spell can't be cast. It fizzles onto the board.
    const ownerId = cardInstance._castableOnlyBy;
    // Resolve the LIVE owner instance so silence / disable / transform
    // read current state, not the stale draw-time snapshot. Carried-aware:
    // an equipped relic owner (Omphalos on its bearer) has no standalone
    // mesh but is very much in play.
    const ownerCard = ownerId ? host._inPlayInstanceById(ownerId) : null;
    const ownerPresent = !!ownerCard;
    // Element-restricted owner (Omphalos) may only cast a held spell
    // requiring one of its elements; Wiccan Tools waives this for the
    // bearer this turn. A full spellcaster owner (Morgana) has no element
    // line, so ownerElementAllowsCast is always true for it.
    const ownerElementOk = ownerCard?._thresholdWaiverThisTurn
      || ownerElementAllowsCast(host._getFullCard(ownerCard?.cardId), fullCard);
    const castable = ownerId
      ? (ownerPresent && ownerCanCastLocked(ownerCard) && ownerElementOk)
      : host._validSpellcastersFor(player, fullCard).length > 0;
    if (!castable) {
      // The spell was never legally cast, so on_spell_cast watchers do
      // NOT fire. It simply fizzles onto the board.
      const why = ownerId
        ? (!ownerPresent
          ? msg('the card that drew it has left play')
          : !ownerCanCastLocked(ownerCard)
            ? msg('its owner can no longer cast spells')
            : msg('its owner cannot cast that element'))
        : msg('no eligible spellcaster');
      const spellLabel = fullCard.name || msg('The spell');
      host.logAction("{name} can't be cast ({reason})", { name: spellLabel, reason: why });
      toast.message(ownerId
        ? (!ownerPresent
          ? t("{name} can't be cast: the card that drew it has left play.", { name: spellLabel })
          : !ownerCanCastLocked(ownerCard)
            ? t("{name} can't be cast: its owner can no longer cast spells.", { name: spellLabel })
            : t("{name} can't be cast: its owner cannot cast that element.", { name: spellLabel }))
        : t("{name} can't be cast: no eligible spellcaster.", { name: spellLabel }));
      host._landCastSpellOnBoard(cardInstance);
      return;
    }
    // (The opponent storyline notice is broadcast at the cast entry point
    // in castMagicFromHand, ungated by automation mode, so it always fires.)
    // Every Magic originates from a spellcaster, so EVERY Magic routes
    // through the pick pipeline. Automated, silent self-controller, or no
    // automation at all. routeSpellPlay fires on_spell_cast, picks the
    // caster (0 / 1 / many, or the owner-locked permanent), then
    // castSpellWithCaster resolves the spell's effects and surfaces the
    // acknowledge / cemetery.
    host._routeSpellPlay(cardInstance, fullCard, triggers, player);
    return;
  }

  // Artifacts have two parallel things to do on play:
  //   1. Fire their on_play (Genesis) automation. Same as any
  //      other card. Per Sorcery, Genesis triggers when the
  //      artifact ENTERS THE REALM, whether the player ends up
  //      placing it on the field or attaching it to a unit.
  //   2. Show the Field/Equip choice prompt so the player picks
  //      where the artifact actually lands.
  //
  // The placement subtype determines target type for the prompt:
  //   • Monument  → conjured atop a site you control (target = Site)
  //   • Automaton → IS a minion, plays as a normal unit (no prompt;
  //                 fall through to standard automation handling)
  //   • Relic / Weapon / Armor / Device / Document / Potion /
  //     Instruments → carriable; equipped to a Minion or your Avatar
  //
  // The equip prompt is queued LAST in newPrompts so any Genesis
  // confirm fires first. The player resolves the Genesis effect
  // (e.g. Scarecrow returning Airborne minions) before choosing
  // Field vs Equip. Earlier code had an early `return` here that
  // skipped the entries loop entirely, so Genesis never fired for
  // any artifact. That bug is what this restructure fixes.
  // Codex: Automatons are artifacts that are MINIONS. They play as
  // units and never see the Field-vs-Equip choice. The predicates
  // accept both catalog shapes (normalized `traits` / raw `subTypes`);
  // the old inline regex read `fullCard.subTypes`, which is undefined
  // on normalized cards, so Kairos got a bogus equip prompt.
  const isArtifact = fullCard?.type === 'Artifact';
  const isAutomaton = isAutomatonCard(fullCard);
  const isAttachableArtifact = isArtifact && !isAutomaton;
  // Buried / Sunken Treasure conjure themselves UNDER an opponent-chosen
  // site on cast (their on_play conjure_under_site op opens that pick).
  // they are NOT equipped to a unit. Suppress the Field/Equip prompt so the
  // conjure is the sole placement, never a relic-on-minion attach.
  const conjuresUnderSite = (fullCard?.automation || []).some(
    (e) => e.trigger === 'on_play' && (e.effect || []).some((f) => f.op === OP.CONJURE_UNDER_SITE),
  );

  let pendingEquipPrompt = null;
  if (isAttachableArtifact && !conjuresUnderSite && triggers.includes('on_play')) {
    const targetType = isMonumentCard(fullCard) ? 'site' : 'unit';
    pendingEquipPrompt = {
      id: `prompt-${cardInstance.id}-equip-${Math.random().toString(36).slice(2, 8)}`,
      cardInstance,
      cardName: fullCard.name || cardInstance.cardId,
      controller: player,
      promptType: 'equip_choice',
      targetType,
      description: null,
      sourceEntry: null,
      sourceEffect: null,
    };
    // No early return. The entries loop below still needs to fire
    // any on_play (Genesis) automation. Automaton artifacts skip
    // this whole branch and behave like a regular minion.
  }

  const triggerSet = new Set(triggers);
  const newPrompts = [];

  // on_spell_cast triggers resolve at the CAST point, before the spell
  // enters the realm. Our play flow adds an aura to the board BEFORE
  // firing this trigger, so the just-cast aura would leak into a "target
  // aura" candidate list (Enchantress animating the aura off its own
  // cast). Exclude the in-flight spell's id from this trigger's targets.
  const castSpellId = triggerSet.has(TRIGGER.ON_SPELL_CAST) ? overrides.watchTarget?.id : null;

  // Auth cutover: a fully-server-silent automation entry is resolved server-side.
  // We emit ONE automation:trigger per (source, trigger) and SKIP every such
  // entry locally, so the effect resolves once (server diff), not twice. The
  // server re-derives + condition-checks every matching server-silent entry from
  // the catalog itself. Mirrors the combat-trigger cutover in GameBoard.
  const authCutover = host._authZonesEnabled?.() === true;
  const sentAuthTriggers = new Set();

  for (const entry of entries) {
    if (!triggerSet.has(entry.trigger)) continue;
    // For attachable artifacts (relics/weapons/monuments/etc.),
    // static entries are handled by other systems.
    // _applyEquippedArtifactEffects when the artifact attaches
    // (GRANT_THRESHOLD / GRANT_MANA → ledger), and
    // recomputeStaticAuras for continuous board effects. Firing
    // them here too would double-apply. Only on_play (Genesis)
    // entries are fired at play time for these artifacts.
    if (isAttachableArtifact && entry.trigger === 'static') continue;
    // All-silent Genesis ops on attachable artifacts resolve when the
    // placement settles, anchored on the final location. A
    // Monument's "here" is the site it's conjured atop, which isn't
    // known yet at drop time (Shrine of the Dragonlord's mass
    // teleport is the canonical case). `_pendingFieldGenesis` marks
    // the deferral; every resolution path (Field pick, ESC, equip
    // commit, equip-pending cancel) consumes the flag exactly once,
    // so Genesis fires once per realm entry no matter how the
    // Field/Equip choice plays out. And never again on later
    // manual re-equips.
    // Conjure-under-site artifacts (Buried / Sunken Treasure) have no
    // Field/Equip prompt to defer behind. Their conjure IS the placement,
    // so fire it immediately (the opponent's site pick opens at play time).
    if (isAttachableArtifact && !conjuresUnderSite && entry.trigger === 'on_play'
        && (entry.effect || []).every((e) => SILENT_OP_SET.has(e.op))) {
      cardInstance._pendingFieldGenesis = true;
      continue;
    }
    // v1.22: conditional static GRANT_THRESHOLD / GRANT_MANA entries
    // are auto-managed by recomputeStaticAuras. Skip the play-time
    // prompt entirely. They evaluate continuously and add/remove
    // their ledger contribution every recompute, so a one-shot
    // Confirm wouldn't model the rule correctly anyway.
    if (entry.trigger === 'static' && entry.condition
        && (entry.effect?.[0]?.op === OP.GRANT_THRESHOLD || entry.effect?.[0]?.op === OP.GRANT_MANA)) {
      continue;
    }
    // Static cost modifiers (Dragonlord's Lair: "Dragons cast here cost
    // (1) less"; Templar) are passive ongoing auras owned solely by
    // recomputeStaticAuras, which installs them into the ledger with a
    // dedup guard. Surfacing the play-time confirm prompt here would
    // install a SECOND, unguarded entry via the prompt-resolution path
    // and double the discount. Skip them. Recompute is the one source.
    if (entry.trigger === 'static' && entry.effect?.[0]?.op === OP.MODIFY_CAST_COST) {
      continue;
    }
    // Continuous auras (stat grants, keyword grants, control claims, ...)
    // are derived every recompute by recomputeStaticAuras / the server's
    // auraDerive, with nothing for the player to decide. A play-time
    // Confirm for them is pure friction, and a card with several such
    // clauses in its sidecar (King of the Realm: "+1 power" and "you control
    // all Mortals") asked the same question once per clause.
    if (isAuraOwnedStaticEntry(entry)) continue;
    // Auth cutover (server-silent entries). A non-static entry whose ops are ALL
    // server-silent is delegated: emit the trigger intent once per (source,
    // trigger) and skip local resolution. The server owns the condition check, so
    // we do NOT pre-filter by condition here (mirrors the combat gate). Choice ops
    // (damage_target/…) are NOT in SERVER_SILENT_OPS. They keep resolving locally
    // until the pendingChoice picker is wired.
    //
    // The shape test is the shared predicate, never a re-spelling of it: the
    // turn-phase runner delegates on `serverOwnsTurnTriggerEntry`
    // (serverOwnsSilentEntry ∪ entryIsSilentPrefixChoice, the gate below), and
    // that hand-off is only sound while this branch matches it. Narrow a copy
    // here and the runner still says "the runtime has it" while nothing emits.
    // a silently lost effect, worse than the double-resolve this gate prevents.
    if (authCutover && entry.trigger !== 'static'
        && serverOwnsSilentEntry(entry)) {
      if (!sentAuthTriggers.has(entry.trigger)) {
        const wt = overrides.watchTarget?.defender || overrides.watchTarget?.killed
          || overrides.watchTarget?.source || overrides.watchTarget || null;
        host._sendMatchIntent(triggerIntent(cardInstance.id, entry.trigger, {
          watchTargetId: wt?.id || null,
          amount: overrides.watchTarget?.amount ?? null,
        }));
        sentAuthTriggers.add(entry.trigger);
      }
      continue;
    }
    // Auth cutover (Kairos restore). Server-AUTONOMOUS: the deathTriggers
    // fan-out fires the Deathrite at the sweep that realized the death. No
    // intent, no local prompt (the archive lives on the server card).
    if (authCutover && entry.trigger !== 'static'
        && (entry.effect || []).some((e) => e.op === OP.RESTORE_ARCHIVED_REALM)) {
      continue;
    }
    // Auth cutover (Pact with the Devil). Delegated BEFORE the generic choice
    // gate so the intent carries the cast pipeline's caster id (the generic
    // emit doesn't thread it).
    if (authCutover && entry.trigger !== 'static'
        && entry.effect?.[0]?.op === OP.PACT_SACRIFICE_DRAW) {
      if (!sentAuthTriggers.has(entry.trigger)) {
        host._sendMatchIntent(triggerIntent(cardInstance.id, entry.trigger, {
          casterId: overrides.casterOverrides?.casterId || null,
        }));
        sentAuthTriggers.add(entry.trigger);
      }
      continue;
    }
    // Auth cutover (choice ops). An entry matching the (silent-prefix +)
    // choice shape. A server-choice pivot optionally preceded by server-
    // silent ops (Mad Dash's leading draw), tail all followups. Is
    // delegated: the trigger intent makes the server resolve the prefix,
    // compute candidates + write a durable pendingChoice; the picker renders
    // from the mirror (authChoice) and the click sends chooseIntent. Skip ALL
    // local resolution (the prefix draw AND the startTargeting halo) so the
    // entry resolves once (server).
    if (authCutover && entry.trigger !== 'static'
        && entryIsSilentPrefixChoice(entry)) {
      if (!sentAuthTriggers.has(entry.trigger)) {
        const wt = overrides.watchTarget?.defender || overrides.watchTarget?.killed
          || overrides.watchTarget?.source || overrides.watchTarget || null;
        host._sendMatchIntent(triggerIntent(cardInstance.id, entry.trigger, {
          watchTargetId: wt?.id || null,
          amount: overrides.watchTarget?.amount ?? null,
        }));
        sentAuthTriggers.add(entry.trigger);
      }
      continue;
    }
    // v1.103. Pass through the watch context so combat/realm
    // triggers' watch_target_filter conditions can gate (Interrogator
    // "if enemy Avatar" needs to see the watchTarget here, not just
    // in the silent-resolve path).
    const condOpts = overrides.watchTarget
      ? {
          watchTarget: overrides.watchTarget?.defender || overrides.watchTarget?.killed || overrides.watchTarget?.source || overrides.watchTarget,
          killedWasStrongestEnemy: overrides.watchTarget?.killedWasStrongestEnemy,
        }
      : {};
    if (!host._evaluateCondition(entry.condition, cardInstance, fullCard, condOpts)) continue;

    // Genesis "Provides (A)(E)(W) this turn" on a SITE (Desert Bloom)
    // grants the site its OWN affinity for the turn, which feeds the
    // per-cell element/region classification (the Geomancer's earth-site
    // Rubble bonus fires synchronously at the play commit, right after
    // this automation pass returns). Stamp the site instance NOW.
    // independent of the player-facing Confirm prompt that installs the
    // controller's casting-pool ledger entry. So the site already reads
    // as an earth site when the Geomancer check runs. Idempotent per
    // automation pass via `_siteProvidesStamped`; cleared at turn end
    // alongside the ledger entries (stepPruneExpiredEffects).
    if (cardInstance.isSite
        && entry.trigger === TRIGGER.ON_PLAY
        && entry.duration === DURATION.THIS_TURN
        && !cardInstance._siteProvidesStamped) {
      for (const eff of entry.effect || []) {
        if (eff.op === OP.GRANT_THRESHOLD && eff.args) {
          stampSiteProvidesThisTurn(cardInstance, eff.args);
          cardInstance._siteProvidesStamped = true;
        }
      }
    }

    // If every effect in this entry is silent-dispatchable, fire
    // them inline and skip the Confirm prompt. Lets on_play entries
    // like `bind_turn_lifespan` resolve invisibly the moment the
    // aura enters play. The player doesn't need to confirm "yes,
    // start the lifespan counter."
    // Entries marked `optional: true` carry a rules-text "may." The
    // player must get the Confirm/Decline drawer even when every op
    // is silent-capable (Vatn Draconis's end-of-turn submerge).
    if (!entry.optional
        && tryDispatchAllSilent(entry, cardInstance, host._silentOpsHost, { controller: player })) {
      continue;
    }

    // "May" target prompts are conditional on a viable target
    // existing. Per codex, a triggered ability with no valid
    // target doesn't get the option. Without this gate, Enchantress
    // would push an "Animate target aura" confirm every time the
    // controller casts a spell, even with zero auras in play. The
    // entry-level skip keeps the confirm drawer free of dead
    // prompts for any future "may target X" trigger too.
    if (host._entryFirstTargetOpHasNoMatches(cardInstance, entry, overrides.casterOverrides, castSpellId)) {
      continue;
    }

    // One prompt per entry (not per effect): cards with combined
    // effects in a single rules clause. Like Black Obelisk's
    // "lose 2 life and gain ② this turn." Should fire one
    // combined Confirm rather than two sequential prompts. The
    // confirm handler iterates entry.effect[] and applies each.
    const prompt = buildPromptForEntry(cardInstance, fullCard, entry, player);
    if (prompt) {
      // Forward caster/source overrides (e.g. Deathrite firing after
      // the card's mesh has left host.meshes. We cached the source
      // position so scope resolution still has an anchor).
      if (overrides.casterOverrides) prompt.casterOverrides = overrides.casterOverrides;
      if (castSpellId) prompt._castSourceSpellId = castSpellId;
      // Activated abilities carry their own agency: the player clicked the
      // ability in the actions menu, so a plain Confirm/Reminder is pure
      // friction (the Necromancer "summon a Skeleton" bug). Apply those
      // effects immediately. Prompts that need a REAL decision (choice,
      // pick_target, pick_cell, *_choice) still surface. And a "may"
      // (optional) entry still gets its Confirm/Decline drawer.
      const noInputConfirm = prompt.promptType === 'confirm' || prompt.promptType === 'reminder';
      if (entry.trigger === 'activated' && !entry.optional && noInputConfirm) {
        host._applyEffectsForConfirm(prompt, entry, [...(entry.effect || [])], cardInstance, player);
      } else {
        newPrompts.push(prompt);
      }
    }
  }

  // v1.78. `newPrompts` is in author/source order. The equip prompt
  // (if any) should resolve LAST per the existing comment "any Genesis
  // confirms... resolve before the player is asked Field vs Equip".
  // so we append it to the END of the intended-order list and let
  // _pushStorylineEvents handle the LIFO reversal. If a caller passed
  // `overrides.collectPrompts`, append to that array instead of
  // pushing. Used by _fireRealmTrigger to batch many cards' triggers
  // into one simultaneous push per codex.
  if (pendingEquipPrompt) newPrompts.push(pendingEquipPrompt);
  if (overrides.collectPrompts) {
    overrides.collectPrompts.push(...newPrompts);
  } else {
    host._pushStorylineEvents(newPrompts);
  }
}

// A Magic with no on_play box text. It has nothing to resolve, but per
// the spellcasting system it still goes onto the storyline as a "this
// card was played" acknowledge before reaching the cemetery.
function magicHasNoOnPlayAutomation(fullCard) {
  return !(fullCard?.automation || []).some((e) => e.trigger === 'on_play');
}

// Caster-side acknowledge entry for a no-automation magic. Confirming it
// (_disposeSpell) sends the resolved spell to the cemetery.
export function buildSpellAckPrompt(cardInstance, fullCard, controller) {
  return {
    id: `ack-cast-${cardInstance.id}`,
    promptType: 'acknowledge',
    cardName: fullCard?.name || cardInstance.cardId,
    cardInstance,
    controller,
    description: t('{name} has been played.', { name: fullCard?.name || t('The spell') }),
    _disposeSpell: true,
  };
}

export function routeSpellPlay(host, spellCard, fullSpell, triggers, controller) {
  // Owner-locked spell ("Only It Can Cast"): the caster is predetermined
  // by the `_castableOnlyBy` permanent that drew it. Skip caster
  // selection entirely and cast via that permanent. The owner must still
  // be in the realm AND still able to cast (ownerCanCastLocked): a minion
  // owner cast via Spellcaster (Morgana, Gabriel's ally) that is silenced
  // / disabled / transformed away from Spellcaster can no longer cast;
  // an artifact Omphalos casts inherently and stays able. If either gate
  // fails the spell can't be cast. It fizzles onto the board.
  const ownerId = spellCard._castableOnlyBy;
  if (ownerId) {
    // Carried-aware (equipped Omphalos owners have no standalone mesh).
    const ownerCard = host._inPlayInstanceById(ownerId);
    // Element-restricted owner (Omphalos) may only cast a held spell
    // requiring one of its elements; Morgana (full spellcaster) and a
    // Wiccan Tools waiver are unrestricted.
    const ownerElementOk = ownerCard?._thresholdWaiverThisTurn
      || ownerElementAllowsCast(host._getFullCard(ownerCard?.cardId), fullSpell);
    if (!ownerCard || !ownerCanCastLocked(ownerCard) || !ownerElementOk) {
      // Never legally cast. No on_spell_cast fire, fizzle onto the board.
      const why = !ownerCard
        ? msg('the card that drew it has left play')
        : !ownerCanCastLocked(ownerCard)
          ? msg('its owner can no longer cast spells')
          : msg('its owner cannot cast that element');
      host.logAction('{name} can only be cast by its owner. {reason}.', { name: fullSpell.name || msg('The spell'), reason: why });
      host._landCastSpellOnBoard(spellCard);
      return;
    }
    host._fireOnSpellCast(spellCard);
    host._castSpellWithCaster(spellCard, ownerCard, fullSpell, triggers, controller);
    return;
  }
  // on_spell_cast watchers (Enchantress, Sylvan Splendor, The Immortal
  // Throne) fire at the cast point, same as the silent fast path. The
  // zero-caster __none__ re-entry must NOT re-fire them, so this is the
  // single fire for the pipeline path.
  host._fireOnSpellCast(spellCard);
  // Element-specific spellcasters (Fire Spellcaster) are filtered to
  // those allowed to cast THIS spell. Passing the spell narrows the
  // caster list to legal casters before the 0 / 1 / many branch.
  const casters = host._validSpellcastersFor(controller, fullSpell);

  // Zero spellcasters. Defensive fallback. Re-enter applyCardAutomation
  // with a sentinel that bypasses this branch, so prompts queue
  // without a caster anchor.
  if (casters.length === 0) {
    host.logAction('No valid spellcaster found. Resolving without anchor');
    host.applyCardAutomation(spellCard, triggers, { controller, spellCaster: '__none__' });
    host._spellPlayInFlight.add(spellCard.id);
    host._maybeDisposeFinishedSpell(spellCard.id, controller);
    return;
  }

  if (casters.length === 1) {
    host._castSpellWithCaster(spellCard, casters[0], fullSpell, triggers, controller);
    return;
  }

  // Multi-caster. Enqueue a pick prompt. handlePromptConfirm reads
  // back the picked card by id and calls _castSpellWithCaster.
  const pickPrompt = {
    id: `prompt-${spellCard.id}-cast-${Math.random().toString(36).slice(2, 8)}`,
    cardInstance: spellCard,
    cardName: fullSpell.name || spellCard.cardId,
    controller,
    promptType: 'pick_caster',
    description: t('Pick a Spellcaster to cast {name}.', { name: fullSpell.name }),
    sourceEntry: null,
    sourceEffect: null,
    _spellTriggers: triggers,
  };
  host._spellPlayInFlight.add(spellCard.id);
  host._pushStorylineEvents([pickPrompt]);
}

// True when the SERVER owns a cast Magic's automation entry end to end. Its
// storyline-cast hook fires on_play the moment MOVE_TO_ZONE('storyline')
// commits, which covers BOTH shapes:
//   • every op server-silent (Divine Healing's gain life)
//   • a server-silent PREFIX ahead of a target-choice pivot (Mad Dash's
//     "draw a card, then give an ally Movement +1"). The server resolves the
//     draw, then writes a durable pendingChoice for the pick.
// Resolving either locally is a SECOND writer. That is what made Mad Dash draw
// TWICE: the server drew off its own draw-source modal, and the client's cast
// pipeline ran the same `draw` op through resolveStandaloneSpellEffects, which
// opened a duplicate spellbook/atlas modal the player also answered. The
// generic (non-Magic) entries loop has delegated this shape since the choice
// cutover; the Magic pipeline never got the same gate.
// Both shapes are the shared `serverOwnsTurnTriggerEntry` predicate. The same
// union the entries loop and the turn-phase runner gate on. Kept as one call so
// the cast pipeline cannot drift away from the other delegation sites.
function serverOwnsCastEntry(host, entry) {
  if (!host._authZonesEnabled?.()) return false;
  return serverOwnsTurnTriggerEntry(entry);
}

export function castSpellWithCaster(host, spellCard, caster, fullSpell, triggers, controller) {
  const casterMesh = host.meshes.get(caster.id);
  if (!casterMesh) {
    host.logAction('Caster left the field mid-cast. Spell fizzles');
    host._landCastSpellOnBoard(spellCard);
    return;
  }
  const casterOverrides = {
    sourcePos: { x: casterMesh.position.x, z: casterMesh.position.z },
    controllerRotated: caster.rotated,
    casterId: caster.id,
  };
  const fullCaster = host._getFullCard(caster.cardId);
  const casterName = fullCaster?.name || caster.name || msg('caster');
  host.logAction('{name} casts {card}', { name: casterName, card: fullSpell.name });

  const triggerSet = new Set(triggers);
  const newPrompts = [];
  for (const entry of fullSpell.automation || []) {
    if (!triggerSet.has(entry.trigger)) continue;
    if (!host._evaluateCondition(entry.condition, spellCard, fullSpell)) continue;
    // Location damage and Pact need the actual caster chosen by this ceremony.
    // The server's cast hook leaves these entries unstamped until that identity
    // arrives. Emit before the generic delegation gate skips local resolution.
    if (host._authZonesEnabled?.() && [OP.PACT_SACRIFICE_DRAW, OP.DAMAGE_EACH_AT_LOCATION].includes(entry.effect?.[0]?.op)) {
      host._sendMatchIntent(triggerIntent(spellCard.id, entry.trigger, { casterId: caster.id }));
      continue;
    }
    if (serverOwnsCastEntry(host, entry)) continue;
    const prompt = buildPromptForEntry(spellCard, fullSpell, entry, controller);
    if (prompt) {
      prompt.casterOverrides = casterOverrides;
      // Sacrificing the caster is not an option when the caster is the
      // player's Avatar (Pact with the Devil). The pure builder can't see
      // the caster, so strip that choice here.
      if (prompt.promptType === 'pact_choice' && fullCaster?.type === 'Avatar') {
        prompt.choices = (prompt.choices || []).filter((c) => c.value !== 'sacrifice');
      }
      newPrompts.push(prompt);
    }
  }
  if (newPrompts.length === 0) {
    // No interactive prompts. Now that the caster is locked in, resolve any
    // silent self-controller effects (gain life / draw / mana, or a forced
    // reveal that opens its own follow-up). Then surface the spell:
    //   • follow-up still pending  → keep it in flight (landed later)
    //   • no automation at all     → "this card was played" acknowledge
    //   • all-silent (now resolved) → straight onto the board
    host._resolveStandaloneSpellEffects(spellCard, controller, fullSpell);
    if (spellResolutionPending(host, spellCard.id)) {
      host._spellPlayInFlight.add(spellCard.id);
      host._castingSpellCards = host._castingSpellCards || {};
      host._castingSpellCards[spellCard.id] = spellCard;
      return;
    }
    if (magicHasNoOnPlayAutomation(fullSpell)) {
      host._spellPlayInFlight.add(spellCard.id);
      host._castingSpellCards = host._castingSpellCards || {};
      host._castingSpellCards[spellCard.id] = spellCard;
      host._pushStorylineEvents([buildSpellAckPrompt(spellCard, fullSpell, controller)]);
      return;
    }
    host._spellPlayInFlight.delete(spellCard.id);
    host._landCastSpellOnBoard(spellCard);
    return;
  }
  host._spellPlayInFlight.add(spellCard.id);
  host._pushStorylineEvents(newPrompts);
}

// Called after a prompt / pendingTarget / pendingCellTarget for a
// spell card resolves. If nothing else in the UI references the
// spell, land it on the board and clear the in-flight flag.
// Universal "is this cast spell still resolving?" True while ANY pending
// resolution step references it as its source (a prompt, a target / cell
// pick, a pile-select, a search, a scry, a sphinx-banish, or a deferred
// discard / sacrifice cost). The single source of truth for the cast
// settle flow: maybeDisposeFinishedSpell keeps the spell in
// _spellPlayInFlight until this returns false, then lands it on the board
// ONCE via the standard addCardToTable. A new card never needs a bespoke
// hook. If it spawns a new pending-resolution state, add ONE line here.
export function spellResolutionPending(host, spellId) {
  const s = host.state || {};
  return (
    (s.pendingPrompts || []).some((p) => p.cardInstance?.id === spellId)
    || s.pendingTarget?.sourceCard?.id === spellId
    || s.pendingCellTarget?.sourceCard?.id === spellId
    || s.pendingPileSelect?.sourceCardId === spellId
    || s.pendingSearchEffect?.sourceCard?.id === spellId
    || s.scry?.sourceCardId === spellId
    || host._pendingDiscardCost?.sourceCardId === spellId
    || host._pendingSacrificeCost?.sourceCardId === spellId
  );
}

export function maybeDisposeFinishedSpell(host, spellId, controller) {
  const busy = spellResolutionPending(host, spellId);
  // Deathspeaker's copy: "enters the realm, uses its Genesis, then is
  // banished." The copy is stamped into `_banishAfterGenesis` at spawn
  // and leaves for Banished the moment its Genesis prompts finish.
  if (!busy && host._banishAfterGenesis?.has(spellId)) {
    host._banishAfterGenesis.delete(spellId);
    const copy = host.meshes.get(spellId)?.userData?.cardInstance;
    if (copy) {
      host.logAction('{name} is banished', { name: cardLogLabel(copy, msg('The copy')) });
      host.banishCard(copy);
    }
  }
  if (!host._spellPlayInFlight.has(spellId)) return;
  if (busy) return;
  host._spellPlayInFlight.delete(spellId);
  // A cast magic has no board mesh while it dwells on the storyline, so
  // fall back to the casting-card ref stashed by castMagicFromHand. A
  // mesh-only lookup would miss it and the spell would vanish instead of
  // landing back on the board.
  const card = host.meshes.get(spellId)?.userData?.cardInstance
    || host._castingSpellCards?.[spellId];
  if (card) host._landCastSpellOnBoard(card);
  if (host._castingSpellCards) delete host._castingSpellCards[spellId];
}

// Fire standalone self-controller effects on a Magic spell at cast
// time, before the v1.104 bypass lands the card on the board. Covers
// GAIN_LIFE (Divine Healing), LOSE_LIFE, DRAW, and GRANT_MANA. The
// ACTUAL mutation lives in `playerResources` so any future change to
// those resource semantics lands once, in the engine, and lights up
// every standalone spell that reads from this dispatch.
//
// Entries that need targeting / caster picks / prompts are skipped
// here. They're served by the (currently dormant) full spell
// pipeline (`_routeSpellPlay` / `_castSpellWithCaster`). When that
// pipeline is re-enabled, this fast-path will still be correct: a
// spell whose only on_play effects are silent self-controller ops
// resolves once here either way.
export function resolveStandaloneSpellEffects(host, spellCard, controller, fullCard) {
  const entries = fullCard?.automation || [];
  if (entries.length === 0) return;
  const sourceName = fullCard?.name || spellCard.name || 'Spell';
  for (const entry of entries) {
    if (entry.trigger !== 'on_play') continue;
    if (entry.scope?.where && entry.scope.where !== 'self_controller') continue;
    if (entry.condition && !host._evaluateCondition(entry.condition, spellCard, fullCard)) continue;
    // Auth: server-owned entries are resolved by the storyline-cast hook (same
    // gate as castSpellWithCaster). Resolving them here too would double-apply
    // (gain life fired locally AND by the hook; Mad Dash's draw fired twice).
    if (serverOwnsCastEntry(host, entry)) continue;
    for (const eff of (entry.effect || [])) {
      if (eff.op === OP.GAIN_LIFE) {
        host.playerResources.gainLife(controller, eff.args?.amount || 0, sourceName);
      } else if (eff.op === OP.LOSE_LIFE) {
        host.playerResources.loseLife(controller, eff.args?.amount || 0, sourceName);
      } else if (eff.op === OP.GRANT_MANA) {
        host.playerResources.grantMana(controller, eff.args?.amount || 0);
      } else if (eff.op === OP.DRAW) {
        const pileName = eff.args?.source === 'site' ? msg('Atlas') : msg('Spellbook');
        const n = executeDrawOp(host, eff.args, spellCard, controller);
        if (n > 0) host.logAction('{name}: drew {n} from {pile}', { name: sourceName, n, pile: pileName });
      } else if (eff.op === OP.GRANT_SITE_PLACEMENT) {
        host._grantSitePlacement(controller, eff.args?.restriction || null, sourceName);
      } else if (eff.op === OP.FORCE_REVEAL_DRAW_DAMAGE) {
        // Searing Truth. Emits the cross-client request from the caster;
        // the target's client draws + reveals and replies with the
        // higher cost, which the caster applies to that player's Avatar.
        host._castForcedRevealDraw(spellCard, controller, eff.args || {});
      }
      // Non-silent ops (target picks, area damage, conditional
      // grants) fall through. They're served by the full pipeline.
    }
  }
}

// The controller's live site-placement allowance (count + land/water
// restriction). Auth: server truth from the mirror (the delegated
// grant_site_placement stamps siteGrants/-Restriction; the server consumes a
// grant at the site-cast commit and expires it at turn end); sandbox: the
// local host counter grantSitePlacement below maintains.
export function siteGrantInfo(host, controller) {
  if (host._authZonesEnabled?.()) {
    const m = host._authMirror?.getState?.();
    return {
      count: m?.siteGrants?.[controller] || 0,
      restriction: m?.siteGrantRestriction?.[controller] || null,
    };
  }
  return {
    count: host._siteGrants?.[controller] || 0,
    restriction: host._siteGrantRestriction?.[controller] || null,
  };
}

// Single primitive for GRANT_SITE_PLACEMENT (Avatar tap, Pathfinder,
// Landmass, Overflow). Increments the per-turn site-grant counter and
// records an optional land/water restriction the placement gate
// enforces. Both counter and restriction reset at turn start
// (turnPhaseRunner). `restriction` is 'land' | 'water' | null.
// SANDBOX-only: under auth the entry is delegated (SERVER_SILENT_OPS) and the
// allowance is server state read via siteGrantInfo.
export function grantSitePlacement(host, controller, restriction, sourceName) {
  if (!host._siteGrants) host._siteGrants = {};
  host._siteGrants[controller] = (host._siteGrants[controller] || 0) + 1;
  if (restriction) {
    if (!host._siteGrantRestriction) host._siteGrantRestriction = {};
    host._siteGrantRestriction[controller] = restriction;
  }
  const who = controller === 'p1' ? msg('Player 1') : msg('Player 2');
  if (restriction === 'land') host.logAction('{name}: {who} may play a land site this turn', { name: sourceName, who });
  else if (restriction === 'water') host.logAction('{name}: {who} may play a water site this turn', { name: sourceName, who });
  else host.logAction('{name}: {who} may play a site this turn', { name: sourceName, who });
}

// The Apex of Babel: open a one-shot free-cast window for the controller
// this turn. Mirrors grantSitePlacement. A flag on the host, cleared at
// the controller's turn end (turnPhaseRunner), waived in payCastingCost,
// and glowed in hand (renderBody). The next spell the controller plays
// costs no mana; the flag is consumed on that play.
export function grantFreeCast(host, controller, sourceName) {
  if (!host._freeCastGrant) host._freeCastGrant = { p1: false, p2: false };
  host._freeCastGrant[controller] = true;
  const who = controller === 'p1' ? msg('Player 1') : msg('Player 2');
  host.logAction('{name}: {who} may cast a spell for free this turn', { name: sourceName, who });
  toast.message(t('You may cast a spell for free.'));
  host.forceUpdate();
}

// True when a free-cast window is open for the controller. Read by the
// affordability gate (canPlayFromHand) and the hand-glow renderer. Auth:
// server truth from the mirror (the delegated cast_spell_for_free stamps
// freeCastGrant; the server prices the next cast 0 and consumes the grant at
// the commit); sandbox: the local host flag grantFreeCast maintains.
export function isFreeCastActive(host, controller) {
  if (host._authZonesEnabled?.()) {
    return !!host._authMirror?.getState?.()?.freeCastGrant?.[controller];
  }
  return !!host._freeCastGrant?.[controller];
}

// Consume the controller's free-cast window. Called from payCastingCost
// the moment a spell is actually played while the window is open.
export function consumeFreeCast(host, controller) {
  if (host._freeCastGrant) host._freeCastGrant[controller] = false;
}

// Resolve a single `draw` op. Count is fixed `args.amount` (default 1)
// unless `args.count` is present, in which case it equals the number of
// in-play cards matching the count's filter within its scope (same
// mechanism as grant_stat_per_each_self), optionally collapsed to
// distinct values of a field via `distinct_by`. `args.from === 'bottom'`
// draws the bottommost card of the pile. Every move routes through the
// synced host.drawCard primitive. Returns the number drawn.
export function executeDrawOp(host, args, sourceCard, controller, chosenAllyId = null, casterOverrides = null) {
  const playerNum = controller === 'p2' ? 2 : 1;
  const pileName = args?.source === 'site' ? 'Atlas' : 'Spellbook';
  const pile = host.findPileByNameForPlayer(pileName, playerNum);
  if (!pile) return 0;

  let count;
  if (args?.count) {
    // A spell emanates from its caster: a position-dependent count
    // (nearby / here / adjacent / covered) must resolve from the
    // caster's cell, not the off-board spell card. casterOverrides
    // carries the caster's sourcePos from the cast pipeline.
    const matches = host._resolveAuraScope(
      sourceCard,
      { where: args.count.scope },
      { filter: args.count.filter, exclude_self: args.count.exclude_self },
      casterOverrides || {},
    );
    count = resolveDrawCount(matches, args, host._getFullCard);
  } else {
    count = resolveDrawCount(null, args, host._getFullCard);
  }

  // Owner-locked draw: `owner: 'self'` (Omphalos / Morgana) tags each
  // drawn card so only the source permanent may cast it;
  // `owner: 'chosen_ally'` (Gabriel) tags it to the picked minion via
  // `chosenAllyId`; absent owner draws ordinary cards (castLockOwnerId
  // stays null).
  const castLockOwnerId = resolveOwnerId(args?.owner, sourceCard, chosenAllyId);
  // Owner art for the unit-hand badge, taken from the live drawing source
  // (the Omphalos / Morgana / chosen ally). Robust against any later
  // ownerId↔mesh-key mismatch, since we don't re-look-up by id here.
  const ownerCard = args?.owner === 'chosen_ally'
    ? host.meshes?.get(chosenAllyId)?.userData?.cardInstance
    : sourceCard;
  const castLockOwnerArt = ownerCard?.imageUrl
    || (ownerCard?.cardId ? host._getFullCard?.(ownerCard.cardId)?.printings?.[0]?.image_url : null);

  // `from: 'bottom'` (Deep-Sea Mermaids) is handled inside drawFromPile,
  // which removes cards[0] regardless of that card's fields. No pre-guard:
  // a previous `if (!bottomCardId(pile)) break` aborted the bottom-draw
  // whenever the bottom card's id was falsy (e.g. a server-adopted /
  // sanitized pile copy). The draw silently no-op'd while the log still
  // reported it as drawn. drawCard self-guards an empty pile.
  // Return the count ACTUALLY drawn, not the requested count. drawCard
  // returns null when a draw can't happen (empty pile, or Garden of Eden's
  // spell-draw cap blocking the 2nd+ spell this turn). Reporting the
  // requested count made owner-locked draws (Morgana / Omphalos / Gabriel)
  // log "drew 3" while the cap routed 0 into the unit hand. A phantom hand
  // the badge then refused to show. Callers gate their "drew N" log on n > 0.
  let drawn = 0;
  for (let i = 0; i < count; i++) {
    const opts = { skipLog: true, castLockOwnerId, castLockOwnerArt };
    if (args?.from === 'bottom') opts.from = 'bottom';
    if (host.drawCard(pile.id, opts)) drawn++;
  }
  return drawn;
}

// A cast spell that has finished resolving SETTLES ON THE BOARD at the cell it
// was cast to. It stays in the realm like any other card. (The cemetery
// routing this used to do is reverted for now; the storyline dwell and the
// rest of the cast ceremony are unchanged.) The drop point was stashed on the
// instance by castMagicFromHand, so addCardToTable rebuilds the mesh there and
// takes the standard realm-entry path: play log, broadcast, position commit.
export function landCastSpellOnBoard(host, spellCard) {
  // The CONTROLLER's client owns the placement (control, not draw-time
  // ownership, follows the cast. See handState's `_ownerSeat` note); the
  // opponent renders it from the broadcast + the authoritative zone fold.
  // `_ownerSeat` stays on the instance: the spell is on the board now, and it
  // still has to route to its owner's cemetery whenever it eventually leaves.
  if (!host.isCardOnLocalSide(spellCard) || host.meshes.has(spellCard.id)) return;
  // `silent` because the spell's on_play ALREADY resolved through the cast
  // pipeline. The play-commit block it skips would re-run applyCardAutomation
  // and re-enter the cast from the top. The "Cast <spell>" log line was written
  // at the cast entry point, so nothing is lost.
  host.addCardToTable(spellCard, true, { silent: true });
  // The spell left the storyline zone for the realm. Record the move so the
  // server (and a reconnect rebuild) sees it as a board card.
  if (host._authZonesEnabled?.()) host._emitMoveToZone?.(spellCard.id, 'play');
}

// Thin wrapper around evaluateCondition (cardEffects/conditions.js).
// Supplies this.meshes; everything else is pure.
export function evaluateCondition(host, condition, cardInstance, fullCard, extraOpts = {}) {
  const getFullCard = host._getFullCard;
  return evaluateConditionRule(condition, cardInstance, fullCard, host.meshes, {
    cellAt: host._cellAtWorld,
    elementsAt: (col, row) => elementsAtCell(host.meshes, host._cellAtWorld, col, row, getFullCard),
    getFullCard,
    // Grid corner data for the `first_visit_to_cell` corner gate
    // (Wayfaring Pilgrim's "each corner of the realm").
    gridDims: getGameGrid(host.state.spawnConfig),
    cemeteryCardsFor: (controller) => host.findOrCreateCemetery(controller === 'p2')?.cards || [],
    handCountFor: (player) => host._handCountFor(player),
    siteCountFor: (player) => host.countSitesForPlayer(player),
    ...extraOpts,
  });
}

// True iff the entry's FIRST effect is a target op AND its target
// filter resolves to zero cards on the board. Used by
// applyCardAutomation to suppress "may"-style triggered prompts
// that would otherwise fizzle the moment the player tried to
// confirm them. Returns false when the effect has no `args.target`
// (non-target op. Let the normal path handle it).
export function entryFirstTargetOpHasNoMatches(host, cardInstance, entry, casterOverrides, excludeId = null) {
  const filter = entry?.effect?.[0]?.args?.target;
  if (!filter) return false;
  let matches = host._resolveAuraScope(
    cardInstance,
    { where: filter.scope || 'global' },
    { filter, exclude_self: filter.exclude_self !== false },
    casterOverrides || {},
  );
  // A "whenever you cast a spell → target" trigger can't target the
  // spell that triggered it. It isn't in the realm yet (Enchantress).
  if (excludeId) matches = matches.filter((c) => c.id !== excludeId);
  return matches.length === 0;
}

// ── Single-target effect orchestration ────────────────────────────
//
// Effects with op ∈ { KILL_TARGET, DAMAGE_TARGET, RETURN_TO_HAND_TARGET,
// BANISH_TARGET } don't apply at Confirm time; they enter "pending
// target" mode. The existing target halos pulse on cards matching
// the filter, the next click on a valid target resolves the effect,
// ESC cancels.
export function startTargeting(host, sourceCard, controller, op, args, casterOverrides = null, followupOps = null, options = {}) {
  const filter = { ...(args.target || {}) };
  // Compute matches up-front purely to detect "no valid targets".
  // a wasted prompt that we can fizzle politely. casterOverrides
  // pivots positional scope to the caster's position (v1.14 spells).
  let matches = host._resolveAuraScope(
    sourceCard,
    { where: filter.scope || 'global' },
    { filter, exclude_self: filter.exclude_self !== false },
    casterOverrides || {},
  );
  // Forced-movement picks can't move `unmovable` units (Talamh Dreig).
  if (FORCED_MOVE_OPS.has(op)) matches = matches.filter(isForceMovable);
  // Exclude the in-flight cast spell (Enchantress can't animate the aura
  // off its own cast. It isn't in the realm yet).
  if (options.excludeId) matches = matches.filter((c) => c.id !== options.excludeId);
  if (matches.length === 0) {
    const filterDesc = host._describeTargetFilter(filter);
    toast.message(t('No valid targets. Effect fizzles ({filter}).', { filter: filterDesc }));
    return;
  }
  // v1.110. When the caller passes a `promptDescription`, push a
  // pick_target prompt onto the storyline so the player sees the
  // source card + a contextual label during target pick (mirrors
  // the pick_cell pattern). Callers that skip the description (the
  // Genesis/Deathrite path where a Confirm prompt already showed
  // context) get the bare halo-only UX.
  const promptId = options.promptDescription
    ? host._openTargetPickPrompt(sourceCard, controller, options.promptDescription)
    : null;
  // v1.77g. FollowupOps lets a single target-pick fan out to
  // multiple ops that share the chosen card (Enduring Faith: Ward
  // + install replacement aura on the same allied minion).
  host.setState({ pendingTarget: { sourceCard, controller, op, args, filter, casterOverrides, followupOps, promptId, excludeId: options.excludeId || null, duration: options.duration || null } });
}

// Human-readable summary of a target filter for fizzle messages.
// Lets the player see exactly what the effect was looking for so they
// can distinguish "no Giants" from "no nearby Giants" from "stale
// subTypes data on the target card".
export function describeTargetFilter(host, filter) {
  const parts = [];
  if (filter.scope) parts.push(filter.scope);
  if (filter.controller === 'enemy') parts.push('enemy');
  else if (filter.controller === 'self') parts.push('allied');
  if (filter.status) parts.push(filter.status);
  if (filter.subtype) parts.push(filter.subtype);
  if (filter.type) parts.push(filter.type.toLowerCase());
  return parts.length > 0 ? parts.join(' ') : 'any target';
}

// Run one deferred post-pick followup in printed order. Target-riding ops
// (tap, grant-status, install-aura, conditional draw-on-target) apply to the
// just-picked card; a sequential `draw` fires STANDALONE ("...then draw a
// spell." Not applied to the target). Shared by the unit-pick and the
// teleport cell-pick resolvers so the lead effect always resolves before its
// followups (Grievous Insult silence→tap→draw, Gift of the Wolf buff→draw).
function runFollowup(host, followup, sourceCard, controller, pickedCard, casterOverrides = null) {
  if (followup.op === OP.DRAW) {
    executeDrawOp(host, followup.args || {}, sourceCard, controller, null, casterOverrides);
    return;
  }
  host._executeTargetOp({
    op: followup.op,
    args: followup.args || {},
    sourceCard,
    controller,
    casterOverrides,
  }, pickedCard);
}

// Click handler. Called from the standard onMouseUp card click code
// path. Returns true if it handled the click (consuming it).
export function tryResolveTarget(host, clickedCard) {
  const pt = host.state.pendingTarget;
  if (!pt) return false;

  // Validate the clicked card actually matches the filter. The user
  // could click off a halo. If not, silently keep pending state.
  const validIds = host.getEquipTargetIds();
  if (!validIds || !validIds.has(clickedCard.id)) return false;

  const wardBroke = host._executeTargetOp(pt, clickedCard) === 'ward_broken';
  // The op may have opened a fresh target pick that re-stages pendingTarget
  // under the same key (Riptide stage one → stage two: site pick → adjacent
  // unit pick). Its pendingTarget + followups are now live on stage two.
  // don't fire the followups or null pendingTarget here, or stage two is
  // wiped (Preact batches the null over the re-stage) and never opens.
  if (pt._restagedTarget) {
    host._dequeuePickPrompt(pt.promptId);
    return true;
  }
  // v1.77g. Fire followup ops on the SAME picked target so a single
  // pick can carry multiple effects (Enduring Faith). v1.79. Skip
  // when TELEPORT has staged a pendingCellTarget; that path runs the
  // followups after the cell pick resolves.
  if (pt.followupOps?.length && !pt._teleportInFlight) {
    // casterOverrides carries so caster-relative followups (Fire Harpoons!'s
    // pull_to_source onto the caster's cell) resolve from the caster, not
    // the off-board spell card.
    for (const followup of pt.followupOps) {
      // Ward broke on the picked unit → nullify this card's remaining
      // unit-targeting followups (tap, grant-status). A standalone `draw`
      // ("...then draw a spell") is not against the unit, so it still runs.
      if (wardBroke && followup.op !== OP.DRAW) continue;
      runFollowup(host, followup, pt.sourceCard, pt.controller, clickedCard, pt.casterOverrides);
    }
  }
  host._dequeuePickPrompt(pt.promptId);
  host.setState({ pendingTarget: null }, () => {
    // v1.79. If a teleport just staged a cell pick, the spell isn't
    // finished yet. _maybeDisposeFinishedSpell will return false
    // because pendingCellTarget references the same source card.
    host._maybeDisposeFinishedSpell(pt.sourceCard.id, pt.controller);
  });
  return true;
}

export function cancelTargeting(host) {
  const pt = host.state.pendingTarget;
  if (!pt) return;
  // A cancelled sacrifice pick charges nothing. Clean up the deferred
  // cost (and dispose the spell / resume any held turn-pass for the
  // triggered "may" path) before the standard targeting teardown.
  if (pt.op === OP.SACRIFICE_DRAW) cancelSacrificeCostPick(host);
  host._dequeuePickPrompt(pt.promptId);
  host.setState({ pendingTarget: null }, () => {
    host._maybeDisposeFinishedSpell(pt.sourceCard.id, pt.controller);
  });
}

// ── Two-step location targeting (v1.13) ──────────────────────────
//
// Effects with op === DAMAGE_EACH_AT_LOCATION enter "pending cell
// target" mode. CellTargetOverlays renders a halo on every cell
// matching the location constraint (scope: 'nearby'|'adjacent', or
// distance: N); the next mousedown that resolves to a valid cell
// iterates units there and applies the per-unit op. ESC cancels.
//
// Cell membership is precomputed up-front (relative to the source's
// mesh position) and cached as a Set<'col,row'> on the pending state
// so the overlay + click validation stay cheap.
export function startCellTargeting(host, sourceCard, controller, op, args, casterOverrides = null) {
  const location = args.location || {};
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;

  // Source cell. For Magic spells the caster's position is the
  // anchor; for activated abilities on the source itself, fall back
  // to the source card's own mesh position.
  let sourceCell = null;
  if (casterOverrides?.sourcePos) {
    sourceCell = host._cellAtWorld(casterOverrides.sourcePos.x, casterOverrides.sourcePos.z);
  } else {
    const sourceMesh = host.meshes.get(sourceCard.id);
    if (!sourceMesh) { toast.message(t('Source card no longer on the field.')); return; }
    sourceCell = host._cellAtWorld(sourceMesh.position.x, sourceMesh.position.z);
  }
  if (!sourceCell) { toast.message(t('Source card not on the grid.')); return; }

  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;

  // Compute which (col,row) cells satisfy the location constraint.
  // Positional scopes (nearby / adjacent / here) come from the shared
  // cellsInScope helper. Nearby explicitly INCLUDES the source cell
  // (Sorcery rule, applies to Grey Wolves' counting and to
  // Ancient Dragon's "target location nearby" cell pick).
  //
  // distance: N = Sorcery "up to N steps away." Site-gated cardinal
  //   BFS where every cell on the path past the source must contain
  //   a site (both as a valid target and as a bridge for further
  //   traversal). Empty cells terminate the chain. The source cell
  //   itself counts as step 0 and is always a valid target.
  //   "up to N steps away" includes here.
  let validCells;
  if (typeof location.distance === 'number') {
    const sitedCells = host._cellsWithSites();
    validCells = bfsStepReachable(sourceCell, location.distance, totalCols, totalRows, sitedCells);
  } else {
    validCells = cellsInScope(sourceCell, location.scope, totalCols, totalRows);
  }

  if (validCells.size === 0) {
    toast.message(t('No valid locations. Effect fizzles.'));
    return;
  }
  host._startCellPick(sourceCard, controller, op, validCells, { args, casterOverrides });
}

// Resolve the caster's grid cell + grid dims for a spell-cast effect.
// Spells anchor on the caster's mesh position (casterOverrides.sourcePos);
// activated abilities on the source's own mesh. Returns null if the grid
// or anchor is unavailable.
function casterCellContext(host, sourceCard, casterOverrides) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return null;
  let anchor = casterOverrides?.sourcePos;
  if (!anchor) {
    const mesh = host.meshes.get(sourceCard.id);
    if (!mesh) return null;
    anchor = { x: mesh.position.x, z: mesh.position.z };
  }
  const cell = host._cellAtWorld(anchor.x, anchor.z);
  if (!cell) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  return { cell, totalCols: colPositions.length - 1, totalRows: rowPositions.length - 1 };
}

// Fire Harpoons!. Damage_site_relative_target. Precompute the legal
// target cells (water sites cardinally adjacent to the caster), inject
// them as `filter.cells`, and start a standard target pick limited to
// minions on those cells. A pull_to_source followup rides the same pick
// so the chosen minion is pulled to the caster's location after damage.
export function startSiteRelativeDamage(host, sourceCard, controller, args, casterOverrides = null, trailingEffects = []) {
  const ctx = casterCellContext(host, sourceCard, casterOverrides);
  if (!ctx) { toast.message(t('Caster not on the grid. Effect fizzles.')); return; }
  const getFullCard = host._getFullCard;
  const cells = siteRelativeTargetCells(
    ctx.cell, ctx.totalCols, ctx.totalRows,
    (col, row) => isWaterSiteAtCell(host.meshes, host._cellAtWorld, col, row, getFullCard),
  );
  if (cells.length === 0) { toast.message(t('No water site adjacent to the caster. Effect fizzles.')); return; }
  const target = { ...(args.target || {}), type: 'Minion', scope: 'global', cells };
  // The pull rides the damage pick; the entry's trailing effects (the
  // `draw`) ride after it so the chain fires in printed order: damage,
  // pull, draw. Not before the pick.
  host._startTargeting(
    sourceCard, controller, OP.DAMAGE_TARGET,
    { amount: args.amount || 1, target },
    casterOverrides,
    [{ op: OP.PULL_TO_SOURCE, args: {} }, ...trailingEffects],
  );
}

// Resolve stage two of Riptide: pull an aboveground unit adjacent to the
// picked site into the site's cell. The player picks which adjacent unit
// via a follow-on target halo seeded with the eligible unit cells.
export function resolvePullToTargetSite(host, pt, site) {
  const sourceName = host._getFullCard(pt.sourceCard.cardId)?.name || pt.sourceCard.name || 'Riptide';
  const siteMesh = host.meshes.get(site.id);
  const siteCell = siteMesh && host._cellAtWorld(siteMesh.position.x, siteMesh.position.z);
  if (!siteCell) { host.logAction('{name}: target site has no cell. The pull fizzles', { name: sourceName }); return; }
  const grid = getGameGrid(host.state.spawnConfig);
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const getFullCard = host._getFullCard;
  const pullable = pullableAbovegroundUnitCells(
    siteCell, host.meshes, host._cellAtWorld,
    colPositions.length - 1, rowPositions.length - 1,
    (col, row, card) => currentRegion(card, { col, row }, host.meshes, host._cellAtWorld, getFullCard) === 'surface',
  );
  if (pullable.length === 0) { host.logAction('{name}: no aboveground unit adjacent to {card}', { name: sourceName, card: cardLogLabel(site, msg('the site')) }); return; }
  const cells = pullable.map((u) => u.cell);
  // Stash the site cell so the second pick pulls the chosen unit INTO
  // it (_moveCardToCell resolves the cell-center point). The halo is
  // limited to the eligible adjacent-unit cells. The trailing `draw`
  // followup rides to stage two so it fires AFTER the unit is pulled
  // (printed order: pull, then draw), not after the site pick.
  host._startTargeting(
    pt.sourceCard, pt.controller, OP.PULL_TO_SOURCE,
    { target: { type: 'Unit', scope: 'global', cells }, _pullDestCell: siteCell },
    pt.casterOverrides,
    pt.followupOps || null,
    { promptDescription: t('Choose an aboveground unit to pull into the site') },
  );
  // Signal tryResolveTarget NOT to clear this freshly-staged stage-two
  // pendingTarget (same key as stage one). And not to fire the followups
  // after stage one, since they now ride on stage two.
  pt._restagedTarget = true;
}

// v1.109. Push a pick prompt onto the storyline so the drawer
// renders the source card + a contextual label + Cancel button while
// the player is in cell-pick or target-pick mode. Returns the prompt
// id so the pick state can dequeue it on resolve / cancel.
export function openPickPrompt(host, sourceCard, controller, promptType, description) {
  const fullCard = host._getFullCard(sourceCard.cardId);
  const cardName = fullCard?.name || sourceCard.name || sourceCard.cardId;
  const prompt = {
    id: `prompt-${promptType}-${sourceCard.id}-${Math.random().toString(36).slice(2, 8)}`,
    cardInstance: sourceCard,
    cardName,
    controller,
    promptType,
    description: description || (promptType === 'pick_cell' ? t('Pick a cell') : t('Choose a target')),
    sourceEntry: null,
  };
  host._pushStorylineEvents([prompt]);
  return prompt.id;
}

// Unified cell-pick kickoff. Opens the prompt + seeds the pending
// cell-target state in one call. Every "Tap → choose a cell" avatar
// ability (Avatar of Water/Air flood, Pathfinder, Geomancer ×2,
// Waveshaper, Flamecaller, etc.) routes through this so the prompt
// chain + state shape stay in lock-step.
//
// Extra fields (e.g. Flamecaller's `projectilePaths`) ride through
// via `opts.extra` and merge into `pendingCellTarget` verbatim.
export function startCellPick(host, sourceCard, controller, op, validCells, opts = {}) {
  const {
    description = t('Choose a location'),
    args = {},
    casterOverrides = null,
    extra = {},
  } = opts;
  // Opponent-seat picks (Buried/Sunken Treasure bury site, Sir Perceval's
  // site pick) are server pendingChoices online. Staged for the opponent
  // seat by the server and rendered via authChoice.js. This local path only
  // runs in sandbox, where one client drives both seats inline.
  const promptId = host._openCellPickPrompt(sourceCard, controller, description);
  host.setState({
    pendingCellTarget: {
      sourceCard,
      controller,
      op,
      args,
      validCells,
      casterOverrides,
      promptId,
      ...extra,
    },
  });
  return promptId;
}

// Sir Perceval Genesis / re-arm: open a cell-pick for `opponent` over
// every cell holding a Site. The picked cell is stamped on the source
// (_percevalTargetCell) by the OPEN_OPPONENT_SITE_CELL_PICK resolver in
// executeCellOp. No legal site → nothing to choose; the Genesis fizzles.
export function startPercevalSitePick(host, sourceCard, opponent) {
  const validCells = siteCellKeys(host.meshes, host._cellAtWorld);
  if (validCells.size === 0) {
    host.logAction('{name}: no site to choose. Genesis fizzles', { name: cardLogLabel(sourceCard, 'Sir Perceval') });
    return;
  }
  host._startCellPick(sourceCard, opponent, OP.OPEN_OPPONENT_SITE_CELL_PICK, validCells, {
    description: t('Choose a site for Sir Perceval'),
  });
}

// Resolver for the OPEN_OPPONENT_SITE_CELL_PICK cell-pick: stamp the
// chosen cell on the source so the on_stop `source_at_remembered_cell`
// gate recognises "that site." Re-arm recursion guard: if the opponent
// picked the cell Perceval is already standing on, stamping it would let
// the next stop (or a stale re-fire) double-draw, so we leave the prior
// pick intact and skip the stamp. Codex "enters that site" needs an
// actual entry, which a pick of the current cell can't produce.
export function resolvePercevalSitePick(host, sourceCard, cell, sourceName) {
  const mesh = host.meshes.get(sourceCard.id);
  const currentCell = mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null;
  if (currentCell && currentCell.col === cell.col && currentCell.row === cell.row) {
    host.logAction('{name}: chosen site is where he already stands. No new target', { name: sourceName });
    return;
  }
  host._setPercevalTarget(sourceCard, { col: cell.col, row: cell.row });
  const site = siteCardAtCell(host.meshes, host._cellAtWorld, cell);
  host.logAction('{name}: opponent chose {card} at [{cell}] for him to enter', { name: sourceName, card: cardLogLabel(site, msg('a site')), cell: getCellNumber(cell) });
}

// Buried Treasure / Sunken Treasure cast clause: open a cell-pick for
// `opponent` over the controller's own land (Buried) / water (Sunken)
// sites. The picked cell conjures the Treasure there buried via
// CONJURE_UNDER_SITE in executeCellOp. No legal allied site → nothing
// to bury under; the clause fizzles and the Treasure stays where cast.
export function startConjureUnderSitePick(host, sourceCard, opponent, element) {
  const controllerRotated = !!sourceCard.rotated;
  const isMatchingSiteAt = element === 'water'
    ? (col, row) => isWaterSiteAtCell(host.meshes, host._cellAtWorld, col, row, host._getFullCard)
    : (col, row) => isLandSiteAtCell(host.meshes, host._cellAtWorld, col, row, host._getFullCard);
  const validCells = alliedSiteCellKeys(host.meshes, host._cellAtWorld, controllerRotated, isMatchingSiteAt);
  const name = sourceCard.name || 'Treasure';
  if (validCells.size === 0) {
    if (element === 'water') host.logAction('{name}: no allied water site to bury under. Conjure fizzles', { name });
    else host.logAction('{name}: no allied land site to bury under. Conjure fizzles', { name });
    return;
  }
  host._startCellPick(sourceCard, opponent, OP.CONJURE_UNDER_SITE, validCells, {
    description: t('Choose a site to bury {name} under', { name }),
    args: { element },
  });
}

// Resolver for the CONJURE_UNDER_SITE cell-pick: conjure the Treasure
// (an Artifact. Relic, not a Site) into the chosen cell stamped
// subsurface (underground for a land site, underwater for a water site)
// so it sits buried beneath the covering site. Hidden until something
// carries it up, at which point the wired on_surface clause fires.
//
// The artifact instance is already on the board (the Field pick that
// triggered this deferred Genesis settled it at the drop cell), so we
// reposition the existing card to the picked cell rather than spawning a
// duplicate. commitCardMove broadcasts the new pose; the explicit
// subsurface broadcast buries it on the opponent's board too.
export function resolveConjureUnderSite(host, pct, cell, sourceName) {
  const { sourceCard, args } = pct;
  const region = buryRegionForElement(args?.element);
  const worldPos = host._cellCenterWorld(cell.col, cell.row);
  if (!worldPos) return;
  host._commitCardMove(sourceCard, worldPos);
  sourceCard.subsurface = region;
  host._broadcastCardSubsurface(sourceCard.id, region);
  host.logAction('{name} is conjured buried under the site at [{cell}]', { name: sourceName, cell: getCellNumber(cell) });
  host.recomputeStaticAuras();
}

// 13 Treasures of Britain on_pickup follow-up: detach the just-picked-up
// Treasures from its bearer and re-spawn it atop a uniformly random
// board cell ("the top of a random site or void"). The roll happens on
// this single firing client; addCardToTable's place broadcast carries
// the chosen position, so the opponent lands the Treasures on the same
// cell rather than rolling its own.
export function teleportSelfToRandomSiteOrVoidTop(host, sourceCard) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const cell = pickRandomSiteOrVoidCell(totalCols, totalRows, host._silentOpsHost.rng);
  if (!cell) return;
  const worldPos = host._cellCenterWorld(cell.col, cell.row);
  if (!worldPos) return;

  // The Treasures was removed from the table when it became carried
  // (equip choke point). Detach it from its bearer so the bearer's
  // equipment list + the opponent's mirror drop it, then re-spawn it as
  // a free field card at the random cell.
  const bearer = bearerOfEquipment(host, sourceCard.id);
  if (bearer) host._silentOpsHost.detachEquippedFromHost(bearer, sourceCard.id);

  sourceCard.x = worldPos.x;
  sourceCard.y = 0;
  sourceCard.z = worldPos.z;
  sourceCard.subsurface = null;
  host.addCardToTable(sourceCard, true, { silent: true });
  host.logAction('{name} teleports to [{cell}]', { name: cardLogLabel(sourceCard, msg('The Treasures')), cell: getCellNumber({ ...cell, totalCols, totalRows }) });
}

// The in-play card currently carrying `equipmentId` in its equippedCards,
// or null. The Treasures is mid-pickup so its bearer is the unit it was
// just attached to.
function bearerOfEquipment(host, equipmentId) {
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (card?.equippedCards?.some((c) => c.id === equipmentId)) return card;
  }
  return null;
}

export function dequeuePickPrompt(host, promptId) {
  if (!promptId) return;
  host.setState((s) => ({
    pendingPrompts: s.pendingPrompts.filter((p) => p.id !== promptId),
  }));
}

// Called from handleMouseDown when pendingCellTarget is set. Resolves
// a world-space click point to a cell, validates it, and runs the op.
// Returns true if the click was consumed (regardless of whether it
// hit a valid cell. Clicks anywhere on the board are reserved while
// cell-targeting is active).
export function tryResolveCellTarget(host, event) {
  const pct = host.state.pendingCellTarget;
  if (!pct) return false;
  const point = host.scene?.raycastTablePoint(event);
  if (!point) return false;
  const cell = host._cellAtWorld(point.x, point.z);
  if (!cell) return false;
  const key = `${cell.col},${cell.row}`;
  if (!pct.validCells.has(key)) {
    // Click landed outside the valid set. Keep targeting active so
    // the player can try again. Don't consume the click.
    return false;
  }
  // A resolver may open a FOLLOW-ON cell pick (Flanking Maneuver's
  // source → knight-destination chain) by calling _startCellPick, which
  // sets the next pendingCellTarget. The unconditional clear below would
  // wipe that just-opened pick. The resolver flips `_cellChainInFlight`
  // (a plain host field, synchronously readable. SetState batching
  // would otherwise hide the new pick) so we skip the clear and leave the
  // staged pick intact. Mirrors the _teleportInFlight plain-field guard.
  host._cellChainInFlight = false;
  host._executeCellOp(pct, cell);
  host._dequeuePickCellPrompt(pct.promptId);
  if (host._cellChainInFlight) {
    host._cellChainInFlight = false;
    return true;
  }
  host.setState({ pendingCellTarget: null }, () => {
    host._maybeDisposeFinishedSpell(pct.sourceCard.id, pct.controller);
    host._maybeResumeDeferredTurnPass();
  });
  return true;
}

export function cancelCellTargeting(host) {
  const pct = host.state.pendingCellTarget;
  if (!pct) return;
  host._dequeuePickCellPrompt(pct.promptId);
  host.setState({ pendingCellTarget: null }, () => {
    host._maybeDisposeFinishedSpell(pct.sourceCard.id, pct.controller);
    host._maybeResumeDeferredTurnPass();
  });
}

// Thunderstorm's "you may move it one step." The confirm prompt
// already carried the "may" (Cancel declined); this opens a cell-pick
// on the strips of cells the aura would newly cover per cardinal
// direction. Strips are disjoint, so the picked cell resolves to one
// destination anchor (engine: auraStepCandidates / auraStepAnchorForCell).
function auraStepContext(host, sourceCard) {
  const mesh = host.meshes.get(sourceCard.id);
  if (!mesh) return null;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const anchor = sourceCard._auraAnchor
    || nearestAura4Anchor(grid, totalCols, totalRows, mesh.position.x, mesh.position.z);
  if (!anchor) return null;
  return { grid, totalCols, totalRows, anchor };
}

export function startAuraOneStep(host, sourceCard, controller) {
  const ctx = auraStepContext(host, sourceCard);
  if (!ctx) return;
  const candidates = auraStepCandidates(ctx.anchor, ctx.totalCols, ctx.totalRows);
  const validCells = new Set(candidates.flatMap((c) => c.cells));
  if (validCells.size === 0) {
    host.logAction('{name}: no legal step from here', { name: cardLogLabel(sourceCard, msg('Aura')) });
    toast.message(t('No legal step from here.'));
    return;
  }
  host._startCellPick(sourceCard, controller, OP.MOVE_AURA_ONE_STEP, validCells, {
    description: t('Choose a location to move toward'),
  });
}

export function resolveAuraOneStep(host, sourceCard, cell, sourceName) {
  const ctx = auraStepContext(host, sourceCard);
  if (!ctx) return;
  const next = auraStepAnchorForCell(ctx.anchor, cell, ctx.totalCols, ctx.totalRows);
  if (!next) return;
  const point = aura4AnchorWorldPoint(ctx.grid, next.col, next.row);
  if (!point) return;
  host.logAction('{name} moves one step', { name: sourceName });
  host._silentOpsHost.moveAuraToCell(sourceCard, { col: next.col, row: next.row, position: point });
}

// Wildfire's after_each_turn move. The aura's CONTROLLER chooses the
// destination among the unvisited adjacent site cells. Online the server
// turn-trigger net owns the spread (the cell choice is a server
// pendingChoice, rendered via authChoice); this local path is
// sandbox-only, where one client pilots both seats.
export function routeWildfireMove(host, sourceCard, controller) {
  startWildfireMove(host, sourceCard, controller);
}

// Runs on the controller's client. Seeds the current cell as visited,
// then either dispels (no fresh ground) or opens the destination pick.
export function startWildfireMove(host, sourceCard, controller) {
  if (!host.meshes.get(sourceCard.id)) return;
  if (!sourceCard._visitedCells) sourceCard._visitedCells = [];
  const here = sourceCard._auraAnchor;
  if (here) {
    const hereKey = `${here.col},${here.row}`;
    if (!sourceCard._visitedCells.includes(hereKey)) sourceCard._visitedCells.push(hereKey);
  }
  const name = host._getFullCard(sourceCard.cardId)?.name || sourceCard.name || 'Wildfire';
  const cells = unvisitedAdjacentSiteCells(sourceCard, host.meshes, host._cellAtWorld);
  if (cells.length === 0) {
    host.logAction('{name} has no fresh ground. Dispels', { name });
    host.sendCardToPile(sourceCard, 'Cemetery');
    return;
  }
  const validCells = new Set(cells.map((c) => `${c.col},${c.row}`));
  host._startCellPick(sourceCard, controller, OP.MOVE_AURA_TO_UNVISITED_ADJACENT, validCells, {
    description: t('Choose where {name} spreads', { name }),
  });
}

export function resolveWildfireMove(host, sourceCard, cell, sourceName) {
  const cells = unvisitedAdjacentSiteCells(sourceCard, host.meshes, host._cellAtWorld);
  const dest = cells.find((c) => c.col === cell.col && c.row === cell.row);
  if (!dest) return;
  if (!sourceCard._visitedCells) sourceCard._visitedCells = [];
  // Push the destination BEFORE moveAuraToCell so the move broadcast
  // carries the up-to-date visited list to the opponent's client.
  sourceCard._visitedCells.push(`${dest.col},${dest.row}`);
  host.logAction('{name} moves to [{cell}]', { name: sourceName, cell: `${dest.col},${dest.row}` });
  host._silentOpsHost.moveAuraToCell(sourceCard, dest);
}

// Iterate every card whose mesh sits inside the picked cell, filter
// by args.target.filter (controller / type / subtype), and apply the
// per-card op. Currently only DAMAGE_EACH_AT_LOCATION is wired here;
// future kill / banish variants would reuse this orchestration.
export function executeCellOp(host, pct, cell) {
  const { sourceCard, op, args, controller } = pct;
  const sourceName = host._getFullCard(sourceCard.cardId)?.name
    || sourceCard.name || 'Card';

  // Sir Perceval Genesis: the opponent picked a site. Stamp it on the
  // source so the on_stop draw recognises "that site."
  if (op === OP.OPEN_OPPONENT_SITE_CELL_PICK) {
    resolvePercevalSitePick(host, sourceCard, cell, sourceName);
    return;
  }

  // Buried Treasure / Sunken Treasure: the opponent picked an allied
  // land/water site. Conjure the Treasure into that cell buried.
  if (op === OP.CONJURE_UNDER_SITE) {
    resolveConjureUnderSite(host, pct, cell, sourceName);
    return;
  }

  // Chains of Prometheus power tie: the drawing player picked one of
  // their tied strongest untapped minions. Tap the tied minion at the
  // chosen cell via the unified tap primitive.
  if (op === OP.TAP_PICKED_TIED_MINION) {
    const tiedIds = new Set(args?.tiedIds || []);
    const picked = minionsAtCell(host.meshes, host._cellAtWorld, cell.col, cell.row)
      .find((c) => tiedIds.has(c.id));
    if (!picked) return;
    const mesh = host.meshes.get(picked.id);
    if (mesh) host._applyTap(picked, mesh, true, { source: 'automation' });
    return;
  }

  // v1.103. Avatar of Water flood finalisation. Unflood any prior
  // site this avatar flooded, flood the site in the picked cell, then
  // pop a confirm prompt offering optional teleport. Picked cell is
  // already guaranteed to contain a Site adjacent to the avatar's
  // body of water (validCells gate enforces it).
  if (op === OP.AVATAR_OF_WATER_FLOOD) {
    host._resolveAvatarOfWaterFlood(sourceCard, controller, cell, sourceName);
    return;
  }

  // v1.103. Geomancer second tap: banish Rubble at picked cell and
  // spawn the topmost atlas site there with full site-play effects.
  if (op === OP.GEOMANCER_REPLACE_RUBBLE) {
    host._resolveGeomancerReplaceRubble(sourceCard, controller, cell, sourceName);
    return;
  }

  // v1.103. Pathfinder tap: spawn the topmost atlas site at the
  // picked adjacent void and move the Pathfinder there.
  if (op === OP.PATHFINDER_TAP) {
    host._resolvePathfinderTap(sourceCard, controller, cell, sourceName);
    return;
  }

  // Bladedancer's post-attack step: move her to the picked cell, then
  // offer an attack against any enemy unit standing there.
  if (op === OP.BLADEDANCER_STEP) {
    host._resolveBladedancerStep(sourceCard, controller, cell, sourceName);
    return;
  }

  // Sparkmage's shock: random other unit at the picked location takes
  // the precomputed (A)-sum damage carried on the pick's args.
  if (op === OP.SPARKMAGE_SHOCK) {
    host._resolveSparkmageShock(sourceCard, controller, cell, sourceName, args?.damage || 0);
    return;
  }

  // Persecutor's step toward the closest Evil: plain move commit.
  if (op === OP.PERSECUTOR_STEP) {
    host._moveCardToCell(sourceCard, cell, sourceName, k('steps'), 'normal');
    return;
  }

  // Thunderstorm's optional one-step move: map the picked strip cell
  // back to its destination anchor and commit the aura move.
  if (op === OP.MOVE_AURA_ONE_STEP) {
    host._resolveAuraOneStep(sourceCard, cell, sourceName);
    return;
  }

  // Wildfire's after_each_turn move: commit the chosen unvisited
  // adjacent site as the destination and record it as visited.
  if (op === OP.MOVE_AURA_TO_UNVISITED_ADJACENT) {
    host._resolveWildfireMove(sourceCard, cell, sourceName);
    return;
  }

  // Flanking Maneuver two-stage cell pick. Stage 'source' picked a cell
  // with allies. Transition to a knight-move destination pick. Stage
  // 'dest' moves the gathered group together, then runs the draw.
  if (op === OP.GROUP_TELEPORT_KNIGHT) {
    resolveFlankingManeuverCell(host, pct, cell, sourceName);
    return;
  }

  // v1.103. Waveshaper flood + tap-minions resolver. Shares the
  // unflood-prior bookkeeping with Avatar of Water; additionally
  // taps every non-Submerge minion at the picked cell and stamps
  // _skipNextUntap on them.
  if (op === OP.WAVESHAPER_FLOOD) {
    host._resolveWaveshaperFlood(sourceCard, controller, cell, sourceName);
    return;
  }

  // v1.111. Flamecaller projectile resolver. Picked cell may be any
  // cell along one of the precomputed projectile paths (the trail OR
  // the impact). We map it back to its direction's impact cell so
  // the player can click anywhere along the trail to fire that
  // direction. Damage = sum of (F) of the banished fire minions.
  if (op === OP.FLAMECALLER_FIRE_BLAST) {
    const path = (pct.projectilePaths || []).find((p) =>
      p.pathCells.some((c) => c.col === cell.col && c.row === cell.row),
    );
    const impact = path?.impactCell || null;
    host._resolveFlamecallerFireBlast(sourceCard, controller, impact, sourceName, args.amount || 0);
    return;
  }

  // Ranged keyword Shoot resolver. Same trail-click-to-direction
  // mapping as the avatar projectiles; resolution declares the shot
  // through the CombatEngine (strike, no strike-back, no defend
  // window). The impact unit(s) live at the direction's impact cell.
  if (op === OP.RANGED_SHOT) {
    const path = (pct.projectilePaths || []).find((p) =>
      p.pathCells.some((c) => c.col === cell.col && c.row === cell.row),
    );
    host._resolveRangedShot(sourceCard, path?.impactCell || null);
    return;
  }

  // v1.115. Avatar of Fire Fireball resolver. Reuses Flamecaller's
  // projectile-cell-to-impact mapping; damage shape differs (4 to
  // impact unit + 2 to each other unit at the impact cell).
  if (op === OP.AVATAR_OF_FIRE_IGNITE) {
    const path = (pct.projectilePaths || []).find((p) =>
      p.pathCells.some((c) => c.col === cell.col && c.row === cell.row),
    );
    const impact = path?.impactCell || null;
    host._resolveAvatarOfFireIgnite(
      sourceCard, controller, impact, sourceName,
      args.amount || 0, args.splash || 0,
    );
    return;
  }

  // v1.79. Teleport finalisation. The picked unit + followups were
  // stashed in args during _executeTargetOp's TELEPORT_TARGET branch.
  if (op === OP.TELEPORT_TARGET) {
    const unit = args._teleportUnit;
    if (!unit) return;
    // v1.87. Teleport CAN cross regions per codex; the runtime
    // applies banish/die consequences if the unit can't survive the
    // landing region.
    host._moveCardToCell(unit, cell, sourceName, k('teleports'), 'teleport');
    const followups = args._teleportFollowups || [];
    for (const followup of followups) {
      runFollowup(host, followup, sourceCard, controller, unit);
    }
    return;
  }

  const filter = args.target?.filter || {};
  const excludeSelf = args.target?.exclude_self !== false;
  const amount = args.amount || 0;

  // Collect every cardInstance whose mesh resolves to the picked cell
  // AND matches the target filter (type / subtype). Snapshot first.
  // _executeTargetOp can mutate host.meshes mid-loop for kill ops.
  const targets = [];
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (excludeSelf && card.id === sourceCard.id) continue;
    const cardCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cardCell || cardCell.col !== cell.col || cardCell.row !== cell.row) continue;
    if (filter.type && card.type !== filter.type) continue;
    if (filter.subtype) {
      // effectiveSubtypes so a Corruptor-transformed unit matches by its
      // current identity (Beast → Monster), not its printed subtype.
      const subs = effectiveSubtypes(card);
      if (!subs.includes(filter.subtype.toLowerCase())) continue;
    }
    // Sites act as the location itself. Exclude them from "every
    // unit" iteration. A unit in Sorcery is a Minion or Avatar; the
    // Site beneath is structurally separate from the contents.
    if (card.type === 'Site') continue;
    targets.push(card);
  }

  if (targets.length === 0) {
    host.logAction('{name}: location has no units (no effect)', { name: sourceName });
    return;
  }

  const cellNum = getCellNumber(cell);
  if (op === OP.DAMAGE_EACH_AT_LOCATION) {
    host.logAction('{name}: dealt {amount} at cell {cell} ({n} units)', { name: sourceName, amount, cell: cellNum, n: targets.length });
    const fakePt = { op: OP.DAMAGE_TARGET, args: { amount }, sourceCard, controller };
    for (const target of targets) host._executeTargetOp(fakePt, target);
    // "Summon a <token> there for each unit that died" (Draconian
    // Bonekite). A unit died iff the damage sweep removed its mesh
    // from play. Ward breaks, prevent_death replacements, and
    // Avatars entering Death's Door all keep the mesh, so the check
    // counts exactly the true deaths. Anchor the spawn on the PICKED
    // cell via the sourcePos override ('here' scope resolves
    // relative to it), not the source's own cell.
    const perDeathToken = args.summon_per_death?.tokenName;
    if (perDeathToken) {
      const deaths = targets.filter((t) => !host.meshes.has(t.id)).length;
      const sourcePos = deaths > 0 ? host._cellCenterWorld(cell.col, cell.row) : null;
      if (sourcePos) {
        host._summonTokenAtLocations(
          sourceCard,
          controller,
          { tokenName: perDeathToken, location: { scope: 'here' }, count: deaths },
          { sourcePos },
        );
      }
    }
  }
}

// Iterate every card matching `args.each` and apply the op directly
// Multi-target effects (Death Dealer, Flayer, Scarecrow). No click
// required; the prompt's Confirm fires this immediately.
export function iterateEachOp(host, sourceCard, controller, op, args, casterOverrides = null) {
  const each = { ...(args.each || {}) };
  const matches = host._resolveAuraScope(
    sourceCard,
    { where: each.scope || 'global' },
    { filter: each, exclude_self: each.exclude_self !== false },
    casterOverrides || {},
  );
  if (matches.length === 0) {
    toast.message(t('No matching cards. Effect fizzles.'));
    return;
  }
  const fakePt = { op, args: { amount: args.amount }, sourceCard, controller };
  // Iterate snapshot. _executeTargetOp may mutate host.meshes
  // (sendCardToPile, removeCardFromTable) while the loop runs.
  const targets = [...matches];
  for (const target of targets) {
    // Map each-op back to its single-target equivalent so
    // _executeTargetOp does the heavy lifting per card.
    let perCardOp = op;
    if (op === OP.KILL_EACH) perCardOp = OP.KILL_TARGET;
    else if (op === OP.DAMAGE_EACH) perCardOp = OP.DAMAGE_TARGET;
    else if (op === OP.RETURN_EACH_TO_HAND) perCardOp = OP.RETURN_TO_HAND_TARGET;
    else if (op === OP.BANISH_EACH) perCardOp = OP.BANISH_TARGET;
    host._executeTargetOp({ ...fakePt, op: perCardOp }, target);
  }
  // Sjaelström. "Draw a spell for each." Counted from the pre-banish
  // match set (the cards are gone by now), drawn for the source's
  // controller via the unified draw op.
  if (op === OP.BANISH_EACH && args.draw_per) {
    const drawn = executeDrawOp(
      host,
      { amount: targets.length, source: args.draw_per.source || 'spell' },
      sourceCard,
      controller,
    );
    if (drawn > 0) {
      const sourceName = host._getFullCard(sourceCard.cardId)?.name || sourceCard.name || 'Effect';
      host.logAction('{name}: drew {n} for the banished', { name: sourceName, n: drawn });
    }
  }
}

// Client host adapter for the shared resolveTargetOp. Maps the target-op
// contract to GameBoard's mesh/animation primitives (exact mirror of the inline
// branches it replaced). The server's automationAdapter is the state mirror.
function targetOpHostFor(host) {
  return {
    cardName: (src) => host._getFullCard(src.cardId)?.name || src.name || 'Card',
    cardLabel: (c) => cardLogLabel(c),
    logAction: (key, values) => host.logAction(key, values),
    hasActiveWard: (c) => host._hasActiveWard(c),
    consumeWard: (c) => host._consumeWard(c),
    sendToCemetery: (c) => host.sendCardToPile(c, 'Cemetery'),
    damageTarget: (c, amount, opts) => host._applyDamageToTarget(c, amount, opts),
    findCatalogToken: (name) => host._findCardCatalogToken(name),
    cellOfCard: (c) => {
      const mesh = host.meshes.get(c?.id);
      return mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null;
    },
    spawnTokenAtCell: (row, rotated, cell) => {
      const point = cellCenterPoint(getGameGrid(host.state.spawnConfig), cell);
      if (point) host._spawnTokenAtPoint(row, rotated, point);
    },
    minionsAtCell: (cell) => minionsAtCell(host.meshes, host._cellAtWorld.bind(host), cell.col, cell.row),
    effectiveStatuses: (card) => getEffectiveStatuses(card),
    grantStatus: (card, keyword) => {
      if (getEffectiveStatuses(card).includes(keyword)) return false;
      host.toggleCardStatus(card, keyword); // unified: applyStatusToggle + badges + broadcast
      return true;
    },
    recordThisTurnStatus: (cardId, keyword) => {
      if (!host._thisTurnStatusReverts) host._thisTurnStatusReverts = [];
      host._thisTurnStatusReverts.push({ cardId, keyword });
    },
    recomputeAuras: () => host.recomputeStaticAuras(),
    tapTarget: (c) => {
      const mesh = host.meshes.get(c?.id);
      if (mesh && !c.tapped) host._applyTap(c, mesh, true, { source: 'tap_target' });
    },
    banishTarget: (c) => host.banishCard(c),
    returnToHand: (c) => {
      if (host.isCardOnLocalSide(c)) host.sendToHand(c);
      else host.removeCardFromTable(c); // opponent's card → their hand (count only locally)
    },
  };
}

export function executeTargetOp(host, pt, target) {
  const { op, args, sourceCard } = pt;
  const sourceName = host._getFullCard(sourceCard.cardId)?.name
    || sourceCard.name || 'Card';

  // Ops migrated to the shared, host-driven resolver (the SAME module the server
  // runs in automationChoice). resolveTargetOp owns its own Ward check, so this
  // delegates before the inline Ward gate below.
  if (RESOLVE_TARGET_OPS.has(op)) return resolveTargetOp(targetOpHostFor(host), pt, target);

  // v1.17a. Ward. A warded card breaks its ward INSTEAD of taking the
  // targeted effect. Consumed ward = removed from card.statuses + badges
  // rebuilt + static auras recomputed (so Faith Incarnate's per-ward
  // power update fires immediately). Per codex, damage + destruction
  // break a Ward from any source, but other targeting special abilities
  // / spells only from an opponent. WardBreaksOnTargetOp encodes that.
  if (host._hasActiveWard(target) && wardBreaksOnTargetOp(op, sourceCard, target)) {
    host._consumeWard(target);
    host.logAction('{name}: ward broken on {card}', { name: sourceName, card: cardLogLabel(target) });
    // Codex: a Ward nullifies the ENTIRE source's effects against the
    // warded unit, THEN breaks. Signal the break so the caller skips this
    // card's remaining unit-targeting followups (e.g. Grievous Insult's tap
    // after its silence). They must not land on the now-unwarded unit.
    return 'ward_broken';
  }

  if (op === OP.SACRIFICE_DRAW) {
    // Cauldron Crones. The click target is the minion being sacrificed
    // as the cost. Route through sendCardToPile (the synced sacrifice
    // primitive. Bypasses Ward / destruction prevention per codex),
    // then resolveSacrificeCostPick fires the deferred draw.
    host.logAction('{name}: sacrificed {card}', { name: sourceName, card: cardLogLabel(target) });
    host.sendCardToPile(target, 'Cemetery');
    host._resolveSacrificeCostPick(pt.controller);
  } else if (op === OP.GRANT_STATUS_TO_TARGET) {
    const keyword = args.keyword;
    if (!keyword) return;
    // Honor the firing entry's duration: this_turn / until_next_turn
    // grants apply now AND queue a revert at the right turn boundary;
    // permanent / instant / until_leaves stay permanent (Fade's
    // Stealth). until_next_turn resolves against the controller's turn
    // counter like the Enchantress animate-aura revert.
    let revert = null;
    if (pt.duration === DURATION.THIS_TURN) {
      revert = { kind: 'this_turn' };
    } else if (pt.duration === DURATION.UNTIL_NEXT_TURN) {
      const opponent = pt.controller === 'p1' ? 'p2' : 'p1';
      const opponentTurn = host.state.turnNumbers?.[opponent] || 0;
      revert = { kind: 'until_next_turn', revertPlayer: opponent, revertAtTurnNumber: opponentTurn + 1 };
    }
    host._applyGrantStatusToTarget(target, keyword, revert);
    host.logAction('{name}: granted {keyword} to {card}', { name: sourceName, keyword, card: cardLogLabel(target) });
  } else if (op === OP.TAG_AVATAR_UNDEFENDABLE) {
    // Kiss of Judas: mark the picked Avatar so attacks against it can't
    // be defended this turn (read by getEligibleDefenders), and stamp the
    // caster so the on_attack hook draws for them. The mark rides the
    // card:status broadcast so the host that resolves combat sees it; the
    // revert clears it at the caster's turn end (_thisTurnStatusReverts).
    const marked = applyStatusToggle(target, JUDAS_MARK, true);
    if (marked) {
      host.rebuildStatusBadges(target.id);
      host._broadcastCardStatus(target.id, JUDAS_MARK, true);
    }
    target._judasCaster = pt.controller;
    if (!host._thisTurnStatusReverts) host._thisTurnStatusReverts = [];
    host._thisTurnStatusReverts.push({ cardId: target.id, keyword: JUDAS_MARK });
    host.logAction("{name}: attacks against {card} can't be defended this turn", { name: sourceName, card: cardLogLabel(target) });
  } else if (op === OP.BUFF_TARGET) {
    // v1.82. Temporary +N stat until end of turn. Reuses the
    // Askelon-style stat-buff queue: mutates the chosen stat +
    // broadcasts + queues a revert in _stepPruneExpiredEffects.
    // `stat: 'mov'` grants bonus movement this turn (Mad Dash).
    const delta = args.delta || 0;
    // "+N power" buffs ATK and DEF equally (Sorcery power semantics);
    // `stat: 'mov'` is bonus movement, explicit `stat: 'atk'` is attack-only.
    const stat = args.stat || 'power';
    if (delta !== 0) {
      host._applyThisTurnStatBuff(target, delta, stat);
      const sign = delta > 0 ? '+' : '';
      const label = stat === 'mov' ? msg('movement') : msg('power');
      host.logAction('{name}: {card} gains {delta} {stat} this turn', { name: sourceName, card: cardLogLabel(target, msg('target')), delta: `${sign}${delta}`, stat: label });
    }
  } else if (op === OP.ANIMATE_TARGET_AURA) {
    // Enchantress. Mutate the picked aura into a Minion whose
    // attack and defence equal its printed cost, then queue an
    // identity revert at the START of the caster's next turn (codex
    // Start phase step 1: "End all 'Until next turn' effects").
    const fullTarget = host._getFullCard(target.cardId);
    if (applyAnimateAuraTransform(target, fullTarget)) {
      // Auras don't get a LifeHUD on entry (addCardToTable only
      // attaches one for type=Minion/Avatar). Animation flips
      // type→Minion, but the HUD is still missing. So the freshly
      // animated aura would show no ATK/HP pills until something
      // else triggered a HUD attach. Stamp one now so the power
      // indicator becomes visible immediately.
      host._attachLifeHUDIfMissing(target);
      host._refreshLifeHUD(target);
      // Sync the WHOLE new shape (type→Minion, base stats, revert
      // snapshot), not just the two stat pills. A stat-only broadcast
      // left the opponent's copy as the printed Aura (null stats), so
      // they rendered a 0-power phantom and the two sides disagreed on
      // whether the card was even a minion.
      if (host._authZonesEnabled?.()) host._sendMatchCommand(authCardState.animateCommand(target.id, animateSyncPayload(target)));
      // Key the revert to the CASTER, not the opponent. Only the
      // caster's client holds this entry, and only the caster's client
      // runs the caster's start phase (sandbox via completePassTurn, MP
      // via the turn:pass handler). Keying it to the opponent's end
      // phase. Which in MP runs on the OPPONENT's client, where this
      // entry never exists. Left the aura animated forever. The cast
      // happens in the caster's Main phase, so the next start phase the
      // caster runs is their following turn's: exactly "until your next
      // turn." `stepEndUntilNextTurn` consumes the entry.
      const casterController = pt.controller;
      if (!host._delayedIdentityReverts) host._delayedIdentityReverts = [];
      host._delayedIdentityReverts.push({
        cardId: target.id,
        kind: 'enchantress_aura',
        revertPlayer: casterController,
      });
      host.logAction('{name}: animates {card} ({atk}/{life}) until your next turn', { name: sourceName, card: cardLogLabel(target, msg('aura')), atk: target.baseAttack, life: target.baseLife });
      host.recomputeStaticAuras();
    }
  } else if (op === OP.SWOOP_AND_BURROW_TARGET) {
    // Draco Corvus. The SOURCE teleports to the picked tapped
    // minion's cell (a self-move, so the swooper itself isn't gated
    // by unmovable), then the target is force-buried. setSubsurface
    // owns the codex lethality: a minion without Burrowing dies
    // underground.
    const targetMesh = host.meshes.get(target.id);
    const destCell = targetMesh && host._cellAtWorld(targetMesh.position.x, targetMesh.position.z);
    if (!destCell) { host.logAction('{name}: target has no cell. The swoop fizzles', { name: sourceName }); return; }
    host._moveCardToCell(pt.sourceCard, destCell, sourceName, k('teleports'), 'teleport');
    host._setSubsurface(target, 'underground');
  } else if (op === OP.DRAW_OWNER_LOCKED_TO_TARGET) {
    // Archangel Gabriel. The picked allied minion draws a spell that
    // only IT can cast. The draw goes to the picked minion's controller
    // (its `rotated` side) and the drawn copy is tagged _castableOnlyBy
    // the picked minion's instance id (resolveOwnerId 'chosen_ally').
    const allyController = target.rotated ? 'p2' : 'p1';
    const drawn = executeDrawOp(
      host,
      { ...args, owner: 'chosen_ally' },
      sourceCard,
      allyController,
      target.id,
    );
    if (drawn > 0) {
      host.logAction('{name}: {card} draws a spell only it can cast', { name: sourceName, card: cardLogLabel(target, msg('an ally')) });
    }
  } else if (op === OP.PULL_TO_SOURCE) {
    // v1.79. Move the picked unit to the source/caster's cell.
    // v1.87. Forced movement; _moveCardToCell enforces the codex
    // "cannot cross regions" rule and fizzles if it would.
    // Riptide stage two passes `_pullDestCell` to pull INTO the picked
    // site's cell instead of the caster's.
    const destCell = args._pullDestCell || host._resolveSourceCell(pt);
    if (!destCell) { host.logAction('{name}: source has no cell. Pull fizzles', { name: sourceName }); return; }
    host._moveCardToCell(target, destCell, sourceName, k('pulls'), 'forced');
  } else if (op === OP.PULL_TO_TARGET_SITE) {
    // Riptide stage one. The player picked a water site; open the
    // stage-two unit pick (aboveground units cardinally adjacent to it)
    // and pull the chosen unit into the site's cell.
    resolvePullToTargetSite(host, pt, target);
  } else if (op === OP.PUSH_TARGET_AWAY) {
    // v1.79. Push the picked unit one cardinal cell away from the
    // source. v1.87. Forced movement, region-preserving.
    const srcCell = host._resolveSourceCell(pt);
    const targetMesh = host.meshes.get(target.id);
    const unitCell = targetMesh && host._cellAtWorld(targetMesh.position.x, targetMesh.position.z);
    if (!srcCell || !unitCell) { host.logAction('{name}: push fizzles (no cell)', { name: sourceName }); return; }
    const grid = getGameGrid(host.state.spawnConfig);
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const dest = pushDestination(unitCell, srcCell, colPositions.length - 1, rowPositions.length - 1);
    if (!dest) { host.logAction('{name}: {card} push fizzles (no straight-line cell)', { name: sourceName, card: cardLogLabel(target) }); return; }
    host._moveCardToCell(target, dest, sourceName, k('pushes'), 'forced');
  } else if (op === OP.TELEPORT_TARGET) {
    // v1.79. Transition to a cell pick anchored on the picked unit.
    // forcedMovement.eligibleDestinations computes the valid cells
    // from args.destination scope. Followup ops fire AFTER the cell
    // pick resolves, not after the unit pick.
    const dest = args.destination || { scope: 'nearby_of_unit' };
    const targetMesh = host.meshes.get(target.id);
    const unitCell = targetMesh && host._cellAtWorld(targetMesh.position.x, targetMesh.position.z);
    if (!unitCell) { host.logAction('{name}: teleport target has no cell', { name: sourceName }); return; }
    const grid = getGameGrid(host.state.spawnConfig);
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const srcCell = host._resolveSourceCell(pt);
    const validCells = eligibleDestinations(dest, {
      unitCell, sourceCell: srcCell,
      totalCols: colPositions.length - 1,
      totalRows: rowPositions.length - 1,
    });
    // Drop the unit's own cell. Teleporting to where you already
    // are is a no-op and confusing UX.
    validCells.delete(`${unitCell.col},${unitCell.row}`);
    // v1.103. Destination filter: requires_site keeps only cells
    // containing a Site (excludes void). Avatar of Air's "to a nearby
    // site" needs this so the destination set is sites, not voids.
    if (dest.requires_site) {
      const sitedCells = host._cellsWithSites();
      for (const key of [...validCells]) {
        if (!sitedCells.has(key)) validCells.delete(key);
      }
    }
    if (validCells.size === 0) {
      host.logAction('{name}: no valid teleport destination. Fizzles', { name: sourceName });
      return;
    }
    // Stash the picked unit + followups onto pendingCellTarget so
    // _executeCellOp can move + chain.
    host._startCellPick(pt.sourceCard, pt.controller, OP.TELEPORT_TARGET, validCells, {
      args: { ...args, _teleportUnit: target, _teleportFollowups: pt.followupOps || [] },
      casterOverrides: pt.casterOverrides,
    });
    // Signal _tryResolveTarget caller NOT to clear pendingTarget's
    // followupOps. They're now riding on pendingCellTarget.
    pt._teleportInFlight = true;
  } else if (op === OP.INSTALL_DAMAGE_REDIRECT_NEARBY) {
    // v1.77g. Enduring Faith. Push a transient WOULD_TAKE_DAMAGE
    // entry onto the target so any nearby ally taking damage gets
    // redirected to the target. Expiry: caster's next turn start.
    // The cleanup pass walks every card's _transientAutomation each
    // end phase and prunes entries whose expiresAtTurn is reached.
    const caster = pt.controller;
    if (!caster) return;
    const expiresAtTurn = (host.state.turnNumbers?.[caster] || 0) + 1;
    if (!target._transientAutomation) target._transientAutomation = [];
    target._transientAutomation.push({
      trigger: TRIGGER.WOULD_TAKE_DAMAGE,
      scope: { where: 'nearby', filter: { allied: true, exclude_self: true } },
      effect: [{ op: OP.REDIRECT_DAMAGE }],
      expiresAtTurn,
      expiresForPlayer: caster,
      sourceLabel: sourceName,
    });
    host.logAction('{name}: {card} now takes damage for nearby allies until your next turn', { name: sourceName, card: cardLogLabel(target) });
  } else if (op === OP.INSTALL_ON_STRIKE_LIFELINK) {
    // Gift of the Raven. Install a one-shot lifelink grant on the
    // picked allied minion. Reuses the _transientAutomation lifecycle
    // (Enduring Faith pattern): the entry expires at the controller's
    // next turn start if it never fires, and is consumed after one
    // strike that damages a unit this turn.
    const controller = pt.controller;
    if (!controller) return;
    const expiresAtTurn = (host.state.turnNumbers?.[controller] || 0) + 1;
    if (!target._transientAutomation) target._transientAutomation = [];
    target._transientAutomation.push(
      buildStrikeLifelinkEntry(controller, expiresAtTurn, sourceName),
    );
    host.logAction('{name}: the next time {card} strikes a unit this turn, the damage heals you', { name: sourceName, card: cardLogLabel(target, msg('an ally')) });
  } else if (op === OP.DRAW_IF_TARGET_ON_ENEMY_SITE) {
    // Fade. Conditional draw: only if the picked minion occupies an
    // enemy-controlled site. Rides the followup chain so it resolves
    // against the same chosen minion. The draw credits the spell's
    // controller (the caster), not the minion's.
    const targetMesh = host.meshes.get(target.id);
    const cell = targetMesh && host._cellAtWorld(targetMesh.position.x, targetMesh.position.z);
    if (!isOnEnemySite(target, cell, host.meshes, host._cellAtWorld)) return;
    const drawn = executeDrawOp(host, args, sourceCard, pt.controller);
    if (drawn > 0) {
      host.logAction('{name}: {card} occupies an enemy site. Draw a card', { name: sourceName, card: cardLogLabel(target, msg('the ally')) });
    }
  } else if (op === OP.TRIGGER_TARGET_DEATHRITE) {
    // Feign Death. Fire the picked allied minion's Deathrite in-band.
    // The minion stays in play; the Stealth grant + spell draw ride the
    // followup / loop chain. No-op log when it has no Deathrite.
    const fired = triggerTargetDeathrite(host, target);
    if (fired) host.logAction("{name}: triggered {card}'s Deathrite", { name: sourceName, card: cardLogLabel(target, msg('an ally')) });
    else host.logAction('{name}: {card} has no Deathrite', { name: sourceName, card: cardLogLabel(target, msg('the ally')) });
  }
}

// v1.45. Return EVERY activated ability for a card, including the
// engine-synthesised built-ins (every Avatar gets the codex
// "Tap → Play or draw a site" ability; the rule lives in the
// codex, not on the card text, so the parser can't emit it).
export function activatedEntriesFor(host, cardInstance) {
  // A manual (automations-off) session resolves nothing on the players'
  // behalf. No activated ability surfaces in the action menu, including the
  // avatar's built-in site draw. The players read the card and move the
  // pieces themselves; the server refuses the 'ability' intent domain too.
  if (host._automationsEnabled?.() === false) return [];
  // Disabled cards lose all abilities. No entry surfaces (v1.16b).
  if (isDisabled(cardInstance)) return [];
  const out = [];
  // Avatar built-in: "Tap → Draw a site." Per codex this is an
  // activated ability on every Avatar. Cards that grant a similar
  // ability (Druid, Imposter variants, etc.) will use the same
  // _builtin tag when their parser entries land.
  if (cardInstance.type === 'Avatar') {
    const rules = resolveAvatarRules(cardInstance);
    // Pathfinder-style avatars replace the standard site-draw tap with
    // their own; the builtin is suppressed so the action menu only
    // shows the avatar's real ability. An Imposter "gains their
    // abilities" but does not BECOME them. It keeps its own
    // tap-to-play/draw-site builtin, so evaluate the suppression
    // against its true identity, never the Pathfinder-style mask it
    // happens to be wearing.
    const baseCardId = maskedImposterBaseCardId(cardInstance);
    const siteRules = baseCardId ? resolveAvatarRules({ cardId: baseCardId }) : rules;
    if (!siteRules.replacesBuiltinAvatarSite) {
      out.push({
        id: '__builtin_avatar_site',
        trigger: 'activated',
        cost: [{ type: 'tap' }],
        description: 'Draw a site',
        _builtin: 'avatar_site',
      });
    }
    // v1.74. Per-avatar extras declared in avatarRules. Sorcerer's
    // "Tap → Draw a spell" is the first; future avatars (Geomancer's
    // second Tap, Necromancer's mana-pay Skeleton spawn, etc.) hang
    // entries off the same field so the action menu surfaces every
    // ability the avatar carries.
    const extras = rules.extraActivatedAbilities || [];
    for (const entry of extras) out.push(entry);
    // Borrowed identities are ADDITIVE ("gain their abilities"): while
    // an Imposter wears a mask or Dragonlord borrows its Dragon,
    // `resolveAvatarRules` resolves by the borrowed cardId. So the
    // ORIGINAL form's own activated abilities (Imposter's Mask,
    // Dragonlord's set-aside/invoke) would fall out of the extras
    // list. Merge them back in from the identity snapshot.
    const originalCardId = cardInstance._originalIdentity?.cardId
      || cardInstance._dragonIdentitySnapshot?.cardId;
    if (originalCardId && originalCardId !== cardInstance.cardId) {
      const originalExtras = resolveAvatarRules({ cardId: originalCardId }).extraActivatedAbilities || [];
      for (const entry of originalExtras) {
        if (!out.some((e) => (e._builtin && e._builtin === entry._builtin) || (e.id && e.id === entry.id))) {
          out.push(entry);
        }
      }
    }
  }
  // Parser-emitted activated entries. Intrinsic from the catalog
  // PLUS any GRANT_TRIGGER_TO_SCOPE-installed entries on this card
  // whose trigger is 'activated' (Homecoming's "Tap → Gain (1)" is
  // granted to nearby minions; the entries live in
  // `cardInstance._grantedTriggers`, rebuilt every static-aura pass).
  const fullCard = host._getFullCard(cardInstance.cardId);
  const granted = (cardInstance._grantedTriggers || []).filter((e) => e.trigger === 'activated');
  // Silenced minions surface no parser/granted activated abilities
  // That box text is offline. Avatar built-ins above still surface
  // because Silence's scope filter (type: Minion) never targets
  // Avatars, but the loop is gated explicitly for forward-safety.
  const entries = isCardSilenced(cardInstance)
    ? []
    : [...(fullCard?.automation || []), ...granted];
  // Dedupe against built-ins / avatarRules extras whose handler in
  // _activateAbility already covers the same parser-emitted clause
  // (Sorcerer's "Tap → Draw a spell" is both an extra _builtin
  // 'tap_draw_spell' and matches tryActivatedTapDraw. Surfacing
  // both would render a redundant generic "Activate" row alongside
  // the labelled "Draw a spell").
  const coveredSignatures = new Set(
    out.map(builtinSignature).filter(Boolean),
  );
  for (const entry of entries) {
    if (entry.trigger !== 'activated') continue;
    const op = entry.effect?.[0]?.op;
    if (!op || op === OP.REMINDER) continue;
    const sig = parsedSignature(entry);
    if (sig && coveredSignatures.has(sig)) continue;
    out.push(entry);
  }
  return out;
}

// Pure cost check for an activated entry: locally controllable +
// untapped if there's a tap cost + enough mana if there's a mana
// cost. Used to disable the action menu button when the cost
// can't be paid.
export function canActivateEntry(host, card, entry) {
  if (!card || !entry || isDisabled(card)) return false;
  if (entry.effect?.some((e) => e.op === OP.REVEAL_DRAW_DAMAGE_BEARER)) {
    const bearer = findBearerOf(host, card);
    if (!bearer || isDisabled(bearer) || isCardSilenced(bearer)
        || bearer.tapped || hasSummoningSickness(bearer)) return false;
  }
  // Manual session: no activations at all (activatedEntriesFor already
  // surfaces nothing. This is the belt for direct callers).
  if (host._automationsEnabled?.() === false) return false;
  // No activations during the start choreography (GH-56): before the server
  // flips matchPhase to 'playing' (roll-off → pre-game → opening deal →
  // mulligan) no ability may fire. A pre-game activation could draw out of
  // turn. The server rejects the intent anyway; this greys the menu row.
  if (host.state.matchPhase !== 'playing') return false;
  const controller = card.rotated ? 'p2' : 'p1';
  const { trackers } = host.state.gameState;
  // v1.81. Once-per-turn gate. The flag on the entry pairs with the
  // existing _abilitiesUsedThisTurn Set (cleared in
  // _stepPruneExpiredEffects at the activating player's end-phase).
  if (entry.once_per_turn) {
    const key = host._oncePerTurnKey(entry);
    if (card._abilitiesUsedThisTurn?.has(`act:${key}`)) return false;
  }
  for (const cost of entry.cost || []) {
    if (cost.type === 'tap') {
      if (card.tapped) return false;
      // v1.54. Summoning sickness blocks tap-as-cost (Avatars
      // are never sick so this only affects fresh-summoned
      // Minions with their own activated abilities).
      if (hasSummoningSickness(card)) return false;
    } else if (cost.type === 'mana') {
      if ((trackers[controller]?.mana || 0) < (cost.amount || 0)) return false;
    } else if (cost.type === 'life') {
      // v1.81. Life cost. Codex Pay Life: "You can only pay X life
      // if your life total is at least X." So life >= cost (NOT
      // life > cost; v1.83 off-by-one fix). Payment can drop life
      // to 0, which enters Death's Door via _dealLifeLoss.
      if ((trackers[controller]?.life || 0) < (cost.amount || 0)) return false;
    } else if (cost.type === 'threshold') {
      // v1.81. Threshold cost. Persistent. Not deducted, just gated.
      const res = host.playerResources.snapshot(controller);
      const have = res[cost.element] || 0;
      if (have < (cost.amount || 0)) return false;
    }
  }
  // Discard cost gate (Ignis Rex). The hand must hold a matching
  // card or the menu row greys out.
  const discardCost = (entry.cost || []).find((c) => c.type === 'discard');
  if (discardCost) {
    const hand = host.getActiveHandCards?.() || [];
    if (!hand.some((h) => matchesFilter(host._getFullCard(h.cardId) || h, discardCost.filter || {}))) {
      return false;
    }
  }
  // Sacrifice cost gate. A self-sacrifice (Wiccan Tools) is always
  // payable; a board sacrifice (Cauldron Crones' "another minion here")
  // greys out when no eligible victim shares the source's cell.
  const sacCost = (entry.cost || []).find((c) => c.type === 'sacrifice');
  if (sacCost && !sacCost.self) {
    if (sacrificeVictimsHere(host.meshes, host._cellAtWorld.bind(host), card).length === 0) {
      return false;
    }
  }
  // Cradle of Etherrum gates. Both abilities read "during your turn"
  // and need a non-empty spellbook; the cast half additionally needs
  // the TOP card to match the filter and be affordable at the
  // destination cell (waivers and cost modifiers there apply).
  const firstActivatedOp = entry.effect?.[0]?.op;
  if (firstActivatedOp === OP.OPEN_SCRY_TOP || firstActivatedOp === OP.CAST_TOP_OF_SPELLBOOK) {
    if (host.state.currentTurn !== controller) return false;
    const ownerRotated = controller === 'p2';
    const args = entry.effect[0].args || {};
    const pileName = firstActivatedOp === OP.CAST_TOP_OF_SPELLBOOK
      ? 'Spellbook'
      : `${(args.pile || 'spellbook').charAt(0).toUpperCase()}${(args.pile || 'spellbook').slice(1)}`;
    const pile = host.state.gameState.piles.find((p) => p.name === pileName && !!p.rotated === ownerRotated);
    if (!pile || pile.cards.length === 0) return false;
    if (firstActivatedOp === OP.CAST_TOP_OF_SPELLBOOK) {
      const top = pile.cards[pile.cards.length - 1];
      const fullTop = host._getFullCard(top.cardId);
      if (!fullTop || !matchesFilter(fullTop, args.filter || {})) return false;
      const cell = castFromTopAnchorCell(host, card);
      const budget = castFromTopBudget(host, controller, fullTop, args.cost_delta, cell);
      if (!budget.manaOk || !budget.thresholdsOk) return false;
    }
  }
  // v1.103. Entry-level condition gate. Avatar of Air's
  // "if you occupy an Air site" is a precondition, not a cost; the
  // context menu greys out and _activateAbility bails before paying.
  if (entry.condition) {
    const fullCard = host._getFullCard(card.cardId)|| null;
    if (!host._evaluateCondition(entry.condition, card, fullCard)) return false;
  }
  // Realm-Eater Digest gate: only meaningful while immobile (the
  // destroy-on-attack passive in the sidecar applies `immobile`;
  // Digest is the only thing that clears it).
  if (entry._builtin === 'realm_eater_digest') {
    if (!getEffectiveStatuses(card).includes('immobile')) return false;
  }
  // Imposter Mask gate: at least one Avatar must sit in the
  // controller's Collection. Without this the action menu would offer
  // a (3) ability that bricks on every press.
  if (entry._builtin === 'imposter_mask') {
    const controller = card.rotated ? 'p2' : 'p1';
    if (!host._collectionHasAvatar(controller)) return false;
  }
  // Archimago cast-a-copy gate: at least three Magic spells must sit
  // in the controller's Cemetery. Without this the action menu would
  // surface a no-op (the costly banish-three can't be paid).
  if (entry._builtin === 'archimago_cast_copy') {
    const controller = card.rotated ? 'p2' : 'p1';
    if (!host._cemeteryHasThreeMagic(controller)) return false;
  }
  // Deathspeaker cast-a-copy gate: at least one dead Minion must sit
  // in the controller's Cemetery.
  if (entry._builtin === 'deathspeaker_cast_copy') {
    const controller = card.rotated ? 'p2' : 'p1';
    if (!host._cemeteryHasMinion(controller)) return false;
  }
  // Animist Spirit gate: at least one Magic must sit in the controller's
  // hand. Without this the action menu would offer an ability that arms
  // nothing. Re-activating while already armed is allowed (it toggles off).
  if (entry._builtin === 'animist_spirit') {
    const controller = card.rotated ? 'p2' : 'p1';
    if (host._animistSpiritArm?.[controller]) return true;
    const hand = controller === 'p2' ? host.state.handCards2 : host.state.handCards;
    const hasMagic = (hand || []).some((c) => {
      if (c.faceDown) return false;
      const fc = host._getFullCard(c.cardId);
      return (fc?.type ?? fc?.guardian?.type ?? c.type) === 'Magic';
    });
    if (!hasMagic) return false;
  }
  // Dragonlord invoke gate. The set-aside has no menu entry. It only
  // happens through the pre-game ceremony (runDragonlordSetAsideSequence).
  // Invoke: needs a companion, no identity already borrowed this turn,
  // and the player must meet the Dragon's printed threshold. The
  // "Once on your turn" timing is carried by the once_per_turn flag
  // like every other activated ability. Gating on state.currentTurn
  // here broke sandbox free-testing, where the turn cycle doesn't
  // track which seat the user is piloting.
  if (entry._builtin === 'dragonlord_invoke') {
    const controller = card.rotated ? 'p2' : 'p1';
    if (!card._dragonlordCompanion || card._dragonIdentityUntilTurnEnd) return false;
    const companion = host._getFullCard(card._dragonlordCompanion);
    if (!meetsDragonThreshold(host.playerResources.snapshot(controller), companion)) return false;
  }
  return true;
}

// The equipped host carrying `artifact`, or null when the artifact
// sits on the field unattached. Walks the meshes for the unit whose
// equippedCards array holds the artifact's id.
function findBearerOf(host, artifact) {
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (c?.equippedCards?.some((eq) => eq.id === artifact.id)) return c;
  }
  return null;
}

// Pnakotic Manuscript host adapter. Narrow surface over the existing
// draw + damage + catalog primitives.
function revealDrawDamageHost(host) {
  return {
    findBearer: (artifact) => findBearerOf(host, artifact),
    isTapped: (card) => !!card.tapped,
    tapBearer: (bearer) => {
      const mesh = host.meshes.get(bearer.id);
      if (mesh) host._applyTap(bearer, mesh, true, { source: 'activate' });
    },
    spellbookTopFor: (controller) => {
      const pile = host.findPileByNameForPlayer('Spellbook', controller === 'p2' ? 2 : 1);
      if (!pile || pile.cards.length === 0) return null;
      const instance = pile.cards[pile.cards.length - 1];
      return { instance, fullCard: host._getFullCard(instance.cardId) };
    },
    drawSpellFor: (controller) => { executeDrawOp(host, { amount: 1, source: 'spell' }, null, controller); },
    getCardCost,
    damageTarget: (card, amount, opts) => host._applyDamageToTarget(card, amount, opts),
    logAction: (key, values) => host.logAction(key, values),
  };
}

// Salmon of Knowledge granted-ability host adapter. D20 roll (the
// outcome syncs through the banish + draw board mutations; the rolled
// number rides the broadcast log), banish-the-source, draw.
function rollBanishDrawHost(host) {
  return {
    rollDie: (sides) => Math.floor((host._silentOpsHost?.rng?.() ?? Math.random()) * sides) + 1,
    findCardById: (id) => host._findCardById(id),
    banishCard: (card) => host.banishCard(card),
    drawSpellsFor: (controller, count) => executeDrawOp(host, { amount: count, source: 'spell' }, null, controller),
    logAction: (key, values) => host.logAction(key, values),
  };
}

// Erik's Curiosa host adapter. Rip-to-pieces banish + the Collection
// pick (reuses the existing pile-search dialog via _startSearchEffect).
function drawFromCollectionHost(host) {
  return {
    banishCard: (card) => host.banishCard(card),
    openCollectionSearch: (controller, sourceName) => {
      host._startSearchEffect(
        { id: `eriks-${controller}` },
        controller,
        { pile: 'collection', filter: {}, shuffle_after: false },
        sourceName,
      );
    },
    logAction: (key, values) => host.logAction(key, values),
  };
}

// Searing Truth caster-side host adapter. Resolve the target seat,
// allocate a correlation id, and emit the forced reveal-draw request.
// The damage is applied later when the target's `revealDrawResult`
// reply lands (see applyForceRevealDrawDamage).
function forceRevealDrawCasterHost(host, targetSeat) {
  return {
    // The caster picked the target player up front, so the op emits for
    // that seat verbatim (no automatic opponent default).
    opponentSeat: () => targetSeat,
    newRequestId: () => `srt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    emitForceRevealDraw: (targetSeat, count, requestId) => {
      // Auth: Searing Truth is server-owned. Never the legacy handshake.
      if (host._authZonesEnabled?.()) return;
      if (!host._pendingForceRevealDraws) host._pendingForceRevealDraws = new Map();
      host._pendingForceRevealDraws.set(requestId, targetSeat);
      host.sync.forceRevealDraw(targetSeat, count, requestId);
    },
    logAction: (key, values) => host.logAction(key, values),
  };
}

// Searing Truth target-side host adapter. Draw one spell from THIS
// client's own spellbook into its hand, mark it revealed, and broadcast
// the updated hand info so the caster sees the revealed faces. The draw
// routes through the unified funnel (pile:sync + on_draw + per-turn
// counters all fire for the correct seat).
function forceRevealDrawTargetHost(host) {
  const localSeat = host._localSeat();
  const playerNum = localSeat === 'p2' ? 2 : 1;
  return {
    drawRevealedSpell: () => {
      const pile = host.findPileByNameForPlayer('Spellbook', playerNum);
      if (!pile || pile.cards.length === 0) return null;
      const card = host.drawCard(pile.id, { skipLog: true });
      if (!card) return null;
      card.revealed = true;
      card.nameKnown = true;
      return card;
    },
    costOf: (card) => getCardCost(host._getFullCard(card.cardId)).mana || 0,
  };
}

// Searing Truth. "Target player draws and reveals two spells, then takes
// damage equal to the higher mana cost." The card targets a PLAYER, so the
// caster chooses: enqueue a two-option prompt (Opponent first. The usual
// burn target. With Yourself as the alternative). The card stays in flight
// until the pick resolves (spellResolutionPending sees the pending prompt),
// then resolveForceRevealTarget runs the draw + damage and the spell goes to
// the cemetery via the universal dispose path.
export function castForcedRevealDraw(host, sourceCard, controller, args) {
  const opponent = controller === 'p2' ? 'p1' : 'p2';
  const prompt = {
    id: `prompt-${sourceCard.id}-srt-${Math.random().toString(36).slice(2, 8)}`,
    cardInstance: sourceCard,
    cardName: sourceCard.name || 'Searing Truth',
    controller,
    promptType: 'force_reveal_target',
    _count: args?.count ?? 2,
    description: t('Choose the target player'),
    choices: [
      { value: opponent, label: t('Opponent') },
      { value: controller, label: t('Yourself') },
    ],
  };
  host.setState((s) => ({ pendingPrompts: [...(s.pendingPrompts || []), prompt] }));
}

// Run the chosen target's draw + reveal + self-damage. When this client
// owns both seats (casual sandbox, or any offline single-client match.
// `connectionStatus === 'offline'`, the same predicate isOwnedByLocalPlayer
// uses), resolve the whole effect locally. Only when a peer is actually
// connected does the draw need to run on the target's own client via the
// cross-client handshake. Otherwise we'd wait forever for a reply that
// never comes.
export function resolveForceRevealTarget(host, sourceCard, controller, targetSeat, count) {
  const ownsBothSeats = host.state?.connectionStatus === 'offline';
  if (ownsBothSeats) {
    const higher = resolveForcedRevealDrawForSeat(host, targetSeat, count);
    host.logAction('{name}: {who} draws and reveals {n} spells', {
      name: cardLogLabel(sourceCard, 'Searing Truth'),
      who: targetSeat === 'p1' ? msg('Player 1') : msg('Player 2'),
      n: count,
    });
    if (higher > 0) {
      const avatar = host._findAvatarCard(targetSeat === 'p2');
      if (avatar) {
        host._applyDamageToTarget(avatar, higher, {
          sourceLabel: sourceCard.name || 'Searing Truth',
          log: true,
          sweep: true,
          floatingDamage: true,
        });
      }
    }
    return;
  }
  castForceRevealDraw(sourceCard, controller, { count }, forceRevealDrawCasterHost(host, targetSeat));
}

// Sandbox-only: draw + reveal `count` spells into a SPECIFIC seat's hand
// (handCards / handCards2 in sandbox) and return the higher printed
// cost. Multiplayer never uses this. There the target draws on its own
// client via forceRevealDrawTargetHost.
function resolveForcedRevealDrawForSeat(host, seat, count) {
  const playerNum = seat === 'p2' ? 2 : 1;
  const handKey = seat === 'p2' ? 'handCards2' : 'handCards';
  return resolveForceRevealDraw(count, {
    drawRevealedSpell: () => {
      const pile = host.findPileByNameForPlayer('Spellbook', playerNum);
      if (!pile || pile.cards.length === 0) return null;
      const card = host.drawCard(pile.id, { skipLog: true });
      if (!card) return null;
      card.revealed = true;
      card.nameKnown = true;
      return card;
    },
    costOf: (card) => getCardCost(host._getFullCard(card.cardId)).mana || 0,
  });
}

// Target side: draw + reveal `count` spells and report the higher cost.
export function resolveForcedRevealDraw(host, count) {
  return resolveForceRevealDraw(count, forceRevealDrawTargetHost(host));
}

// Caster side: the target replied with the higher cost. Deal it to that
// player's Avatar through the damage funnel. The pending map is keyed by
// requestId so a stale / duplicate reply can't double-damage.
export function applyForcedRevealDrawDamage(host, requestId, higherCost) {
  const targetSeat = host._pendingForceRevealDraws?.get(requestId);
  if (!targetSeat) return;
  host._pendingForceRevealDraws.delete(requestId);
  if (!higherCost || higherCost <= 0) return;
  const avatar = host._findAvatarCard(targetSeat === 'p2');
  if (!avatar) return;
  host._applyDamageToTarget(avatar, higherCost, {
    sourceLabel: 'Searing Truth',
    log: true,
    sweep: true,
    floatingDamage: true,
  });
}

// Generic activator. Pays costs, then dispatches by entry kind:
// built-ins route to a dedicated handler; parser-emitted entries
// fall through to applyCardAutomation's existing 'activated'
// pipeline.
export function activateAbility(host, cardInstance, entry) {
  host.closeContextMenu();
  if (!cardInstance || !entry) return;
  if (entry.effect?.some((e) => e.op === OP.REVEAL_DRAW_DAMAGE_BEARER)
      && !canActivateEntry(host, cardInstance, entry)) return;
  // Manual session: nothing activates. Client-resolved entries would
  // otherwise still fire even though the server refuses the intent path.
  if (host._automationsEnabled?.() === false) return;
  const controller = cardInstance.rotated ? 'p2' : 'p1';
  const { trackers } = host.state.gameState;

  // v1.104. Mandatory-action gate is advisory only; the activation
  // proceeds and the warning lands in the log. Avatar's site-play
  // built-in IS the mandatory resolution so it's exempt from the
  // notice entirely.
  if (entry._builtin !== 'avatar_site') {
    const blocked = host._anyMandatoryActionPending(controller);
    if (blocked) host.addAdvisoryLog(blocked, cardInstance);
  }

  // ── Cost validation ────────────────────────────────
  // v1.81. Once-per-turn check (toast feedback to match cost errors).
  if (entry.once_per_turn) {
    const key = host._oncePerTurnKey(entry);
    if (cardInstance._abilitiesUsedThisTurn?.has(`act:${key}`)) {
      toast.error(t('Already used this turn'));
      return;
    }
  }
  for (const cost of entry.cost || []) {
    if (cost.type === 'tap') {
      if (cardInstance.tapped) { toast.error(t('Card is already tapped')); return; }
    } else if (cost.type === 'mana') {
      if ((trackers[controller].mana || 0) < (cost.amount || 0)) {
        toast.error(t('Need {need} mana. You have {have}.', { need: cost.amount, have: trackers[controller].mana || 0 }));
        return;
      }
    } else if (cost.type === 'life') {
      if ((trackers[controller].life || 0) < (cost.amount || 0)) {
        toast.error(t('Need at least {need} life. You have {have}.', { need: cost.amount, have: trackers[controller].life || 0 }));
        return;
      }
    } else if (cost.type === 'threshold') {
      const have = host.playerResources.snapshot(controller)[cost.element] || 0;
      if (have < (cost.amount || 0)) {
        toast.error(t('Need {need} {element} threshold. You have {have}.', { need: cost.amount, element: cost.element, have }));
        return;
      }
    } else if (cost.type === 'discard') {
      // Skip re-validation on the resume pass. The picked card has
      // already left the hand by then.
      if (!entry._discardPaid) {
        const hand = host.getActiveHandCards() || [];
        const eligible = hand.filter((h) => matchesFilter(host._getFullCard(h.cardId) || h, cost.filter || {}));
        if (eligible.length === 0) {
          toast.error(t('No matching card in hand to discard'));
          return;
        }
      }
    }
  }

  // The activation click in the actions menu IS the confirmation. The
  // controller already chose to do this, so there's no confirm prompt for
  // them; the ability resolves immediately. The opponent still gets a
  // read-only storyline notice so they see what happened (otherwise an
  // activated ability would be invisible to them). Confirm prompts are
  // reserved for effects the controller did NOT initiate. Watchers,
  // triggers, combat, movement. Where they need to register what fired.
  const fullCard = host._getFullCard(cardInstance.cardId) || cardInstance;
  host._broadcastAbilityNotice(cardInstance, fullCard, entry, controller);
  resolveActivatedAbility(host, cardInstance, entry);
}

// v1.111. The resolution half of an activated ability. Runs ONLY after
// the caster confirms the activated_confirm prompt (or immediately for
// the self-confirming choice-drawer carve-out). The body below is the
// pre-v1.111 activateAbility body, logic unchanged: it pays deferred
// costs, dispatches built-ins, runs the switch pickers, the
// single-target fallback, and finally the generic applyCardAutomation
// pipeline.
export function resolveActivatedAbility(host, cardInstance, entry) {
  if (!cardInstance || !entry) return;
  const controller = cardInstance.rotated ? 'p2' : 'p1';

  // Auth (S2): an activated entry the SERVER resolves also has its costs
  // SERVER-paid. RunAbilityIntent (server-lead ops) and the activated trigger
  // fan-out (fully-silent entries) validate + pay tap/mana/discard off the
  // intent and reject unpayable activations. The client must NOT pre-pay
  // (a tap/mana write landing before the intent would make the server see the
  // cost as already spent and reject the honest activation), so the whole
  // local payment block below is skipped; the paid cost folds from the diff.
  const op0 = entry.effect?.[0]?.op;
  const SERVER_PAID_COST_TYPES = new Set(['tap', 'mana', 'discard', 'threshold']);
  const serverPaysCosts = entry.trigger === 'activated' && host._authZonesEnabled?.()
    && (entry.cost || []).every((c) => SERVER_PAID_COST_TYPES.has(c.type))
    && (
      entryIsSilentPrefixChoice(entry)
      || SERVER_ABILITY_CELL_OPS.has(op0) || SERVER_ABILITY_MODAL_OPS.has(op0)
      || SERVER_ABILITY_PILE_OPS.has(op0) || SERVER_ABILITY_DIRECT_OPS.has(op0)
      || ((entry.effect || []).length > 0
          && (entry.effect || []).every((e) => SERVER_SILENT_OPS.has(e.op)))
    );

  // Discard costs are paid via a hand pick (Ignis Rex: "discard a
  // fire card → ..."). Defer ALL payment + resolution until the
  // player clicks the card to discard; ESC / clicking elsewhere
  // cancels with nothing charged. _resolveDiscardCostPick performs
  // the discard, stamps `entry._discardPaid`, and re-enters here.
  // Server-paid entries skip the local pick. The server stages the
  // mandatory hand pile prompt and chains the effect off its resume.
  const discardCost = (entry.cost || []).find((c) => c.type === 'discard');
  if (discardCost && !entry._discardPaid && !serverPaysCosts) {
    host._pendingDiscardCost = {
      controller,
      sourceCardId: cardInstance.id,
      entry,
      filter: discardCost.filter || {},
    };
    toast.message(t('Click a glowing card in your hand to discard it.'));
    host.forceUpdate();
    return;
  }

  // Sacrifice cost (Wiccan Tools "Sacrifice → Draw a spell ..."). Like
  // the discard cost, the whole effect defers until the sacrifice is
  // paid. A self-sacrifice resolves inline (no pick); a board sacrifice
  // hands off to the target-pick halo. The draw + any followups ride the
  // entry's SACRIFICE_DRAW op once the victim leaves play.
  const sacCost = (entry.cost || []).find((c) => c.type === 'sacrifice');
  if (sacCost && !entry._sacrificePaid) {
    // Auth: a SELF-sacrifice on a fully server-silent entry (Wiccan Tools'
    // sacrifice_draw + bearer grant) delegates whole. The silent trigger
    // intent makes the server pay the sacrifice (detach + cemetery), draw,
    // and stamp the bearer's Spellcaster + threshold waiver; everything
    // folds from the diff. Skip the ENTIRE local sacrifice ceremony
    // (startSacrificeCost self-branch, finishSacrificeDraw,
    // grantSpellcasterNoThresholdToBearer) so it resolves once.
    if (host._authZonesEnabled?.() && sacCost.self
        && (entry.effect || []).length > 0
        && (entry.effect || []).every((e) => SERVER_SILENT_OPS.has(e.op))) {
      host._sendMatchIntent(triggerIntent(cardInstance.id, 'activated'));
      return;
    }
    startSacrificeCost(host, cardInstance, controller, entry, sacCost, { kind: 'activated' });
    return;
  }

  // Imposter Mask defers mana payment until the player actually
  // picks an Avatar in the Collection. Validation above already
  // confirmed the pool has enough; the deduction runs inside
  // `_resolveMaskPick` so cancelling the picker (ESC, click-outside,
  // close button) refunds nothing because nothing was charged. The
  // pick handler re-validates mana at apply time in case the player
  // spent it elsewhere in the meantime.
  if (entry._builtin === 'imposter_mask') {
    const manaCost = (entry.cost || []).find((c) => c.type === 'mana')?.amount || 0;
    host._startImposterMask(cardInstance, controller, manaCost);
    return;
  }

  // Archimago "Banish three magic spells in your cemetery → Cast a
  // copy of one of them." No standard cost (no tap, no mana). The
  // banish-three is paid inside the resolution flow, so we shortcut
  // straight into the cemetery picker. The gate already confirmed at
  // least three Magic spells are present.
  if (entry._builtin === 'archimago_cast_copy') {
    host._startArchimagoCastCopy(cardInstance, controller);
    return;
  }

  // Deathspeaker "Banish a dead minion each turn → cast a copy of
  // it." Dispatched before the generic once-per-turn mark: the mana
  // cost is dynamic (the picked minion's cost; (0) at Death's Door)
  // and both payment and the per-turn mark land inside the pick
  // resolver, so cancelling the picker doesn't burn the budget.
  if (entry._builtin === 'deathspeaker_cast_copy') {
    host._startDeathspeakerCastCopy(cardInstance, controller);
    return;
  }

  // Animist "Summon a magic as a Spirit." No cost (no tap, no mana) and
  // repeatable. It just arms a one-shot flag, so shortcut before the
  // cost/stealth/once-per-turn pipeline. The hand → table boundary
  // consumes the arm and flips the played magic into a Spirit Minion.
  if (entry._builtin === 'animist_spirit') {
    host._armAnimistSpirit(cardInstance, controller);
    return;
  }

  // Realm-Eater Digest (auth): server-owned. The ability intent pays the tap
  // and clears `immobile` server-side; both fold back from the diff. Shortcut
  // BEFORE the cost pipeline so the local tap doesn't pre-pay (the server
  // would see the avatar already tapped and reject the honest activation).
  if (entry._builtin === 'realm_eater_digest' && host._authZonesEnabled?.()) {
    host._emitAbilityIntent?.(cardInstance, host._oncePerTurnKey(entry), false);
    return;
  }

  // ── Cost payment ───────────────────────────────────
  // Server-paid entries (S2) skip local payment entirely. The server pays
  // tap/mana off the ability/trigger intent and the diff folds it back.
  if (!serverPaysCosts) {
    for (const cost of entry.cost || []) {
      if (cost.type === 'tap') {
        const mesh = host.meshes.get(cardInstance.id);
        if (mesh) host._applyTap(cardInstance, mesh, true, { source: 'activate' });
      } else if (cost.type === 'mana') {
        host.playerResources.payMana(controller, cost.amount || 0);
      } else if (cost.type === 'life') {
        // Life payment routes through the engine class so Death's
        // Door logic stays uniform with combat / spell damage. The
        // _canActivateEntry gate already ensured the payment won't
        // kill the player.
        host.playerResources.loseLife(
          controller,
          cost.amount || 0,
          `${cardInstance.name || 'Card'} activation`,
        );
      }
      // 'threshold' has no payment step. It's a gate-only cost.
    }
  }
  // v1.81. Mark once-per-turn AFTER cost validation passes so a
  // failed activation doesn't burn the budget.
  if (entry.once_per_turn) {
    const key = host._oncePerTurnKey(entry);
    if (!cardInstance._abilitiesUsedThisTurn) cardInstance._abilitiesUsedThisTurn = new Set();
    cardInstance._abilitiesUsedThisTurn.add(`act:${key}`);
    // On the auth path the once-per-turn marker is owned by the server: the
    // ability INTENT makes the gating decision authoritative and stamps the
    // durable marker (same field the legacy flags broadcast writes). Suppress
    // the legacy SET_CARD_FLAGS broadcast for the marker when the intent went
    // out. The opponent renders it from the intent diff. Off the auth path the
    // legacy broadcast runs unchanged.
    if (!host._emitAbilityIntent?.(cardInstance, key, true)) {
      host._broadcastAbilitiesUsed?.(cardInstance);
    }
  }

  // v1.55. Stealth consumed when the minion activates an ability
  // (codex: "Stealth is lost after the minion ... casts a spell,
  // activating an ability"). Avatars never have Stealth so this
  // only affects Minions; the consume is a no-op otherwise.
  if (consumeStealth(cardInstance)) {
    host.logAction('{name} loses Stealth', { name: cardLogLabel(cardInstance, msg('Card')) });
    host.rebuildStatusBadges(cardInstance.id);
    host._broadcastCardStatus(cardInstance.id, 'stealth', false); // v1.65
  }

  // Auth: an activated ability that leads with a server-resolvable op. A CHOICE
  // (Avatar of Air's teleport) or a CELL pick (Pathfinder). Is resolved by the
  // server from the ability intent: it emits the pick + drives the chain, and
  // authChoice renders it from the mirror. Skip the local dispatch so the effect
  // resolves once. A once-per-turn ability already emitted the intent above; a
  // REPEATABLE one emits it here (it has no gate). Cost was already paid above.
  const serverChoiceLead = entryIsSilentPrefixChoice(entry);
  const serverCellLead = SERVER_ABILITY_CELL_OPS.has(entry.effect?.[0]?.op);
  const serverModalLead = SERVER_ABILITY_MODAL_OPS.has(entry.effect?.[0]?.op);
  const serverPileLead = SERVER_ABILITY_PILE_OPS.has(entry.effect?.[0]?.op);
  const serverDirectLead = SERVER_ABILITY_DIRECT_OPS.has(entry.effect?.[0]?.op);
  if (entry.trigger === 'activated' && host._authZonesEnabled?.() && (serverChoiceLead || serverCellLead || serverModalLead || serverPileLead || serverDirectLead)) {
    if (!entry.once_per_turn) host._emitAbilityIntent?.(cardInstance, host._oncePerTurnKey(entry), false);
    // Avatar of Fire: the server stamps the authoritative ignite window off the
    // intent; the local flag is pure UX (hand glow + toast for the fire sites).
    if (entry.effect?.[0]?.op === OP.AVATAR_OF_FIRE_IGNITE) {
      host._startAvatarOfFireIgnite(cardInstance, controller);
    }
    return;
  }

  // ── Dispatch on entry kind ─────────────────────────
  // Dragonlord "pay (3) to gain its abilities this turn." The (3)
  // was paid and the once-per-turn budget marked by the generic
  // pipeline above; the resolver applies the borrowed identity.
  if (entry._builtin === 'dragonlord_invoke') {
    host._resolveDragonlordInvoke(cardInstance, controller);
    return;
  }
  if (entry._builtin === 'avatar_site') {
    // Tap → draw a site from this Avatar's Atlas. Tap cost was
    // already paid above; the gate in _canActivateEntry kept the
    // button disabled if the Avatar was already tapped.
    const ownerRotated = !!cardInstance.rotated;
    const atlas = host.state.gameState.piles.find(
      (p) => p.name === 'Atlas' && !!p.rotated === ownerRotated,
    );
    if (atlas && atlas.cards.length > 0) {
      host.drawCard(atlas.id);
    } else {
      toast.message(t('Your Atlas is empty.'));
    }
    return;
  }
  // Realm-Eater's "Digest a site" tap option. Clears the `immobile`
  // status applied by Destroy, freeing the avatar to move again.
  // _canActivateEntry has already verified the avatar is immobile.
  if (entry._builtin === 'realm_eater_digest') {
    const changed = applyStatusToggle(cardInstance, 'immobile', false);
    if (changed) {
      host.rebuildStatusBadges(cardInstance.id);
      host._broadcastCardStatus(cardInstance.id, 'immobile', false);
      host.recomputeStaticAuras();
    }
    host.logAction('{name} digests (no longer immobile)', { name: cardLogLabel(cardInstance, 'Realm-Eater') });
    return;
  }
  // Druid flip. Front-face activated ability. The tap cost is
  // already paid by the generic activate path above. Spawns Bruin
  // (a Beast token) at Druid's cell, then flips the avatar to its
  // back face. `flipCard(..., { fromAbility: true })` bypasses the
  // `flipPolicy: 'ability_only'` lock so the F hotkey stays blocked
  // for accidental triggers while the deliberate ability succeeds.
  if (entry._builtin === 'druid_flip') {
    host._doDruidFlip(cardInstance);
    return;
  }
  // v1.74. Sorcerer (and future avatars with Tap → Draw a spell).
  // Routes to the local-player's spellbook draw with the same engine
  // path the start-phase prompt uses. Forced draw. No Spellbook /
  // Atlas choice (this ability explicitly draws a spell).
  if (entry._builtin === 'tap_draw_spell') {
    const controller = cardInstance.rotated ? 'p2' : 'p1';
    const ownerRotated = controller === 'p2';
    const piles = host.state.gameState?.piles || [];
    const spellbook = piles.find((p) => p.name === 'Spellbook' && !!p.rotated === ownerRotated);
    if (!spellbook || spellbook.cards.length === 0) {
      host.logSystem("{name} can't draw. Spellbook is empty.", { name: cardLogLabel(cardInstance, msg('Avatar')) });
      return;
    }
    host.drawCard(spellbook.id);
    return;
  }
  // The activation click is the caster's commit, so cell-pick abilities
  // drop straight into cell-pick mode with a "Choose a location" drawer.
  switch (entry.effect?.[0]?.op) {
    case OP.AVATAR_OF_WATER_FLOOD:    host._startAvatarOfWaterFlood(cardInstance, controller); return;
    case OP.PATHFINDER_TAP:           host._startPathfinderTap(cardInstance, controller); return;
    case OP.GEOMANCER_REPLACE_RUBBLE: host._startGeomancerReplaceRubble(cardInstance, controller); return;
    case OP.WAVESHAPER_FLOOD:         host._startWaveshaperFlood(cardInstance, controller); return;
    case OP.FLAMECALLER_FIRE_BLAST:   host._startFlamecallerFireBlast(cardInstance, controller); return;
    case OP.AVATAR_OF_FIRE_IGNITE:    host._startAvatarOfFireIgnite(cardInstance, controller); return;
    case OP.SPARKMAGE_SHOCK:          host._startSparkmageShock(cardInstance, controller); return;
    // Cradle of Etherrum. The peek opens the scry dialog directly
    // (the dialog IS the interaction); when the entry carries
    // args.cast_from_top, the dialog offers the Summon button and the
    // cast resolves from there.
    case OP.OPEN_SCRY_TOP:            openScryTopForController(host, controller, entry.effect[0].args || {}, {}, cardInstance); return;
    case OP.CAST_TOP_OF_SPELLBOOK:    resolveCastTopOfSpellbook(host, cardInstance, entry, controller); return;
    // Pnakotic Manuscript bearer ability. Reveal + draw the bearer's
    // spellbook top, then deal that card's printed cost to the bearer.
    case OP.REVEAL_DRAW_DAMAGE_BEARER:
      applyRevealDrawDamageBearer(cardInstance, controller, revealDrawDamageHost(host));
      return;
    // Salmon of Knowledge granted ability. Roll a d20, on 14+ banish
    // the granting Salmon and draw three. `entry` carries _sourceCardId.
    // Auth: server-owned. The trigger intent pays the tap and rolls the
    // SEEDED server d20; the banish + draw fold from the diff (the local
    // roll would fork the two clients' realities).
    case OP.ROLL_BANISH_SOURCE_DRAW:
      if (host._authZonesEnabled?.()) {
        host._sendMatchIntent(triggerIntent(cardInstance.id, 'activated'));
        return;
      }
      applyRollBanishSourceDraw(cardInstance, entry, entry.effect[0].args || {}, controller, rollBanishDrawHost(host));
      return;
    // Erik's Curiosa. Rip to pieces (banish self), then draw any card
    // from the in-match Collection via the existing pile-search dialog.
    case OP.DRAW_FROM_COLLECTION:
      applyDrawFromCollection(cardInstance, controller, drawFromCollectionHost(host));
      return;
  }
  // v1.111. Single-effect target-pick activated abilities (Savior's
  // mana-cost Ward, future Tap → Kill target) route directly to
  // `_startTargeting` with a pick_target drawer prompt for context
  // once the gate's confirm has resolved. Multi-effect entries fall
  // through to applyCardAutomation so the existing followup chain
  // (Enduring Faith pattern) still works correctly.
  const SINGLE_TARGET_ACTIVATED_OPS = new Set([
    OP.KILL_TARGET, OP.DAMAGE_TARGET, OP.RETURN_TO_HAND_TARGET,
    OP.BANISH_TARGET, OP.GRANT_STATUS_TO_TARGET, OP.BUFF_TARGET,
    OP.TELEPORT_TARGET,
  ]);
  const firstEffect = entry.effect?.[0];
  if (
    entry.effect?.length === 1
    && firstEffect
    && SINGLE_TARGET_ACTIVATED_OPS.has(firstEffect.op)
  ) {
    host._startTargeting(
      cardInstance, controller, firstEffect.op, firstEffect.args || {},
      null, null, { promptDescription: t('Choose a target') },
    );
    return;
  }
  // Parser-emitted activated entry → existing pipeline.
  host.applyCardAutomation(cardInstance, ['activated']);
}

// Anchor cell for the cast-from-top family: the source's own cell
// when field-placed, or the bearer's cell when the source is an
// equipped Relic living in a unit's equipment array.
function castFromTopAnchorCell(host, cardInstance) {
  let mesh = host.meshes.get(cardInstance.id);
  if (!mesh) {
    for (const [, m] of host.meshes) {
      const c = m?.userData?.cardInstance;
      if (c?.equippedCards?.some((eq) => eq.id === cardInstance.id)) { mesh = m; break; }
    }
  }
  return (mesh && host._cellAtWorld(mesh.position.x, mesh.position.z)) || null;
}

// Shared affordability for cast-from-top. The destination is FIXED
// (the anchor cell), so cell-scoped synergies apply exactly like a
// hand cast landing on that square: ledger cost modifiers
// (Dragonlord's Lair "cost (1) less", Caelestis set_to) adjust the
// mana, and WAIVE_THRESHOLD statics ("require no threshold") forgive
// unmet thresholds. Never check mana or thresholds for an effect
// cast outside this helper.
function castFromTopBudget(host, controller, fullTop, costDelta, cell) {
  const cost = getCardCost(fullTop);
  const { need, discounted } = host.effectLedger.castManaBreakdown(
    controller, cost.mana + (costDelta || 0), fullTop, cell,
  );
  const res = host.playerResources.snapshot(controller);
  const waivers = collectThresholdWaivers(host.meshes, host._cellAtWorld, host._getFullCard);
  return {
    need,
    discounted,
    manaOk: (res.mana || 0) >= need,
    thresholdsOk: thresholdsMetAt(res, fullTop, cost.thresholds, cell, waivers),
  };
}

// Auth: the Summon affordance appended to a SERVER ability-scry LOOK modal
// (Cradle of Etherrum). The prompt id carries the source (`ability:<id>:<key>`);
// the displayed card is the own deck top the server just revealed. Returns a
// castOption { sourceId, topCardId, cell, need } when the top matches the
// source's cast_from_top filter AND is affordable at the anchor cell.
// mirroring the legacy peek dialog's gate. The server re-validates + prices
// the actual fromDeckTop cast intent, so this is a pure UX affordance.
export function buildServerScryCastOption(host, pc) {
  if (!pc?.display?.cardId || typeof pc.promptId !== 'string' || !pc.promptId.startsWith('ability:')) return null;
  const sourceId = pc.promptId.split(':')[1];
  let source = host._findCardById(sourceId);
  if (!source) {
    for (const [, mesh] of host.meshes) {
      const eq = mesh?.userData?.cardInstance?.equippedCards?.find((e) => e.id === sourceId);
      if (eq) { source = eq; break; }
    }
  }
  if (!source) return null;
  const cf = (host._getFullCard(source.cardId)?.automation || [])
    .filter((e) => e.trigger === 'activated')
    .flatMap((e) => e.effect || [])
    .find((e) => e.op === OP.OPEN_SCRY_TOP && e.args?.cast_from_top)?.args?.cast_from_top;
  if (!cf) return null;
  const top = (host.state.gameState?.piles || []).flatMap((p) => p.cards || [])
    .find((c) => c.id === pc.display.cardId);
  const fullTop = top && host._getFullCard(top.cardId);
  if (!fullTop || !matchesFilter(fullTop, cf.filter || {})) return null;
  const cell = castFromTopAnchorCell(host, source);
  if (!cell) return null;
  const budget = castFromTopBudget(host, pc.seat, fullTop, cf.cost_delta, cell);
  if (!budget.manaOk || !budget.thresholdsOk) return null;
  return { sourceId, topCardId: pc.display.cardId, cell: { col: cell.col, row: cell.row }, need: budget.need };
}

// Open the scry dialog on the controller's named pile. Shared by the
// turn-trigger silent path (Seer's start-of-turn peek) and activated
// abilities (Cradle of Etherrum's "look at your next spell").
export function openScryTopForController(host, controller, args = {}, opts = {}, cardInstance = null) {
  const pileRaw = args.pile || 'spellbook';
  const pileName = pileRaw.charAt(0).toUpperCase() + pileRaw.slice(1);
  const pile = host.findPileByNameForPlayer(pileName, controller === 'p2' ? 2 : 1);
  if (!pile || pile.cards.length === 0) {
    if (!opts.quiet) toast.message(t('Your {pile} is empty.', { pile: pileRaw }));
    return;
  }
  // Cradle of Etherrum: args.cast_from_top makes the peek dialog
  // offer a Summon button when the top card matches the filter AND
  // is affordable at the anchor cell (castFromTopBudget. Destination
  // waivers and cost modifiers apply). Ineligible tops peek as usual,
  // just without the button. The dialog's only choices are summon or
  // leave it.
  let castOption = null;
  const cf = args.cast_from_top;
  if (cf && cardInstance) {
    const top = pile.cards[pile.cards.length - 1];
    const fullTop = top && host._getFullCard(top.cardId);
    if (fullTop && matchesFilter(fullTop, cf.filter || {})) {
      const cell = castFromTopAnchorCell(host, cardInstance);
      const budget = castFromTopBudget(host, controller, fullTop, cf.cost_delta, cell);
      if (budget.manaOk && budget.thresholdsOk) {
        castOption = {
          sourceCardId: cardInstance.id,
          controller,
          filter: cf.filter || {},
          cost_delta: cf.cost_delta || 0,
          need: budget.need,
        };
      }
    }
  }
  const allowed = args.allow_bottom === false ? ['same'] : ['same', 'opposite'];
  host.openScry(pile, 'top', { allowedDestinations: allowed, maxPeek: args.count || 1, castOption });
}

// Riddle Sphinx: open the controller's spellbook scry, then run the rest
// of the clause once the session closes by any exit. The peek lets the
// controller put the top spell on the bottom (allow_bottom). After it
// resolves, the OPPONENT may swap the controller's top and bottom spells
// (cross-client handshake), then the draw fires (reads the post-swap top).
// `swapArgs` is the opponent_may_swap_pile_ends args (null to skip the
// swap step entirely). The draws ride the swap reply so they always read
// the final pile order.
export function openScryWithDraws(host, controller, args, cardInstance, draws, cardName, swapArgs = null) {
  const pileRaw = args.pile || 'spellbook';
  const pileName = pileRaw.charAt(0).toUpperCase() + pileRaw.slice(1);
  const pile = host.findPileByNameForPlayer(pileName, controller === 'p2' ? 2 : 1);
  const drawAll = () => {
    for (const d of draws) {
      const n = executeDrawOp(host, d.args || {}, cardInstance, controller);
      if (n > 0) host.logAction('{name}: drew {n}', { name: cardName, n });
    }
  };
  const onComplete = () => {
    if (swapArgs) castOpponentPileEndSwap(host, controller, swapArgs, cardName, drawAll, cardInstance);
    else drawAll();
  };
  if (!pile || pile.cards.length === 0) {
    // Empty spellbook still resolves the rest of the clause.
    onComplete();
    return;
  }
  const allowed = args.allow_bottom === false ? ['same'] : ['same', 'opposite'];
  host.openScry(pile, 'top', { allowedDestinations: allowed, maxPeek: args.count || 1, onComplete });
}

// Riddle Sphinx middle clause. "an opponent may exchange your top and
// bottommost spells." The controller owns the spellbook, but the decision
// is the opponent's, so the two live on opposite clients. The controller
// emits a request to the opponent and stashes the post-swap continuation
// (the draw) keyed by requestId; the reply lands in resolvePileEndSwapResult.
// In casual sandbox one client owns both seats, so the prompt + swap + draw
// resolve locally with no peer handshake.
export function castOpponentPileEndSwap(host, controller, swapArgs, cardName, onDone, cardInstance = null) {
  const pileRaw = swapArgs?.pile || 'spellbook';
  const pileName = pileRaw.charAt(0).toUpperCase() + pileRaw.slice(1);
  const pile = host.findPileByNameForPlayer(pileName, controller === 'p2' ? 2 : 1);
  if (!pile || pile.cards.length < 2) {
    // Fewer than two spells: nothing to exchange, go straight to the draw.
    onDone();
    return;
  }
  // Card identity for the opponent's prompt. Like every other storyline
  // prompt, it shows the source card's art, not bare buttons. The opponent
  // may be on another client (online), so we ship the public bits.
  const cardMeta = cardInstance ? {
    cardId: cardInstance.cardId,
    imageUrl: cardInstance.imageUrl,
    isSite: !!cardInstance.isSite,
    name: cardName,
  } : null;
  if (!host._pendingPileEndSwaps) host._pendingPileEndSwaps = new Map();
  const requestId = requestOpponentSwap(controller, pile.id, {
    opponentSeat: (c) => (c === 'p2' ? 'p1' : 'p2'),
    newRequestId: () => `swap-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    // Auth: Riddle Sphinx is server-owned. Never the legacy handshake.
    emitSwapRequest: (opponentSeat, pileId, reqId) => { if (!host._authZonesEnabled?.()) host.sync.requestPileEndSwap(opponentSeat, pileId, reqId, cardMeta); },
  });
  host._pendingPileEndSwaps.set(requestId, { pileId: pile.id, cardName, onDone });
}

// Swap the ends of the OWNED pile and broadcast the new order so both
// clients agree. Logs a public summary (no card identities. The
// spellbook is private info).
function applyPileEndSwap(host, pile, cardName) {
  if (!swapPileEnds(pile.cards)) return;
  host.updatePileMeshes();
  host.logAction('{name}: an opponent exchanged your top and bottommost spells', { name: cardName });
}

// Opponent side. Surface a yes/no choice asking whether to swap the
// controller's top and bottom spells. `onDecide(swap)` runs on resolve.
function pushPileEndSwapPrompt(host, decider, cardName, onDecide, card = null) {
  const promptId = `pileswap-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  host._pushStorylineEvents([{
    id: promptId,
    cardInstance: card,
    cardName: cardName || 'Riddle Sphinx',
    controller: decider,
    sourceEntry: null,
    promptType: 'pile_end_swap',
    description: t('{name}: exchange their top and bottommost spells?', { name: cardName || 'Riddle Sphinx' }),
    choices: [
      { value: 'swap', label: t('Exchange') },
      { value: 'no', label: t('Leave') },
    ],
    _pileEndSwap: { onDecide },
  }]);
}

// Opponent-client entry from the `requestPileEndSwap` socket message:
// surface the yes/no prompt, then reply with the boolean. The controller
// (who owns the pile) performs the swap on the reply.
export function promptOpponentPileEndSwap(host, requestId, card) {
  pushPileEndSwapPrompt(host, host._localSeat(), card?.name || 'Riddle Sphinx', (swap) => {
    host.sync.pileEndSwapResult(requestId, swap);
  }, card || null);
}

// Controller-client entry from the `pileEndSwapResult` socket reply:
// perform the swap on the owned pile when the opponent said yes, then run
// the stashed continuation (the draw) either way.
export function resolvePileEndSwapResult(host, requestId, swap) {
  const pending = host._pendingPileEndSwaps?.get(requestId);
  if (!pending) return;
  host._pendingPileEndSwaps.delete(requestId);
  if (swap) {
    const pile = host.state.gameState.piles.find((p) => p.id === pending.pileId);
    if (pile) applyPileEndSwap(host, pile, pending.cardName);
  }
  pending.onDone();
}

// Cradle of Etherrum: cast the topmost spellbook card to the source's
// cell (or the bearer's cell for an equipped Relic) at printed cost +
// args.cost_delta. _canActivateEntry verified turn, filter match,
// mana, and thresholds. This is the commit.
export function resolveCastTopOfSpellbook(host, cardInstance, entry, controller) {
  const args = entry.effect?.[0]?.args || {};
  const ownerRotated = controller === 'p2';
  const spellbook = host.state.gameState.piles.find(
    (p) => p.name === 'Spellbook' && !!p.rotated === ownerRotated,
  );
  const top = spellbook?.cards?.[spellbook.cards.length - 1];
  if (!top) { toast.message(t('Your spellbook is empty.')); return; }
  const fullTop = host._getFullCard(top.cardId);
  if (!fullTop || !matchesFilter(fullTop, args.filter || {})) {
    toast.message(t('The top of your spellbook does not match.'));
    return;
  }
  const cell = castFromTopAnchorCell(host, cardInstance);
  if (!cell) {
    toast.message(t('No location to cast to. The cast fizzles.'));
    return;
  }
  const budget = castFromTopBudget(host, controller, fullTop, args.cost_delta, cell);
  if (!budget.manaOk) {
    toast.error(t('Need {need} mana.', { need: budget.need }));
    return;
  }
  if (!budget.thresholdsOk) {
    toast.error(t('Thresholds not met.'));
    return;
  }
  const need = budget.need;
  if (need > 0) host.playerResources.payMana(controller, need);
  if (budget.discounted) {
    host.effectLedger.consumeCostModifierUse(controller, fullTop, cell);
  }
  spellbook.cards.pop();
  host.updatePileMeshes();
  const grid = getGameGrid(host.state.spawnConfig);
  const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
  if (!geo) return;
  const cx = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
  const cz = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
  const sourceName = cardInstance.name || 'Cradle of Etherrum';
  host.logAction('{name}: casts {card} from the top of the spellbook for ({cost})', { name: sourceName, card: fullTop.name || cardLogLabel(top), cost: need });
  host.addCardToTable({
    ...top,
    type: fullTop.type,
    x: cx,
    y: 0,
    z: cz,
    rotated: ownerRotated,
  }, true, { logVerb: k('Casts') });
}

// Stamp an archive record's FULL state onto a live card through the
// host primitives. Used both for in-play rewinds and for cards that
// just respawned from a pile. Every write is idempotent, so stamping
// a field that already matches is harmless.
function stampArchiveRecord(host, card, rec, sourceName) {
  const mesh = host.meshes.get(card.id);
  if (!mesh) return;
  // Post-archive carried artifacts get LEFT BEHIND at the bearer's
  // PRE-return cell (FAQ: a return is not movement, so they don't
  // ride along. "There is a Grim Guisarme in the void on square 9
  // now"). Detach them to the field here, before the position write;
  // attachments that ARE in the record stay aboard for the rewind.
  const recEqIds = new Set(rec.equippedCards.map((e) => e.id));
  const current = card.equippedCards || [];
  const leftBehind = current.filter((e) => !recEqIds.has(e.id));
  if (leftBehind.length > 0) {
    const stillAttached = current.filter((e) => recEqIds.has(e.id));
    card.equippedCards = leftBehind;
    host._detachAllEquipmentToField(card, { x: mesh.position.x, z: mesh.position.z });
    card.equippedCards = stillAttached;
    host._broadcastCardEquipment(card.id, card.equippedCards);
  }
  // Position next. Scope-sensitive writes below read the cell.
  // Direct placement, NOT a forced move: a return never "enters" a
  // location (no triggers), ignores region barriers, and may land an
  // Avatar in the void. The void sweep at the end of the restore
  // handles stranded units.
  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (rec.cell && (cell?.col !== rec.cell.col || cell?.row !== rec.cell.row)) {
    host._placeCardAtCell(card, rec.cell, sourceName, k('returns'));
  }
  if (!!card.tapped !== rec.tapped) {
    host._applyTap(card, mesh, rec.tapped, { source: 'automation' });
  }
  if (!!card.faceDown !== rec.faceDown) {
    host.flipCard(card, mesh, { fromAbility: true });
  }
  if (!!card.rotated !== rec.rotated) {
    host._applyControllerFlip(card, mesh, rec.rotated, { source: 'automation' });
  }
  if (rec.currentAttack !== null && card.currentAttack !== rec.currentAttack) {
    card.currentAttack = rec.currentAttack;
    host._broadcastCardStat(card.id, 'atk', card.currentAttack);
  }
  if (rec.currentLife !== null && card.currentLife !== rec.currentLife) {
    card.currentLife = rec.currentLife;
    host._broadcastCardStat(card.id, 'hp', card.currentLife);
  }
  host._refreshLifeHUD(card);
  // Manual statuses: toggle the diff through the canonical primitive
  // so badges + broadcast stay in sync on both clients.
  const want = new Set(rec.statuses);
  for (const key of [...(card.statuses || [])]) {
    if (!want.has(key)) host.toggleCardStatus(card, key);
  }
  for (const key of want) {
    if (!(card.statuses || []).includes(key)) host.toggleCardStatus(card, key);
  }
  if ((card.subsurface ?? null) !== rec.subsurface) {
    host._setSubsurface(card, rec.subsurface);
  }
  if ((card._movementBonus || 0) !== rec.movementBonus) {
    card._movementBonus = rec.movementBonus;
    host._refreshMovementHUD?.(card.id);
  }
  if ((card._counters || 0) !== (rec.counters || 0)) {
    card._counters = rec.counters || 0;
    host._broadcastCardCounters?.(card.id, card._counters);
    host._refreshCounterAnchorFor?.(card.id);
  }
  // Equipment: reclaim every archived attachment from wherever it
  // ended up (detached to the field, or in a pile), then re-attach.
  const haveEq = (card.equippedCards || []).map((e) => e.id).sort().join(',');
  const wantEq = rec.equippedCards.map((e) => e.id).sort().join(',');
  if (haveEq !== wantEq) {
    const restored = [];
    for (const eqRec of rec.equippedCards) {
      const eqMesh = host.meshes.get(eqRec.id);
      if (eqMesh) {
        // Detached to the field. Pull the mesh back off the table.
        host.removeCardFromTable(eqMesh.userData.cardInstance, true, { detachEquipmentToField: false });
        restored.push({ ...eqRec });
        continue;
      }
      let found = null;
      for (const pile of host.state.gameState.piles || []) {
        // FAQ: banished cards can never be returned. An archived
        // attachment that got banished stays lost.
        if (departedPileKind(pile) === 'banished') continue;
        const idx = (pile.cards || []).findIndex((c) => c.id === eqRec.id);
        if (idx === -1) continue;
        pile.cards.splice(idx, 1);
        found = true;
        break;
      }
      if (!found) {
        // Same copy-tracking as the card returns: an attachment that
        // bounced into a locally-visible hand is reclaimed from there.
        for (const key of ['handCards', 'handCards2']) {
          const handRow = (host.state[key] || []).find((c) => c.id === eqRec.id);
          if (!handRow) continue;
          host.removeFromHand(handRow);
          found = true;
          break;
        }
      }
      if (found) restored.push({ ...eqRec });
    }
    card.equippedCards = restored;
    host._broadcastCardEquipment(card.id, card.equippedCards);
    for (const eq of card.equippedCards) {
      host._applyEquippedArtifactEffects(eq, card);
    }
    host.syncEquipmentOverlays();
  }
}

// Kairos the Archivist's Deathrite. Diffs the Genesis archive against
// the live board (ops/kairosArchive.buildRestorePlan) and executes
// each step through host primitives so taps, flips, control changes,
// statuses, and broadcasts behave like any other effect. A returning
// SITE evicts the post-archive site at its cell (the Rubble that
// replaced it). The occupant is banished, no Rubble. "All archived
// cards" tracks the specific physical copy across zone hops: departed
// cards are reclaimed from ANY pile (cemetery, banished, deck,
// collection) and from locally-visible hands; a copy in no visible
// zone (opponent's private hand in MP) is skipped, never duplicated.
// Finishes by banishing Kairos.
export function resolveRestoreArchivedRealm(host, cardInstance, controller) {
  const archive = cardInstance._realmArchive;
  const sourceName = cardInstance.name || 'Kairos the Archivist';
  if (!archive || archive.length === 0) {
    host.logAction('{name}: no archived realm state to restore', { name: sourceName });
  } else {
    const live = [];
    for (const [, mesh] of host.meshes) {
      const c = mesh?.userData?.cardInstance;
      if (!c) continue;
      live.push(Object.assign(Object.create(Object.getPrototypeOf(c)), c, {
        cell: host._cellAtWorld(mesh.position.x, mesh.position.z),
      }));
    }
    const hands = [
      { key: 'handCards', cards: host.state.handCards || [] },
      { key: 'handCards2', cards: host.state.handCards2 || [] },
    ];
    const departed = collectDepartedRows(host.state.gameState.piles, cardInstance.id, hands);
    const plan = buildRestorePlan(archive, live, departed);
    if (plan.length === 0) {
      host.logAction('{name}: the realm already matches the archive. Nothing to restore', { name: sourceName });
    } else if (plan.length === 1) {
      host.logAction('{name}: returns 1 archived change to their recorded state', { name: sourceName });
    } else {
      host.logAction('{name}: returns {n} archived changes to their recorded state', { name: sourceName, n: plan.length });
    }

    const respawnRecord = (rec, row) => {
      const fullCard = host._getFullCard(rec.cardId);
      const grid = getGameGrid(host.state.spawnConfig);
      const geo = grid && rec.cell && sampleCellGeometry(grid, rec.cell.col, rec.cell.row);
      if (!geo || !fullCard) return;
      const cx = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
      const cz = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
      // Re-entry to a recorded state is NOT a fresh cast. Genesis
      // already fired when the card was first played, so the spawn
      // goes in silent.
      host.addCardToTable({
        ...(row || {}),
        id: rec.id,
        cardId: rec.cardId,
        name: rec.name,
        imageUrl: rec.imageUrl || row?.imageUrl,
        printingId: rec.printingId || row?.printingId,
        foiling: rec.foiling || row?.foiling,
        type: fullCard.type,
        isToken: !!(rec.isToken || row?.isToken),
        x: cx,
        y: 0,
        z: cz,
        rotated: rec.rotated,
      }, true, { silent: true, logVerb: k('Returns') });
      const back = host.meshes.get(rec.id)?.userData?.cardInstance;
      if (back) stampArchiveRecord(host, back, rec, sourceName);
    };

    for (const step of plan) {
      if (step.kind === 'restore_state') {
        const card = host.meshes.get(step.id)?.userData?.cardInstance;
        if (card) stampArchiveRecord(host, card, step.rec, sourceName);
      } else if (step.kind === 'clear_site_at') {
        // The post-archive site squatting on a returning site's cell
        // is BANISHED (FAQ: "the site that's in the way gets
        // banished!"). noRubble: the returning archived site takes
        // the cell. An eviction is not destruction, so no Rubble.
        const occupant = host.meshes.get(step.occupantId)?.userData?.cardInstance;
        if (occupant) {
          host.logAction('{name}: {card} is in the way of a returning site', { name: sourceName, card: cardLogLabel(occupant, msg('a site')) });
          host.sendCardToPile(occupant, 'Banished', false, 'top', { noRubble: true });
        }
      } else if (step.kind === 'replace') {
        const card = host.meshes.get(step.id)?.userData?.cardInstance;
        if (card) host.removeCardFromTable(card);
        respawnRecord(step.rec, null);
      } else if (step.kind === 'return_to_play') {
        if (!step.rec.cell) continue;
        let row = null;
        if (step.handKey) {
          // Reclaimed from a locally-visible hand. removeFromHand
          // stamps _fromHand on the instance (the hand-origin
          // transform marker); strip it from the respawn row so the
          // archive return never reads as a fresh hand play.
          const handRow = (host.state[step.handKey] || [])
            .find((c) => c.id === step.rec.id);
          if (!handRow) continue;
          host.removeFromHand(handRow);
          row = { ...handRow };
          delete row._fromHand;
        } else if (step.pileId) {
          const pile = host.state.gameState.piles.find((p) => p.id === step.pileId);
          if (!pile) continue;
          const idx = pile.cards.findIndex((c) => c.id === step.rec.id);
          if (idx === -1) continue;
          [row] = pile.cards.splice(idx, 1);
        }
        // fromPile 'token' carries no source row at all. The token
        // is recreated from the record (unlimited copies).
        respawnRecord(step.rec, row);
      }
    }
    // FAQ: non-archived cards stranded where no site stands anymore
    // are banished ("The Bluecap Knockers find themselves in the void
    // and are banished"). One sweep AFTER every return has landed so
    // simultaneity holds. A unit whose archived site returned in the
    // same restore is safe. Avatars are exempt: an Avatar CAN end up
    // in the void this way; the mandatory-actions rule takes over.
    for (const [, mesh] of [...host.meshes]) {
      const c = mesh?.userData?.cardInstance;
      if (!c || c.isSite || c.type === 'Avatar' || c.subsurface) continue;
      const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
      if (!cell) continue;
      const regions = regionsAtCell(host.meshes, host._cellAtWorld, cell.col, cell.row, host._getFullCard);
      if (!regions.has(REGION.VOID) || regions.has(REGION.SURFACE)) continue;
      if (regionConsequence(c, REGION.VOID) !== 'banish') continue;
      host.logAction('{name} is left in the void and banished', { name: cardLogLabel(c, msg('A unit')) });
      host.sendCardToPile(c, 'Banished');
    }
    // Returning sites re-enter silently (no mana bump, no Genesis.
    // FAQ), but elemental affinity is continuous state derived from
    // the realm: recalculate it once now that the board has settled.
    host.recalculateSiteAffinities?.('p1');
    host.recalculateSiteAffinities?.('p2');
    host.updatePileMeshes();
    host.recomputeStaticAuras();
  }
  // "Then banish Kairos." The Deathrite already moved Kairos to a
  // pile; relocate from the cemetery to Banished if it isn't there.
  // Classified via departedPileKind. Pile names carry player
  // suffixes ("Cemetery P1"), never the bare word.
  const cemetery = host.state.gameState.piles.find(
    (p) => departedPileKind(p) === 'cemetery' && p.cards.some((c) => c.id === cardInstance.id),
  );
  if (cemetery) {
    const idx = cemetery.cards.findIndex((c) => c.id === cardInstance.id);
    const [row] = cemetery.cards.splice(idx, 1);
    const banished = host.findOrCreateBanished(controller === 'p2');
    banished.cards.push(row);
    host.updatePileMeshes();
    host.logAction('{name} is banished', { name: sourceName });
  }
}

// Xeraphine Konrul's cemetery rites. A two-stage pile multi-select
// session (choose three dead minions, then pick the one that returns;
// the others banish). The generic session machinery
// (pileMultiSelect.js) owns selection highlights, undo, and the
// Accept commit; nothing mutates until the stage-2 Accept, so closing
// the dialog at ANY point aborts for free.
export function startXeraphineRites(host, cardInstance, controller, args = {}) {
  const cemetery = host.findOrCreateCemetery(controller === 'p2');
  if (!cemetery) return;
  const choose = args.choose ?? 3;
  startPileMultiSelect(host, cemetery, {
    kind: 'xeraphine_choose',
    controller,
    sourceCardId: cardInstance.id,
    sourceName: cardInstance.name || 'Xeraphine Konrul',
    title: t('Choose {n} dead minions', { n: choose }),
    hint: t('Click to select. Right-click or click again to unselect'),
    confirmLabel: t('Choose these'),
    max: choose,
  });
}

// Open a cemetery in multi-select mode for a cemetery → deck effect.
// `kind` dispatches the commit (shuffle-into-deck or return-to-bottom);
// `filter` gates which dead cards are eligible (Pendragon: Uniques).
// `minSelect: 0` makes it an "up to N" / "any number" session, so Accept
// fires with any count up to `max`. The eligible card count caps `max` so
// the running "x / N" never exceeds what's actually pickable.
//
// `args.span` ("from a cemetery." Return to Nature): the pick spans BOTH
// players' Cemetery piles. The dialog opens on the controller's own
// cemetery; the cemetery tab switcher (renderBody) flips to the opponent's
// pile mid-pick without losing the running selection. Each returned card
// routes to ITS OWNER's deck (keyed off the cemetery it came from).
export function startCemeteryDeckSelect(host, cardInstance, controller, kind, args = {}) {
  const own = host.findOrCreateCemetery(controller === 'p2');
  if (!own) return;
  const opp = args.span ? host.findOrCreateCemetery(controller !== 'p2') : null;
  const cemeteries = [own, opp].filter(Boolean);
  const ctx = { getFullCard: host._getFullCard };
  const eligible = cemeteries.flatMap(
    (c) => eligibleCemeteryCards(c.cards, args.filter || null, ctx),
  );
  if (eligible.length === 0) {
    host.logAction('{name}: no eligible cards in the cemetery', { name: cardLogLabel(cardInstance, msg('Effect')) });
    return;
  }
  const cap = typeof args.max === 'number' ? Math.min(args.max, eligible.length) : eligible.length;
  startPileMultiSelect(host, own, {
    kind,
    controller,
    sourceCardId: cardInstance.id,
    sourceName: cardInstance.name || 'Effect',
    title: args.title || t('Choose cards'),
    hint: t('Click to select. Right-click or click again to unselect'),
    confirmLabel: args.confirmLabel || t('Confirm'),
    max: cap,
    minSelect: 0,
    eligibleIds: eligible.map((c) => c.id),
    ...(args.span ? { pileIds: cemeteries.map((c) => c.id), spanCemeteries: true } : {}),
  });
}

// Black Mass: open the controller's spellbook windowed to its top `window`
// cards ONLY (never the whole library), in multi-select mode. Eligible
// = cards matching `filter` (Evil minions). The player picks up to `pick`
// DIFFERENT-named eligibles to draw; the rest of the window goes to the
// bottom of the spellbook. The synthetic window pile shares the real
// spellbook's id so the dialog renders only the seven while the commit
// mutates the actual spellbook by id.
export function startBlackMass(host, cardInstance, controller, args = {}) {
  const ownerRotated = controller === 'p2';
  const piles = host.state.gameState?.piles || [];
  const spellbook = piles.find((p) => p.name === 'Spellbook' && !!p.rotated === ownerRotated);
  if (!spellbook || spellbook.cards.length === 0) {
    host.logAction('{name}: your spellbook is empty', { name: cardLogLabel(cardInstance, 'Black Mass') });
    return;
  }
  const windowSize = args.window ?? 7;
  const windowCards = topWindow(spellbook.cards, windowSize);
  const ctx = { getFullCard: host._getFullCard };
  const eligible = eligibleWindowCards(windowCards, args.filter || null, ctx);
  const pick = args.pick ?? 3;
  // Render only the top-N window. Same id as the real spellbook so the
  // commit handler resolves the authoritative pile via session.pileId.
  const windowPile = { ...spellbook, cards: windowCards };
  startPileMultiSelect(host, windowPile, {
    kind: 'black_mass_window',
    controller,
    sourceCardId: cardInstance.id,
    sourceName: cardInstance.name || 'Black Mass',
    title: t('Search your top {n} spells', { n: windowSize }),
    hint: t('Draw up to {n} different Evil minions. The rest go to the bottom.', { n: pick }),
    confirmLabel: t('Draw + bottom rest'),
    max: Math.min(pick, eligible.length),
    minSelect: 0,
    eligibleIds: eligible.map((c) => c.id),
    meta: { windowIds: windowCards.map((c) => c.id), windowCards },
  });
}

// Flanking Maneuver stage one: open a cell pick over every source cell
// holding an allied force-movable unit. The chained draw effects ride on
// `_draws` so the destination resolver can fire them after the group
// lands. No legal source ⇒ the spell fizzles (the draw still wouldn't
// fire. Sorcery resolves the whole effect or nothing).
export function startFlankingManeuver(host, sourceCard, controller, casterOverrides, draws) {
  const cellAt = host._cellAtWorld;
  const sources = sourceCellsWithAllies(host.meshes, cellAt, controller);
  if (sources.size === 0) {
    host.logAction('{name}: no allies to flank with', { name: cardLogLabel(sourceCard, 'Flanking Maneuver') });
    return;
  }
  host._startCellPick(sourceCard, controller, OP.GROUP_TELEPORT_KNIGHT, sources, {
    description: t('Choose a location with allies'),
    args: { _stage: 'source', _draws: draws || [] },
    casterOverrides,
  });
}

// Flanking Maneuver cell resolver. Stage 'source' transitions to a
// knight-move destination pick anchored on the picked cell; stage 'dest'
// moves every allied unit at the source cell to the destination together,
// then fires the chained draw.
export function resolveFlankingManeuverCell(host, pct, cell, sourceName) {
  const { sourceCard, controller, args, casterOverrides } = pct;
  if (args._stage === 'source') {
    const grid = getGameGrid(host.state.spawnConfig);
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const dests = new Set(
      knightMoveCells(cell, colPositions.length - 1, rowPositions.length - 1)
        .map((c) => `${c.col},${c.row}`),
    );
    if (dests.size === 0) {
      host.logAction("{name}: no knight's-move destination from there", { name: sourceName });
      return;
    }
    host._startCellPick(sourceCard, controller, OP.GROUP_TELEPORT_KNIGHT, dests, {
      description: t("Choose a knight's-move destination"),
      args: { _stage: 'dest', _sourceCell: cell, _draws: args._draws || [] },
      casterOverrides,
    });
    // Signal tryResolveCellTarget NOT to clear the pick we just staged.
    host._cellChainInFlight = true;
    return;
  }

  // Stage 'dest'. Re-read the allies at the source cell (the board may
  // have shifted between picks) and let the player pick WHICH of them to
  // teleport ("any number"). The pick runs through the canonical pile
  // multi-select dialog over a virtual pile of those unit cards.
  const src = args._sourceCell;
  const allies = alliedUnitsAtCell(host.meshes, host._cellAtWorld, src.col, src.row, controller);
  if (allies.length === 0) {
    host.logAction('{name}: no allies remain at the source', { name: sourceName });
    return;
  }
  const subsetPile = { id: 'flanking-subset', name: 'Flanking', cards: allies, infinite: true };
  startPileMultiSelect(host, subsetPile, {
    kind: 'flanking_subset',
    controller,
    sourceCardId: sourceCard.id,
    sourceName,
    title: t('Choose which allies to teleport'),
    hint: t('Click to select. Right-click or click again to unselect'),
    confirmLabel: t('Teleport'),
    max: allies.length,
    minSelect: 0,
    eligibleIds: allies.map((c) => c.id),
    meta: { destCell: cell, allyIds: allies.map((c) => c.id), draws: args._draws || [] },
  });
}

// Flanking Maneuver final stage. Teleport the player-chosen subset of
// allies to the knight-move destination, then fire the chained draw.
// Driven by the flanking_subset pile-select confirm; selectedIds are the
// units to move, meta carries the destination cell and draw ops.
export function finishFlankingSubset(host, session) {
  const { destCell, draws } = session.meta;
  let moved = 0;
  for (const id of session.selectedIds) {
    const ally = host._findCardById(id);
    if (ally && host._moveCardToCell(ally, destCell, session.sourceName, k('teleports'), 'teleport')) moved++;
  }
  if (moved === 1) host.logAction('{name}: teleported 1 ally', { name: session.sourceName });
  else host.logAction('{name}: teleported {n} allies', { name: session.sourceName, n: moved });
  const source = host._findCardById(session.sourceCardId) || { id: session.sourceCardId };
  for (const draw of draws || []) {
    executeDrawOp(host, draw.args || {}, source, session.controller);
  }
}

// Howl from Beyond: enumerate the outer-column voids, read that many cards
// off the spellbook top, draw the Voidwalk-minion / Monster ones, banish
// the rest. No pick. Fully automatic. Moves cards via the synced
// take-to-hand + banish-from-pile primitives.
export function resolveHowlFromBeyond(host, sourceCard, controller) {
  const grid = getGameGrid(host.state.spawnConfig);
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const regionsAt = (col, row) =>
    regionsAtCell(host.meshes, host._cellAtWorld, col, row, host._getFullCard);
  const voids = outerColumnVoidCells(totalCols, totalRows, regionsAt);
  const n = voids.size;
  const name = sourceName(host, sourceCard);
  if (n === 0) {
    host.logAction('{name}: no outer-column voids to deal to', { name });
    toast.message(t('{name}: no empty voids in the outer columns. Effect fizzles.', { name }));
    return;
  }
  const spellbook = host.findPileByNameForPlayer('Spellbook', controller === 'p2' ? 2 : 1);
  if (!spellbook || spellbook.cards.length === 0) {
    host.logAction('{name}: spellbook is empty', { name });
    toast.message(t('{name}: your spellbook is empty. Effect fizzles.', { name }));
    return;
  }
  // Snapshot the dealt cards BEFORE mutating the pile. Both the take and
  // banish primitives splice the live array, so resolve identities first.
  const dealt = dealtTopCards(spellbook.cards, n);

  // Plan the cosmetic reveal off the SAME snapshot, before the state
  // mutates. cellCenter is the four-corner average of the void cell;
  // sampleCellGeometry returns null when there's no scene/grid (headless
  // tests), so the plan degrades to (0,0) anchors and the playback below
  // no-ops cleanly. The plan never touches game state.
  const cellCenter = (col, row) => {
    const corners = sampleCellGeometry(grid, col, row);
    if (!corners) return { x: 0, z: 0 };
    const { tl, tr, bl, br } = corners;
    return {
      x: (tl.x + tr.x + bl.x + br.x) / 4,
      z: (tl.z + tr.z + bl.z + br.z) / 4,
    };
  };
  const plan = planHowlDeal({ voids, dealt, cellCenter, getFullCard: host._getFullCard });

  // STATE resolves immediately + synchronously. Keepers to hand, the
  // rest banished. So the end state is identical to before and the e2e
  // still asserts the same outcome. The reveal is a cosmetic overlay
  // played AFTER, from a broadcast record (mirrors combat playback).
  const drawnNames = [];
  const banishedNames = [];
  for (const card of dealt) {
    const full = host._getFullCard(card.cardId);
    const label = full?.name || card.name || 'a card';
    if (classifyDealtCard(full) === 'draw') {
      // silent: the named summary below replaces the primitive's generic line.
      host.takeCardFromPile(spellbook, card, { silent: true });
      drawnNames.push(label);
    } else {
      // silent: the named summary below replaces the primitive's generic line.
      host.banishCardFromPile(spellbook, card, { silent: true });
      banishedNames.push(label);
    }
  }
  if (dealt.length === 1) host.logAction('{name} deals 1 spell to the void', { name });
  else host.logAction('{name} deals {n} spells to the void', { name, n: dealt.length });
  if (drawnNames.length) host.logAction('{name}: drew {cards}', { name, cards: drawnNames.join(', ') });
  if (banishedNames.length) host.logAction('{name}: banished {cards}', { name, cards: banishedNames.join(', ') });

  // Cosmetic public reveal: deal each card FACE-UP into its void on BOTH
  // clients, then animate keepers to hand + banish the rest. Guarded to
  // no-op without a scene (headless/tests) and never throw.
  host._publishHowl?.(spellbook, plan);
}

// Resolve a card's display name through the catalog (spell cards carry no
// name on the bare instance once cast).
function sourceName(host, card) {
  return host._getFullCard(card.cardId)?.name || card.name || 'Card';
}

// Vanishment-style mass status grant: apply `args.keyword` to every
// in-play card matching `args.filter` within the entry's scope. Mirror
// of _iterateEachOp but for a status keyword. Routes each match
// through the unified toggleCardStatus primitive (apply + badge +
// broadcast + recompute). No prompt, no pick.
export function grantStatusEach(host, sourceCard, controller, args = {}, casterOverrides = null) {
  const keyword = args.keyword;
  if (!keyword) return;
  const filter = { ...(args.filter || {}) };
  const matches = host._resolveAuraScope(
    sourceCard,
    { where: filter.scope || 'global' },
    { filter, exclude_self: !!args.exclude_self },
    casterOverrides || {},
  );
  let granted = 0;
  for (const card of matches) {
    if (getEffectiveStatuses(card).includes(keyword)) continue;
    host.toggleCardStatus(card, keyword);
    granted += 1;
  }
  const sourceName = host._getFullCard(sourceCard.cardId)?.name || sourceCard.name || 'Effect';
  if (granted === 1) host.logAction('{name}: granted {keyword} to 1 card', { name: sourceName, keyword });
  else if (granted > 1) host.logAction('{name}: granted {keyword} to {n} cards', { name: sourceName, keyword, n: granted });
}

// Hand-card click while a discard cost is pending. Pays the discard
// (hand → cemetery), stamps the entry, then resumes the deferred
// resolution. `_activateAbility` for an activated cost (Ignis Rex) or
// the triggered entry's effects for a Genesis / Deathrite cost (Sisters
// of Avalon). Returns true when the click was consumed.
// Single eligibility gate shared by the hand glow (renderBody) and the click
// resolver below: the server's candidate list wins when present (it is
// narrower than any local filter - Sites, locked spells); local costs stage a
// filter instead. qa#81 came from the two sites disagreeing, so there is
// exactly one predicate.
export function isEligibleDiscardPick(pd, card, fc) {
  if (!pd) return false;
  return pd.candidateIds ? pd.candidateIds.has(card.id) : matchesFilter(fc, pd.filter);
}

export function resolveDiscardCostPick(host, handCard) {
  const pd = host._pendingDiscardCost;
  if (!pd) return false;
  const fc = host._getFullCard(handCard.cardId) || handCard;
  // An off-list pick must be rejected HERE: the server answers it with zero
  // patches (no diff, no prompt clear), so forwarding it and clearing the
  // local prompt would soft-lock a promptless UI against a waiting server.
  if (!isEligibleDiscardPick(pd, handCard, fc)) {
    toast.error(t('That card does not match the discard cost'));
    return true;
  }
  // A server hand prompt (Wizard's Den under auth): reply with the pile
  // choose intent. The server moves the card; the diff renders the discard.
  if (pd.kind === 'server_choice') {
    host._pendingDiscardCost = null;
    host._sendMatchIntent(choosePileIntent(pd.promptId, [handCard.id], pd.controller));
    host.setState({ pendingServerChoice: null });
    return true;
  }
  host._pendingDiscardCost = null;
  const source = host._findCardById(pd.sourceCardId);
  if (!source) return true;
  host.removeFromHand(handCard);
  const cemetery = host.findOrCreateCemetery(pd.controller === 'p2');
  cemetery.cards.push(handCard);
  host.updatePileMeshes();
  host.logAction('{name}: discards {card}', { name: cardLogLabel(source, msg('Card')), card: cardLogLabel(handCard, msg('a card')) });
  // 'effect_only' (Wizard's Den's discard EFFECT, not a cost) has no
  // deferred follow-up. The discard above is the whole effect.
  if (pd.kind === 'effect_only') { host.forceUpdate(); return true; }
  pd.entry._discardPaid = true;
  try {
    if (pd.kind === 'triggered') {
      host._applyEffectsForConfirm(pd.prompt, pd.entry, pd.effects, source, pd.controller);
      host._maybeDisposeFinishedSpell(source.id, pd.controller);
      host._maybeResumeDeferredTurnPass();
    } else {
      host._activateAbility(source, pd.entry);
    }
  } finally {
    delete pd.entry._discardPaid;
  }
  host.forceUpdate();
  return true;
}

// ESC / cancel for a pending discard-cost pick. Nothing was charged.
// A MANDATORY server hand prompt (Wizard's Den) can't be dismissed: the
// durable pendingChoice stays open server-side, so keep the pick up.
export function cancelDiscardCostPick(host) {
  if (!host._pendingDiscardCost) return false;
  if (host._pendingDiscardCost.kind === 'server_choice') return true;
  host._pendingDiscardCost = null;
  host.forceUpdate();
  return true;
}

// Stage a sacrifice cost (Cauldron Crones / Wiccan Tools). Mirrors the
// discard-cost defer: a self-sacrifice resolves inline (no pick); a
// board sacrifice hands off to the standard target-pick halo, scoped to
// the source's cell (`here`, exclude_self). `meta` carries the kind
// ('activated' | 'triggered') + the originating prompt so the draw
// resolves through the right path once the victim leaves play.
function entryGrantsSpellcasterNoThreshold(entry) {
  return (entry?.effect || []).some((e) => e.op === OP.GRANT_SPELLCASTER_NO_THRESHOLD_THIS_TURN);
}

// Wiccan Tools secondary clause. Grants the bearer Spellcaster + a
// per-bearer threshold waiver, both expiring at the controller's turn
// end. The keyword rides the synced status path (badge + broadcast) and
// reverts via _thisTurnStatusReverts; the waiver flag (read by
// findValidSpellcasters) reverts via _thisTurnThresholdWaiverReverts.
function grantSpellcasterNoThresholdToBearer(host, bearer) {
  const { statusReverts, waiverReverts } = applySpellcasterNoThresholdGrant(bearer);
  if (!getEffectiveStatuses(bearer).includes('spellcaster')) {
    host.toggleCardStatus(bearer, 'spellcaster');
  }
  if (!host._thisTurnStatusReverts) host._thisTurnStatusReverts = [];
  for (const r of statusReverts) host._thisTurnStatusReverts.push(r);
  if (!host._thisTurnThresholdWaiverReverts) host._thisTurnThresholdWaiverReverts = [];
  for (const r of waiverReverts) host._thisTurnThresholdWaiverReverts.push(r);
  host.logAction('{name}: gains Spellcaster this turn. Its spells require no threshold', { name: cardLogLabel(bearer, msg('Bearer')) });
}

function startSacrificeCost(host, cardInstance, controller, entry, sacCost, meta) {
  const drawArgs = entry.effect?.[0]?.args?.draw || { amount: 1, source: 'spell' };
  if (sacCost.self) {
    // Capture the bearer BEFORE the self-sacrifice. Sending the Artifact
    // to cemetery clears the bearer's equippedCards link (Wiccan Tools'
    // "bearer gains Spellcaster" clause targets the carrier, not the
    // Artifact, which is gone by the time the draw resolves).
    const bearer = entryGrantsSpellcasterNoThreshold(entry) ? findBearerOf(host, cardInstance) : null;
    // If this artifact is equipped (Wiccan Tools on a bearer), detach it
    // before it leaves. SendCardToPile only handles board meshes / piles,
    // not the equip link, so without this the sacrificed artifact lingers in
    // the bearer's equippedCards (stale equipment + effects).
    const carrier = bearer || findBearerOf(host, cardInstance);
    if (carrier?.equippedCards?.some((c) => c.id === cardInstance.id)) {
      carrier.equippedCards = carrier.equippedCards.filter((c) => c.id !== cardInstance.id);
      host._clearEquippedArtifactEffects?.(cardInstance.id);
      host._broadcastCardEquipment?.(carrier.id, carrier.equippedCards);
      host.rebuildStatusBadges?.(carrier.id);
    }
    host.logAction('{name}: sacrificed', { name: cardLogLabel(cardInstance, msg('Card')) });
    host.sendCardToPile(cardInstance, 'Cemetery');
    finishSacrificeDraw(host, cardInstance, controller, entry, drawArgs, meta);
    if (bearer) grantSpellcasterNoThresholdToBearer(host, bearer);
    return;
  }
  const victims = sacrificeVictimsHere(host.meshes, host._cellAtWorld.bind(host), cardInstance);
  if (victims.length === 0) {
    // Triggered "may" with nothing to sacrifice fizzles for free.
    host.logAction('{name}: no minion to sacrifice. It fizzles', { name: cardLogLabel(cardInstance, msg('Card')) });
    if (meta.kind === 'triggered') {
      host._maybeDisposeFinishedSpell(cardInstance.id, controller);
      host._maybeResumeDeferredTurnPass();
    }
    return;
  }
  host._pendingSacrificeCost = { controller, sourceCardId: cardInstance.id, entry, drawArgs, meta };
  // Reuse the synced target-pick halo for the board minion pick. The
  // SACRIFICE_DRAW op in _executeTargetOp sacrifices the click target
  // and re-enters resolveSacrificeCostPick for the draw.
  host._startTargeting(
    cardInstance, controller, OP.SACRIFICE_DRAW,
    { target: { ...(sacCost.target || { scope: 'here' }), exclude_self: true } },
    null, null, { promptDescription: t('Sacrifice which minion?') },
  );
}

// Sacrifice resolved (self or picked victim already in cemetery). Fire
// the trailing draw, then close out the originating flow exactly like
// the discard-cost resolver does. `_sacrificePaid` is stamped so a
// re-entrant activate path doesn't re-charge the cost.
function finishSacrificeDraw(host, cardInstance, controller, entry, drawArgs, meta) {
  const pileName = drawArgs.source === 'site' ? msg('Atlas') : msg('Spellbook');
  const n = executeDrawOp(host, drawArgs, cardInstance, controller);
  if (n > 0) host.logAction('{name}: drew {n} from {pile}', { name: cardLogLabel(cardInstance, msg('Card')), n, pile: pileName });
  entry._sacrificePaid = true;
  try {
    if (meta.kind === 'triggered') {
      host._maybeDisposeFinishedSpell(cardInstance.id, controller);
      host._maybeResumeDeferredTurnPass();
    }
  } finally {
    delete entry._sacrificePaid;
  }
  host.forceUpdate();
}

// Called from _executeTargetOp once the player clicks the minion to
// sacrifice. The victim is already sent to cemetery by the dispatch;
// this just fires the deferred draw + cleanup.
export function resolveSacrificeCostPick(host, controller) {
  const pending = host._pendingSacrificeCost;
  if (!pending) return;
  host._pendingSacrificeCost = null;
  const source = host._findCardById(pending.sourceCardId);
  if (!source) return;
  finishSacrificeDraw(host, source, pending.controller, pending.entry, pending.drawArgs, pending.meta);
}

// ESC / cancel of the sacrifice target pick. Nothing was sacrificed.
export function cancelSacrificeCostPick(host) {
  if (!host._pendingSacrificeCost) return false;
  const pending = host._pendingSacrificeCost;
  host._pendingSacrificeCost = null;
  if (pending.meta.kind === 'triggered') {
    const source = host._findCardById(pending.sourceCardId);
    if (source) {
      host._maybeDisposeFinishedSpell(source.id, pending.controller);
      host._maybeResumeDeferredTurnPass();
    }
  }
  return true;
}

// Pact with the Devil resolution. Pay the chosen cost (sacrifice the
// casting Spellcaster via sendCardToPile, OR lose ceil(life/2) via
// loseLife), THEN draw three from the spellbook. The caster id rides
// `prompt.casterOverrides.casterId` from the cast pipeline. Cancel
// (no choice) pays nothing and skips the draw. Either way the Pact spell
// finishes to cemetery via _maybeDisposeFinishedSpell.
function resolvePactChoice(host, prompt, controller, choiceValue) {
  const { cardInstance, sourceEntry } = prompt;
  const spellName = prompt.cardName || 'Pact with the Devil';
  let paid = false;
  if (choiceValue === 'sacrifice') {
    const casterId = prompt.casterOverrides?.casterId;
    const caster = casterId ? host._findCardById(casterId) : null;
    if (caster) {
      host.logAction('{name}: sacrificed {card}', { name: spellName, card: cardLogLabel(caster, msg('the caster')) });
      host.sendCardToPile(caster, 'Cemetery');
      paid = true;
    } else {
      host.logAction('{name}: no caster to sacrifice (the Pact fizzles)', { name: spellName });
    }
  } else if (choiceValue === 'life') {
    const life = host.state.gameState.trackers[controller]?.life || 0;
    const loss = Math.ceil(life / 2);
    host.playerResources.loseLife(controller, loss, spellName);
    host.logAction('{name}: lost {n} life (half, rounded up)', { name: spellName, n: loss });
    paid = true;
  }
  if (paid) {
    const n = executeDrawOp(host, sourceEntry?.effect?.[0]?.args?.draw || { amount: 3 }, cardInstance, controller);
    if (n > 0) host.logAction('{name}: drew {n}', { name: spellName, n });
  }
  host._maybeDisposeFinishedSpell(cardInstance.id, controller);
  host._maybeResumeDeferredTurnPass();
}

// Sphinx Sarcophagus Genesis: banish two identically-named spells from
// the cemeteries, then draw a spell. Codex "from cemeteries" (plural).
// the pair may straddle BOTH players' Cemetery piles, so the pick spans
// both via the canonical multi-select system (kind `sphinx_banish_pair`).
// The dialog opens on the controller's own Cemetery; the cemetery tab
// switcher (renderBody) lets the player flip to the opponent's pile
// mid-pick without losing the running selection. The KIND's `validate`
// only admits a spell with an unselected same-named twin, so the pair is
// always identical; `confirm` banishes both copies (each to its owner's
// Banished) and fires the draw. Fizzles for free when no pair exists, and
// closing the dialog routes the spell to the cemetery via cancelPileSelect.
function startSphinxBanishPair(host, cardInstance, controller, args, sourceName) {
  const own = host.findOrCreateCemetery(controller === 'p2');
  const opp = host.findOrCreateCemetery(controller !== 'p2');
  const cemeteries = [own, opp].filter(Boolean);
  const catalog = host.props.sorceryCards || [];
  if (cemeteries.length === 0 || !hasBanishablePair(cemeteries, catalog)) {
    host.logAction('{name}: no two identical spells to banish. It fizzles', { name: sourceName || 'Sphinx Sarcophagus' });
    return;
  }
  startPileMultiSelect(host, own, {
    kind: 'sphinx_banish_pair',
    controller,
    sourceCardId: cardInstance.id,
    sourceName: sourceName || 'Sphinx Sarcophagus',
    title: t('Banish two identical spells'),
    hint: t('Pick a spell, then its identical twin from either cemetery.'),
    confirmLabel: t('Banish 2, draw'),
    max: 2,
    minSelect: 2,
    pileIds: cemeteries.map((c) => c.id),
    spanCemeteries: true,
    meta: { drawArgs: args.draw || { amount: 1, source: 'spell' } },
  });
}

// Player confirmed a prompt. Commit the effect to the ledger, sync,
// and dequeue. `choiceValue` is the picked option for choice prompts
// (e.g. Valley of Delight passes the chosen element key).
export function handlePromptConfirm(host, promptId, choiceValue) {
  if (typeof promptId === 'string' && promptId.startsWith(SERVER_DRAW_PROMPT_PREFIX)) {
    confirmServerDrawPrompt(host, promptId, choiceValue);
    return;
  }
  const queue = host.state.pendingPrompts;
  const prompt = queue.find((p) => p.id === promptId);
  if (!prompt) return;

  const { sourceEntry, cardInstance, controller } = prompt;

  // Acknowledge (spellcasting system): a "this card was played" storyline
  // entry. The caster's own ack carries _disposeSpell → the resolved
  // spell lands on the board; the opponent's read-only mirror has no
  // such flag and just clears. Neither blocks the game.
  if (prompt.promptType === 'acknowledge') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      if (prompt._disposeSpell && cardInstance) {
        host._maybeDisposeFinishedSpell(cardInstance.id, controller);
      }
    });
    return;
  }

  // Spellcaster pick (v1.14): choiceValue is the picked caster's
  // card id. Dequeue this prompt, then re-enter the spell-cast flow
  // with the caster locked in.
  if (prompt.promptType === 'pick_caster') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      if (!choiceValue) {
        // Cancelled at the caster pick. Spell fizzles onto the board.
        host._landCastSpellOnBoard(cardInstance);
        host._spellPlayInFlight.delete(cardInstance.id);
        return;
      }
      const caster = host.meshes.get(choiceValue)?.userData?.cardInstance;
      const fullSpell = host._getFullCard(cardInstance.cardId);
      if (!caster || !fullSpell) {
        host._landCastSpellOnBoard(cardInstance);
        host._spellPlayInFlight.delete(cardInstance.id);
        return;
      }
      host._castSpellWithCaster(cardInstance, caster, fullSpell, prompt._spellTriggers || ['on_play'], controller);
    });
    return;
  }

  // v1.103. Avatar of Water's optional "You may teleport there."
  // Confirm moves the avatar to the freshly-flooded cell; cancel
  // (handled by handlePromptCancel) leaves it where it stands.
  if (prompt._avatarFloodTeleport) {
    const { cell, sourceName } = prompt._avatarFloodTeleport;
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    host._moveCardToCell(cardInstance, cell, sourceName, k('teleports'), 'teleport');
    return;
  }

  // v1.30b. Placement prompt commits the chosen subsurface state
  // to the cardInstance and broadcasts. Mesh-position is unchanged
  // (subsurface is purely a flag. The z-stacking pass handles
  // visual layering separately).
  if (prompt.promptType === 'placement_choice') {
    const region = choiceValue || 'surface';
    cardInstance.subsurface = (region === 'surface') ? null : region;
    host._broadcastCardSubsurface?.(cardInstance.id, cardInstance.subsurface);
    if (region === 'surface') host.logAction('{name} enters on surface', { name: prompt.cardName });
    else host.logAction('{name} enters {region}', { name: prompt.cardName, region });
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    return;
  }

  // Searing Truth. The caster picked the target player (choiceValue is
  // the chosen seat). Resolve the draw + reveal + self-damage for that
  // seat, then dispose the spell to the cemetery via the universal path.
  if (prompt.promptType === 'force_reveal_target') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      const targetSeat = choiceValue === prompt.controller ? prompt.controller
        : (prompt.controller === 'p2' ? 'p1' : 'p2');
      resolveForceRevealTarget(host, cardInstance, prompt.controller, targetSeat, prompt._count ?? 2);
      host._maybeDisposeFinishedSpell(cardInstance.id, prompt.controller);
    });
    return;
  }

  // v1.103. Interrogator dodge resolver. Pay = opponent loses 3 life
  // (no draw); decline = Interrogator's controller draws a spell.
  if (prompt.promptType === 'interrogator_dodge') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    const meta = prompt._interrogator || {};
    const sourceController = meta.sourceController;
    const opponent = meta.opponent;
    if (choiceValue === 'pay') {
      host.playerResources.loseLife(opponent, 3, msg('{name} dodge', { name: prompt.cardName || 'Interrogator' }));
      host.logAction('{who} pays 3 life to dodge {name}', { who: opponent === 'p1' ? msg('Player 1') : msg('Player 2'), name: prompt.cardName || 'Interrogator' });
    } else {
      const ownerRotated = sourceController === 'p2';
      const piles = host.state.gameState?.piles || [];
      const spellbook = piles.find((p) => p.name === 'Spellbook' && !!p.rotated === ownerRotated);
      if (spellbook && spellbook.cards.length > 0) {
        host.drawCard(spellbook.id);
        host.logAction('{name}: {who} draws a spell', { name: prompt.cardName || 'Interrogator', who: sourceController === 'p1' ? msg('Player 1') : msg('Player 2') });
      } else {
        host.logAction('{name}: spellbook empty (no draw)', { name: prompt.cardName || 'Interrogator' });
      }
    }
    return;
  }

  // Riddle Sphinx. The opponent's yes/no on swapping the controller's
  // top and bottom spells. The decision rides `_pileEndSwap.onDecide`,
  // which replies across the wire (MP) or swaps locally (sandbox). Any
  // value other than 'swap' (or Cancel via handlePromptCancel) declines.
  if (prompt.promptType === 'pile_end_swap') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    prompt._pileEndSwap?.onDecide(choiceValue === 'swap');
    return;
  }

  // Persecutor's 2-mode picker: step toward the closest Evil, or
  // brand an enemy as Evil this turn. Resolution lives in
  // avatarAbilities.resolvePersecutorChoice.
  if (prompt.promptType === 'persecutor_choice') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    if (!choiceValue) return;
    host._resolvePersecutorChoice(prompt.cardInstance, controller, choiceValue);
    return;
  }

  // v1.103. Witch's 3-option curse picker. All three curses hit
  // the opponent's Avatar (1v1 sandbox assumption. Sorcery's "target
  // Avatar" with the opponent as the only legal target). 'cost' adds
  // a per-turn cost modifier; 'life' deals 2 life via _dealLifeLoss;
  // 'power' applies a -3 power debuff that reverts at the end of the
  // opponent's intervening turn (matches codex "until your next turn"
  // = until the start of caster's next turn).
  if (prompt.promptType === 'witch_curse_choice') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    if (!choiceValue) return;
    const opponent = controller === 'p1' ? 'p2' : 'p1';
    host._resolveWitchCurse(prompt.cardName || 'Witch', controller, opponent, choiceValue);
    return;
  }

  // Pact with the Devil. "Sacrifice the caster or lose half your life,
  // rounding up. If you do, draw three cards." Both options pay a cost
  // then draw; cancelling (no choiceValue) pays nothing and skips the
  // draw. The caster id rides casterOverrides from the cast pipeline.
  if (prompt.promptType === 'pact_choice') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      resolvePactChoice(host, prompt, controller, choiceValue);
    });
    return;
  }

  // v1.103. Seer's "look at top spell or site" choice. Opens the
  // existing ScryDialog on the chosen pile with 'opposite' destination
  // allowed (so the player can move the peeked card to the bottom).
  if (prompt.promptType === 'seer_scry_choice') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    if (!choiceValue) return;
    const pileName = choiceValue === 'atlas' ? 'Atlas' : 'Spellbook';
    const pile = host.findPileByNameForPlayer(pileName, controller === 'p2' ? 2 : 1);
    if (!pile || pile.cards.length === 0) {
      host.logAction('{name}: {pile} is empty', { name: prompt.cardName || 'Seer', pile: choiceValue === 'atlas' ? msg('Atlas') : msg('Spellbook') });
      return;
    }
    host.openScry(pile, 'top', { allowedDestinations: ['same', 'opposite'], maxPeek: 1 });
    return;
  }

  // v1.50. Start Phase draw. Codex: "Draw a card from either your
  // spellbook or atlas." Cancel (no choice) skips the draw. Codex
  // doesn't strictly allow skipping but sandbox UX permits it.
  if (prompt.promptType === 'draw_phase_choice') {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    host._closePrompt(promptId); // v1.105. Clear persistent descriptor
    if (choiceValue === 'spellbook' && prompt._spellbookPileId) {
      host.drawCard(prompt._spellbookPileId);
    } else if (choiceValue === 'atlas' && prompt._atlasPileId) {
      host.drawCard(prompt._atlasPileId);
    }
    return;
  }

  // Artifact Field/Equip prompt has no sourceEffect. It just sets
  // up the next interaction (or does nothing for Field).
  if (prompt.promptType === 'equip_choice') {
    // A Monument is conjured atop one of your sites and anchors there.
    // codex "Casting Artifacts": its only placement is the surface of one
    // of your sites, and a Monument can never be carried or left floating.
    // There is no real Field-vs-Equip choice, so auto-attach to the host
    // site (the site sharing its cell) however the prompt resolves.
    // equipCardTo consumes any deferred Genesis, anchored on the host. If
    // no controlled site shares the cell (an effect conjured it somewhere
    // illegal), fall back to leaving it on the field so the play still
    // commits rather than vanishing.
    if (isMonumentCard(host._getFullCard(cardInstance.cardId))) {
      const hostSite = monumentHostSite(host, cardInstance);
      if (hostSite) host.equipCardTo(cardInstance, { id: hostSite.id });
      else fireDeferredFieldArtifactGenesis(host, cardInstance);
      host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
      return;
    }
    if (choiceValue === 'equip') {
      // Drop into the existing equip-pending UX: next click on a
      // friendly card commits the equip via commitEquipPending →
      // equipCardTo → _applyEquippedArtifactEffects.
      host.setState({
        equipPending: new Set([cardInstance.id]),
        pendingPrompts: queue.filter((p) => p.id !== promptId),
      });
    } else {
      // Field choice: the artifact stays placed. Genesis still fires
      // Codex: "When this card enters the realm." All-silent
      // Genesis ops were deferred at play time so an EQUIP could
      // anchor them on the host site (Shrine of the Dragonlord's
      // mass teleport); leaving the artifact on the field makes its
      // own cell the final location, so the deferred ops fire now.
      // Player can still manually equip later via the Ctrl+click flow.
      fireDeferredFieldArtifactGenesis(host, cardInstance);
      host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
    }
    return;
  }

  // Iterate every effect in the entry. Multi-effect entries (Black
  // Obelisk: lose 2 life + gain 2 mana) all fire on a single Confirm.
  const effects = [...(sourceEntry?.effect || [])];

  // Choice prompts: replace the abstract choice effect with the
  // concrete grant_threshold for the picked element. Single-effect
  // entries only, so we mutate the array in place.
  if (effects[0]?.op === OP.CHOICE_GRANT_THRESHOLD) {
    if (!choiceValue) {
      host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) });
      return;
    }
    effects[0] = {
      op: OP.GRANT_THRESHOLD,
      args: { [choiceValue]: effects[0].args?.amount ?? 1 },
    };
  }

  // Triggered discard cost (Genesis / Deathrite: "Discard a spell. Draw
  // a spell."). The Confirm just committed the player to paying. Defer
  // to the same hand-pick flow activated abilities use, then resolve
  // these effects once the discard stamps `_discardPaid`. Fizzles for
  // free when the hand holds nothing matching the cost.
  const discardCost = (sourceEntry?.cost || []).find((c) => c.type === 'discard');
  if (discardCost && !sourceEntry._discardPaid) {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      deferTriggeredDiscardCost(host, prompt, sourceEntry, effects, discardCost.filter || {});
    });
    return;
  }

  // Triggered sacrifice cost (Cauldron Crones Genesis: "You may sacrifice
  // another minion here to draw a spell."). The Confirm IS the "may".
  // committing here defers to the sacrifice pick, then resolves the draw
  // once the victim leaves play. Fizzles for free when no minion shares
  // the source's cell.
  const sacCost = (sourceEntry?.cost || []).find((c) => c.type === 'sacrifice');
  if (sacCost && !sourceEntry._sacrificePaid) {
    host.setState({ pendingPrompts: queue.filter((p) => p.id !== promptId) }, () => {
      startSacrificeCost(host, cardInstance, controller, sourceEntry, sacCost, { kind: 'triggered', prompt });
    });
    return;
  }

  host._applyEffectsForConfirm(prompt, sourceEntry, effects, cardInstance, controller);

  host.setState(
    { pendingPrompts: queue.filter((p) => p.id !== promptId) },
    () => {
      host._maybeDisposeFinishedSpell(cardInstance.id, controller);
      host._maybeResumeDeferredTurnPass();
    },
  );
}

// Stage a triggered entry's discard cost on `host._pendingDiscardCost`,
// then either fizzle (no matching hand card) or hand off to the glowing
// hand-pick UI. The pending payload carries enough to resolve THIS
// entry's effects after the pick (`resolveDiscardCostPick` branches on
// `kind: 'triggered'`).
function deferTriggeredDiscardCost(host, prompt, sourceEntry, effects, filter) {
  const { cardInstance, controller } = prompt;
  const hand = host.getActiveHandCards() || [];
  const eligible = hand.filter((h) => matchesFilter(host._getFullCard(h.cardId) || h, filter));
  if (eligible.length === 0) {
    host.logAction('{name}: no matching card to discard. It fizzles', { name: prompt.cardName || msg('Card') });
    host._maybeDisposeFinishedSpell(cardInstance.id, controller);
    host._maybeResumeDeferredTurnPass();
    return;
  }
  host._pendingDiscardCost = {
    kind: 'triggered',
    controller,
    sourceCardId: cardInstance.id,
    entry: sourceEntry,
    filter,
    prompt,
    effects,
  };
  toast.message(t('Click a glowing card in your hand to discard it.'));
  host.forceUpdate();
}

// Fire the all-silent Genesis ops the play pipeline deferred for an
// attachable artifact, anchored on the artifact's own cell. Called
// when the placement settles WITHOUT an attach: the Field pick, the
// ESC on the Field/Equip prompt, and a cancelled equip-pending state
// all land here. Gated on `_pendingFieldGenesis` so the Genesis
// fires exactly once per realm entry. The equip path consumes the
// same flag in _applyEquippedArtifactEffects, anchored on the host.
// The Site a Monument is conjured atop: the site sharing the Monument's
// own cell, controlled by the same player. A Monument anchors to this
// site (codex: conjured atop one of your sites, never carried). Returns
// null when no such site exists. An effect placed it off a controlled site.
function monumentHostSite(host, monument) {
  const mMesh = host.meshes.get(monument.id);
  if (!mMesh) return null;
  const cell = host._cellAtWorld(mMesh.position.x, mMesh.position.z);
  if (!cell) return null;
  const key = `${cell.col},${cell.row}`;
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c?.isSite) continue;
    if (!!c.rotated !== !!monument.rotated) continue;
    const sc = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (sc && `${sc.col},${sc.row}` === key) return c;
  }
  return null;
}

export function fireDeferredFieldArtifactGenesis(host, cardInstance) {
  if (!cardInstance?._pendingFieldGenesis) return;
  delete cardInstance._pendingFieldGenesis;
  const fullCard = host._getFullCard(cardInstance.cardId);
  if (fullCard?.type !== 'Artifact') return;
  const controller = cardInstance.rotated ? 'p2' : 'p1';
  for (const entry of fullCard.automation || []) {
    if (entry.trigger !== 'on_play') continue;
    if (!(entry.effect || []).every((e) => SILENT_OP_SET.has(e.op))) continue;
    if (!host._evaluateCondition(entry.condition, cardInstance, fullCard)) continue;
    for (const effect of entry.effect) {
      dispatchSilentOp(effect, cardInstance, entry, host._silentOpsHost, { controller });
    }
  }
}

// Apply every effect in an array against the game state, mirroring
// the per-op behaviour from the prior single-effect handler. Pure
// helper so handlePromptConfirm stays linear.
export function applyEffectsForConfirm(host, prompt, sourceEntry, effects, cardInstance, controller) {
  const { trackers } = host.state.gameState;
  let needsAffinityRecalc = false;
  let manaBump = 0;
  let lifeDelta = 0;
  let opponentLifeDelta = 0;
  // Spellcaster overrides flow from the spell-cast pipeline (v1.14).
  // When present, every positional scope resolves relative to the
  // caster's position instead of the source card's mesh position.
  const casterOverrides = prompt.casterOverrides || null;

  // v1.77g. Entries that chain INSTALL_DAMAGE_REDIRECT_NEARBY after
  // a regular target op (Enduring Faith: Ward + install aura) need
  // BOTH ops to share the picked target. Detect that pattern here
  // and pass the chain through `followupOps`. We only collapse when
  // the followup chain is exclusively post-pick ops; otherwise each
  // effect runs through its own dispatch path.
  // v1.79. TELEPORT_TARGET / PULL_TO_SOURCE / PUSH_TARGET_AWAY are
  // also target ops; followup ops attached to them (Blink: "draw a
  // card" after the teleport) ride the same pick chain.
  // TAP_TARGET (Grievous Insult: silence + tap the same pick) and
  // DRAW_IF_TARGET_ON_ENEMY_SITE (Fade: conditional draw on the picked
  // minion) both chain on the resolved target.
  // GRANT_STATUS_TO_TARGET is a target op in its own right, but when it
  // FOLLOWS another target op (Feign Death: trigger-deathrite then "It
  // gains Stealth") it rides the lead pick so both apply to the same
  // chosen minion. No existing card chains it after a target op, so
  // listing it here only changes Feign Death's behaviour.
  const TARGET_OPS = new Set([
    OP.KILL_TARGET, OP.DAMAGE_TARGET, OP.RETURN_TO_HAND_TARGET,
    OP.BANISH_TARGET, OP.GRANT_STATUS_TO_TARGET, OP.FLOOD_TARGET_SITE,
    OP.TELEPORT_TARGET, OP.PULL_TO_SOURCE, OP.PUSH_TARGET_AWAY,
    OP.BUFF_TARGET, OP.ANIMATE_TARGET_AURA,
    OP.SWOOP_AND_BURROW_TARGET, OP.DRAW_OWNER_LOCKED_TO_TARGET,
    OP.INSTALL_ON_STRIKE_LIFELINK, OP.SUMMON_TOKEN_AT_TARGET,
    OP.PULL_TO_TARGET_SITE,
    OP.TRIGGER_TARGET_DEATHRITE,
    OP.TAG_AVATAR_UNDEFENDABLE,
  ]);
  const targetEffectIdx = effects.findIndex((e) => TARGET_OPS.has(e?.op));
  // Everything after the lead target op runs AFTER the pick resolves, in
  // printed order. Never early. Target-riding ops (tap, grant-status, etc.)
  // apply to the picked card; sequential ops (e.g. draw) fire standalone.
  // See runFollowup. Fixes "the draw fires before the silence/buff/pull
  // resolves" (Grievous Insult, Gift of the Wolf, Riptide).
  const followupForPick = targetEffectIdx >= 0 ? effects.slice(targetEffectIdx + 1) : [];

  // Riddle Sphinx: open_scry_top is a dialog; the opponent swap +
  // any draw chained after it must wait until the player resolves the
  // peek (codex order: peek, may bottom, opponent may swap, then draw).
  // Defer the swap + draws into the scry's onComplete and skip them in
  // the main loop.
  const scryIdx = effects.findIndex((e) => e?.op === OP.OPEN_SCRY_TOP);
  const afterScry = scryIdx >= 0 ? effects.slice(scryIdx + 1) : [];
  const drawsAfterScry = afterScry.filter((e) => e?.op === OP.DRAW);
  const swapAfterScry = afterScry.find((e) => e?.op === OP.OPPONENT_MAY_SWAP_PILE_ENDS) || null;

  for (let i = 0; i < effects.length; i++) {
    let effect = effects[i];
    if (!effect) continue;

    // Every effect after the lead target op is deferred to the pick's
    // resolution (followupForPick), so the draw / tap / etc. never fire
    // before the pick. Printed-order execution. Must run BEFORE the
    // TARGET_OPS branch.
    if (targetEffectIdx >= 0 && i > targetEffectIdx) continue;

    // Open the scry dialog; the opponent swap + chained draw fire from
    // its onComplete (codex order: peek/bottom, opponent may swap, draw).
    if (effect.op === OP.OPEN_SCRY_TOP) {
      const scryArgs = effect.args || {};
      openScryWithDraws(host, controller, scryArgs, cardInstance, drawsAfterScry, prompt.cardName, swapAfterScry?.args || null);
      continue;
    }
    // The opponent swap + draws chained after the scry already ride its
    // onComplete.
    if (effect.op === OP.OPPONENT_MAY_SWAP_PILE_ENDS && scryIdx >= 0 && i > scryIdx) continue;
    if (effect.op === OP.DRAW && scryIdx >= 0 && i > scryIdx) continue;

    // Single-target ops don't apply directly. They enter pending
    // target mode. The next valid-target click resolves the effect.
    if (TARGET_OPS.has(effect.op)) {
      const followups = (i === targetEffectIdx) ? followupForPick : null;
      host._startTargeting(cardInstance, controller, effect.op, effect.args || {}, casterOverrides, followups, { excludeId: prompt._castSourceSpellId || null, duration: sourceEntry?.duration || null });
      continue;
    }

    // Fire Harpoons!. Precompute the legal target cells (water sites
    // adjacent to the caster), then enter a damage pick limited to those
    // cells with a pull_to_source chained onto the same pick.
    if (effect.op === OP.DAMAGE_SITE_RELATIVE_TARGET) {
      const trailing = effects.slice(i + 1).filter(Boolean);
      startSiteRelativeDamage(host, cardInstance, controller, effect.args || {}, casterOverrides, trailing);
      // The trailing effects ride the pick's followup chain; drop them
      // from the main loop so they don't also fire standalone.
      for (let j = i + 1; j < effects.length; j++) effects[j] = null;
      continue;
    }

    // Multi-target iteration. No click needed. Resolve the scope
    // immediately and apply the op to every matching card. BANISH_EACH
    // additionally draws one card per banished (Sjaelström) via the
    // `draw_per` arg. Counted from the pre-banish match set inside
    // _iterateEachOp so the count survives the removal.
    const EACH_OPS = new Set([OP.KILL_EACH, OP.DAMAGE_EACH, OP.RETURN_EACH_TO_HAND, OP.BANISH_EACH]);
    if (EACH_OPS.has(effect.op)) {
      host._iterateEachOp(cardInstance, controller, effect.op, effect.args || {}, casterOverrides);
      continue;
    }

    // Two-step location targeting. Fire the cell-pick UI; the click
    // resolver applies damage to every unit in the chosen cell.
    if (effect.op === OP.DAMAGE_EACH_AT_LOCATION) {
      host._startCellTargeting(cardInstance, controller, effect.op, effect.args || {}, casterOverrides);
      continue;
    }

    // Summon a named token to every cell matching the location scope
    // (v1.15e. Midland Army's Deathrite). Only cells with a Site
    // qualify as spawn destinations (Voidwalk tokens excepted).
    if (effect.op === OP.SUMMON_TOKEN_AT_LOCATIONS) {
      host._summonTokenAtLocations(cardInstance, controller, effect.args || {}, casterOverrides);
      continue;
    }

    // v1.86. Open the existing pile-search dialog scoped to the
    // controller's named pile (spellbook / atlas / cemetery). The
    // filter gates which cards in that pile are clickable;
    // pendingSearchEffect routes the click through _resolveSearchPick
    // (take-to-hand + shuffle) instead of plain takeCardFromPile.
    if (effect.op === OP.SEARCH_DECK) {
      host._startSearchEffect(cardInstance, controller, effect.args || {}, prompt.cardName);
      continue;
    }

    // Xeraphine Konrul's end-of-turn rites: open the cemetery in
    // two-stage pick mode (choose three, then pick the one to return).
    if (effect.op === OP.CEMETERY_RITES) {
      startXeraphineRites(host, cardInstance, controller, effect.args || {});
      continue;
    }

    // Sphinx Sarcophagus Genesis: banish two identically-named spells
    // from the cemetery, then draw a spell. Opens the cemetery picker
    // (Archimago pattern); the trailing draw rides the pending state.
    if (effect.op === OP.BANISH_PAIR_DRAW) {
      startSphinxBanishPair(host, cardInstance, controller, effect.args || {}, prompt.cardName);
      continue;
    }

    // Vanishment's "Give all allied minions Stealth": apply the keyword
    // to every scope-matched card with no pick.
    if (effect.op === OP.GRANT_STATUS_EACH) {
      grantStatusEach(host, cardInstance, controller, effect.args || {}, casterOverrides);
      continue;
    }

    // Pendragon Legacy: open the cemetery in multi-select mode (any
    // number of Uniques) → shuffle each back into its deck.
    if (effect.op === OP.SHUFFLE_FROM_CEMETERY_INTO_DECK) {
      startCemeteryDeckSelect(host, cardInstance, controller, 'shuffle_cemetery_into_deck', {
        ...(effect.args || {}),
        title: t('Shuffle which cards into your decks?'),
        confirmLabel: t('Shuffle in'),
      });
      continue;
    }

    // Return to Nature: open the cemetery in multi-select mode (up to
    // three) → return each to the bottom of its deck.
    if (effect.op === OP.RETURN_FROM_CEMETERY_TO_DECK_BOTTOM) {
      startCemeteryDeckSelect(host, cardInstance, controller, 'return_cemetery_to_deck_bottom', {
        max: effect.args?.max ?? 3,
        filter: effect.args?.filter,
        span: true,
        title: t('Return which cards to the bottom of their decks?'),
        confirmLabel: t('Return to bottom'),
      });
      continue;
    }

    // Black Mass: open the spellbook windowed to its top seven → draw up
    // to three different Evil minions, rest to the bottom.
    if (effect.op === OP.SEARCH_TOP_DRAW_REST_TO_BOTTOM) {
      startBlackMass(host, cardInstance, controller, effect.args || {});
      continue;
    }

    // Flanking Maneuver: pick a source cell holding allied units, then a
    // knight-move destination; the whole group teleports together. The
    // chained `draw` rides as a followup once the destination resolves.
    if (effect.op === OP.GROUP_TELEPORT_KNIGHT) {
      const draws = effects.slice(i + 1).filter((e) => e?.op === OP.DRAW);
      startFlankingManeuver(host, cardInstance, controller, casterOverrides, draws);
      // The draw followup is consumed inside the resolver; drop it from
      // the main loop so it doesn't also fire standalone.
      for (let j = i + 1; j < effects.length; j++) {
        if (effects[j]?.op === OP.DRAW) effects[j] = null;
      }
      continue;
    }

    // Howl from Beyond: deal the top spells to each outer-column void,
    // draw the Voidwalk-minion / Monster ones, banish the rest. No pick.
    if (effect.op === OP.DEAL_TO_OUTER_VOIDS_DRAW_VOIDWALK) {
      resolveHowlFromBeyond(host, cardInstance, controller);
      continue;
    }

    // Kairos the Archivist's Deathrite: rewind archived cards, then
    // banish Kairos from the cemetery.
    if (effect.op === OP.RESTORE_ARCHIVED_REALM) {
      resolveRestoreArchivedRealm(host, cardInstance, controller);
      continue;
    }

    // v1.103. Avatar of Water's specific Flood + optional teleport
    // flow. Dispatches a cell-pick constrained to sites adjacent to
    // the avatar's body of water; the resolver unfloods any prior
    // flood by this avatar, floods the new one, and prompts the
    // optional teleport.
    if (effect.op === OP.AVATAR_OF_WATER_FLOOD) {
      host._startAvatarOfWaterFlood(cardInstance, controller);
      continue;
    }

    // v1.103. Pathfinder's "play topmost atlas site to adjacent
    // location and move there" flow. Draws the atlas top into hand,
    // sets the pathfinder placement marker (adjacency gate + auto-
    // move on commit). If the atlas is empty or its top isn't a
    // Site, the activation fizzles (the tap cost was already paid).
    if (effect.op === OP.PATHFINDER_TAP) {
      host._startPathfinderTap(cardInstance, controller);
      continue;
    }

    // Bladedancer's post-attack step: cell-pick on her 1-step reach,
    // then an optional attack at the destination.
    if (effect.op === OP.BLADEDANCER_STEP) {
      host._startBladedancerStep(cardInstance, controller);
      continue;
    }

    // Thunderstorm's "you may move it one step": cell-pick on the
    // newly covered strips per cardinal direction.
    if (effect.op === OP.MOVE_AURA_ONE_STEP) {
      host._startAuraOneStep(cardInstance, controller);
      continue;
    }

    // Sparkmage's shock: cell-pick on nearby cells with another unit;
    // a random unit there takes the controller's per-turn (A) cast sum.
    if (effect.op === OP.SPARKMAGE_SHOCK) {
      host._startSparkmageShock(cardInstance, controller);
      continue;
    }

    // Generic per-turn gate consumed by `not_used_this_turn`
    // conditions. Pairs with the same op in the silent combat-trigger
    // path; confirming a prompt that carries it burns the key.
    if (effect.op === OP.MARK_USED_THIS_TURN) {
      const key = effect.args?.key;
      if (key) {
        if (!cardInstance._abilitiesUsedThisTurn) cardInstance._abilitiesUsedThisTurn = new Set();
        cardInstance._abilitiesUsedThisTurn.add(key);
        host._broadcastAbilitiesUsed?.(cardInstance);
      }
      continue;
    }

    // v1.103. Geomancer's "Tap → Replace an adjacent Rubble with the
    // topmost site of your atlas." Opens a cell-pick on cardinal-
    // adjacent cells holding a Rubble; the resolver banishes the
    // Rubble and drops the atlas-top site at that cell.
    if (effect.op === OP.GEOMANCER_REPLACE_RUBBLE) {
      host._startGeomancerReplaceRubble(cardInstance, controller);
      continue;
    }

    // v1.103. Waveshaper's "Tap → Flood a site near your body of
    // water + tap non-submerge minions there (skip their next untap)."
    // Opens cell pick on sites nearby the avatar's body of water; the
    // resolver unfloods any prior, floods the new site, taps the
    // affected minions, and stamps _skipNextUntap on them.
    if (effect.op === OP.WAVESHAPER_FLOOD) {
      host._startWaveshaperFlood(cardInstance, controller);
      continue;
    }

    // v1.115. Avatar of Fire's "Tap → fire sites in hand are also
    // Fireballs." Opens a hand-picker on fire sites; the picked site
    // moves to cemetery as a cast Magic spell and Fireball's
    // projectile resolves from the avatar's cell.
    if (effect.op === OP.AVATAR_OF_FIRE_IGNITE) {
      host._startAvatarOfFireIgnite(cardInstance, controller);
      continue;
    }

    // v1.96. Same site-grant counter the Avatar's Tap → Play branch
    // uses. Cleared at end of the controller's turn by the existing
    // _stepPruneExpiredEffects path.
    if (effect.op === OP.GRANT_SITE_PLACEMENT) {
      host._grantSitePlacement(controller, effect.args?.restriction || null, prompt.cardName);
      continue;
    }

    // The Apex of Babel: "then you may cast a spell for free." Open the
    // free-cast window for the controller; the next spell they play
    // costs no mana.
    if (effect.op === OP.CAST_SPELL_FOR_FREE) {
      host._grantFreeCast(controller, prompt.cardName);
      continue;
    }

    // Silent-set ops confirmed through an `optional: true` prompt
    // (Vatn Draconis's "you may submerge all minions and artifacts
    // here") resolve through the same dispatcher the silent path
    // uses. One resolution recipe per op, no parallel logic. EACH_OPS
    // were already consumed above (their confirm path is caster-
    // override aware), so this only catches the remaining silent ops.
    if (SILENT_OP_SET.has(effect.op)) {
      dispatchSilentOp(effect, cardInstance, sourceEntry, host._silentOpsHost, { controller });
      continue;
    }

    if (effect.op === OP.GRANT_THRESHOLD || effect.op === OP.GRANT_MANA) {
      const turnNumber = host.state.turnNumbers[controller] || 0;
      const expiry =
        sourceEntry.duration === DURATION.THIS_TURN
          ? { type: 'turn_end', player: controller, turnNumber }
          : sourceEntry.duration === DURATION.UNTIL_LEAVES
          ? { type: 'until_leaves' }
          : { type: 'permanent' };
      const ledgerEntry = {
        sourceCardId: cardInstance.id,
        sourceCardName: prompt.cardName,
        controller,
        effect,
        expiry,
      };
      host.effectLedger.add(ledgerEntry);
      // The site's OWN per-cell affinity ("Provides (E) this turn") is
      // stamped synchronously during `applyCardAutomation`, before this
      // Confirm resolves. That's what the Geomancer's earth-site Rubble
      // bonus reads at the play commit. This Confirm path only installs
      // the controller's casting-pool ledger entry above; it must NOT
      // re-stamp the site or the granted affinity would double.
      needsAffinityRecalc = true;
      if (effect.op === OP.GRANT_MANA) manaBump += effect.args?.amount || 0;
    } else if (effect.op === OP.MODIFY_CAST_COST) {
      // v1.89. Install a cost modifier on the target player. Tax
      // Collector: { delta: 1, target_player: 'opponent', duration:
      // 'this_turn' }. Expiry resolves against the TARGET player's
      // turn number so "opponent's next turn" decays correctly.
      // `filter.cells: "source_cell"` resolves to the source's LIVE
      // cell at install time (Caelestis: "the next Dragon you cast to
      // THIS LOCATION costs (0)"). Same cells mechanism Harbinger's
      // marked squares use, anchored to wherever the source sits now.
      if (effect.args?.filter?.cells === 'source_cell') {
        const mesh = host.meshes.get(cardInstance.id);
        const cell = mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null;
        if (!cell) {
          host.logAction('{name}: no location to anchor the cost effect. It fizzles', { name: prompt.cardName });
          continue;
        }
        effect = { ...effect, args: { ...effect.args, filter: { ...effect.args.filter, cells: [{ col: cell.col, row: cell.row }] } } };
      }
      const target = effect.args?.target_player === 'opponent'
        ? (controller === 'p1' ? 'p2' : 'p1')
        : controller;
      const targetTurnNumber = host.state.turnNumbers[target] || 0;
      // "Opponent's NEXT turn" means we want the modifier to be
      // active during turn N+1 and expire at the end of that turn.
      const expiresAtTurn = effect.args?.target_player === 'opponent'
        ? targetTurnNumber + 1
        : targetTurnNumber;
      const expiry = sourceEntry.duration === DURATION.UNTIL_LEAVES
        ? { type: 'until_leaves' }
        : sourceEntry.duration === DURATION.PERMANENT
        ? { type: 'permanent' }
        : { type: 'turn_end', player: target, turnNumber: expiresAtTurn };
      const ledgerEntry = {
        sourceCardId: cardInstance.id,
        sourceCardName: prompt.cardName,
        controller,
        targetPlayer: target,
        effect,
        expiry,
      };
      host.effectLedger.add(ledgerEntry);
      const targetLabel = target === 'p1' ? msg('Player 1') : msg('Player 2');
      if (typeof effect.args?.set_to === 'number') {
        host.logAction(
          "{name}: {who}'s next matching cast costs ({cost})",
          { name: prompt.cardName, who: targetLabel, cost: effect.args.set_to },
        );
      } else {
        const verb = (effect.args?.delta || 0) > 0 ? msg('more') : msg('less');
        host.logAction(
          "{name}: {who}'s spells cost {n} {verb} on their next turn",
          { name: prompt.cardName, who: targetLabel, n: Math.abs(effect.args?.delta || 0), verb },
        );
      }
    } else if (effect.op === OP.LOSE_LIFE) {
      if (effect.args?.target_player === 'opponent') opponentLifeDelta -= effect.args?.amount || 0;
      else lifeDelta -= effect.args?.amount || 0;
    } else if (effect.op === OP.GAIN_LIFE) {
      if (effect.args?.target_player === 'opponent') opponentLifeDelta += effect.args?.amount || 0;
      else lifeDelta += effect.args?.amount || 0;
    } else if (effect.op === OP.DRAW) {
      const pileName = effect.args?.source === 'site' ? msg('Atlas') : msg('Spellbook');
      const n = executeDrawOp(host, effect.args, cardInstance, controller, null, casterOverrides);
      if (n > 0) {
        if (effect.args?.owner) host.logAction('{name}: drew {n} from {pile}, only {name} can cast them', { name: prompt.cardName, n, pile: pileName });
        else host.logAction('{name}: drew {n} from {pile}', { name: prompt.cardName, n, pile: pileName });
      }
    }
    // REMINDER is a no-op here. The player acknowledged the prompt.
  }

  if (needsAffinityRecalc) host.recalculateSiteAffinities(controller);

  if (manaBump !== 0) host.playerResources.bumpMana(controller, manaBump);

  // applyLifeDelta routes negative deltas through loseLife (floors at
  // 0, sets DD immunity on transition) and positive through gainLife
  // (gated by DD, capped at max). Mirrors the codex split.
  if (lifeDelta !== 0) {
    host.playerResources.applyLifeDelta(controller, lifeDelta, prompt.cardName);
  }
  if (opponentLifeDelta !== 0) {
    const opponent = controller === 'p1' ? 'p2' : 'p1';
    host.playerResources.applyLifeDelta(opponent, opponentLifeDelta, prompt.cardName);
  }

  if (needsAffinityRecalc) {
    host.updateTrackerTokenPositions();
    host.forceUpdate();
  }
}

// Player declined a prompt. Drop it from the queue. No ledger
// mutation, no broadcast: the opponent never knew this prompt fired
// locally.
export function handlePromptCancel(host, promptId) {
  const prompt = host.state.pendingPrompts.find((p) => p.id === promptId);
  // Acknowledge entries have no cancel. ESC just clears them (same as
  // confirming). The caster's still disposes its spell to the cemetery.
  if (prompt?.promptType === 'acknowledge') {
    host.handlePromptConfirm(promptId);
    return;
  }
  // v1.103. Interrogator dodge: ESC/Cancel means "let them draw"
  // (the dodge requires an explicit "Pay 3 life" pick).
  if (prompt?.promptType === 'interrogator_dodge') {
    host.handlePromptConfirm(promptId, 'decline');
    return;
  }
  // Riddle Sphinx. Cancel/ESC on the opponent's swap prompt declines
  // the exchange (the swap requires an explicit "Exchange" pick).
  if (prompt?.promptType === 'pile_end_swap') {
    host.handlePromptConfirm(promptId, 'no');
    return;
  }
  // v1.109. Pick_cell Cancel via drawer routes through here. The
  // pending cell-target state is the source of truth; clearing it
  // dequeues the prompt as well (via _cancelCellTargeting →
  // _dequeuePickCellPrompt). The early return prevents the generic
  // filter below from running before the cancel orchestrator has
  // cleared the cell-pick state.
  if (prompt?.promptType === 'pick_cell') {
    host._cancelCellTargeting();
    return;
  }
  if (prompt?.promptType === 'pick_target') {
    host._cancelTargeting();
    return;
  }
  // Animist's "summon a magic as a Spirit" popup. Cancel/ESC disarms the
  // one-shot. DisarmAnimistSpirit clears the flag, hand glow, and popup.
  if (prompt?.promptType === 'pick_animist_spirit') {
    host._disarmAnimistSpirit(prompt.controller);
    return;
  }
  // Cancelling the Field/Equip choice routes through the same confirm
  // handler: a carriable artifact lands on the field (same as the explicit
  // Field pick, including any deferred Genesis), while a Monument auto-
  // attaches to its host site (it can't be left floating).
  if (prompt?.promptType === 'equip_choice') {
    host.handlePromptConfirm(promptId, 'field');
    return;
  }
  // Searing Truth's target-player pick can't be cancelled out of (the
  // spell is already cast). Default to the opponent, the usual burn
  // target, then resolve + dispose.
  if (prompt?.promptType === 'force_reveal_target') {
    const opponent = prompt.controller === 'p2' ? 'p1' : 'p2';
    host.handlePromptConfirm(promptId, opponent);
    return;
  }
  // Cancelling the spellcaster pick fizzles the spell straight onto the
  // board. You can't un-cast in Sorcery.
  if (prompt?.promptType === 'pick_caster') {
    host.setState((s) => ({ pendingPrompts: s.pendingPrompts.filter((p) => p.id !== promptId) }), () => {
      host._landCastSpellOnBoard(prompt.cardInstance);
      host._spellPlayInFlight.delete(prompt.cardInstance.id);
    });
    return;
  }
  host.setState(
    (s) => ({ pendingPrompts: s.pendingPrompts.filter((p) => p.id !== promptId) }),
    () => {
      if (prompt?.cardInstance) {
        host._maybeDisposeFinishedSpell(prompt.cardInstance.id, prompt.controller);
      }
      host._maybeResumeDeferredTurnPass();
    },
  );
  // v1.105. Drop the matching descriptor (if any). No-op for prompts
  // we haven't yet wired into the stack, so safe to always call.
  host._closePrompt(promptId);
}
